import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Inventory2Outlined as Inventory2OutlinedIcon, Search as SearchOutlinedIcon } from "../../../components/icons";
import { LocalShippingOutlined as LocalShippingOutlinedIcon, ReportProblemOutlined as ReportProblemOutlinedIcon, AssignmentTurnedInOutlined as AssignmentTurnedInOutlinedIcon, AddOutlined as AddOutlinedIcon, CloseOutlined as CloseOutlinedIcon, EditOutlined as EditOutlinedIcon } from "../../../components/icons";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import { useSessionStore } from "../../../store/useSessionStore";
import { useUserStore } from "../../../store/useUserStore";
import {
  confirmWarehouseMaterialDispatch,
  confirmWarehouseMaterialReturn,
  completeWarehouseCleaningRoster,
  createInventoryMaterial,
  importInventoryMaterials,
  createWarehouseCleaningRoster,
  createWarehouseBrokenLossReport,
  createWarehouseMaterialChecklist,
  createWarehousePreparation,
  createWarehouseStockTake,
  getInventoryDashboard,
  getInventoryMaterials,
  lookupInventoryMaterials,
  resolveInventoryMaterials,
  getInventoryMovements,
  getInventoryMovementHistory,
  getOperationalLocations,
  getWarehouseCleaningRosters,
  getWarehouseBrokenLossReport,
  getWarehouseBrokenLossReports,
  getWarehouseMaterialChecklist,
  getWarehouseMaterialChecklists,
  getWarehousePreparation,
  getWarehousePreparations,
  getWarehouseStockTakes,
  markWarehousePreparationReady,
  previewInventoryImport,
  reconcileWarehouseOutstandingMaterials,
  requestWarehouseDispatchApproval,
  decideWarehouseDispatchApproval,
  submitWarehouseBrokenLossReport,
  submitWarehousePreparation,
  submitWarehouseStockTake,
  startWarehouseCleaningRoster,
  updateWarehouseStockTakeCounts,
  reviewWarehouseStockTake,
  triggerWarehouseLossReport,
  updateInventoryMaterial,
  updateWarehouseBrokenLossReport,
  updateWarehouseMaterialChecklist,
  updateWarehousePreparation,
} from "../../../services/dashboard";
import {
  formatOperationsStatus,
  getOperationsStatusSx,
  OperationsDetailField,
  OperationsDetailGrid,
  OperationsEmptyState,
  OperationsPageShell,
  OperationsSectionHeader,
  ifasColors,
  operationsActionBarSx,
  operationsDetailPaperSx,
  operationsRowSx,
  operationsTableHeadSx,
  operationsTablePaperSx,
} from "../ifas/components/OperationsUI";
import {
  canAccessIfasModule,
  hasIfasCapability,
} from "../../../utils/ifasAccess";
import {
  StandardMetricCard,
  STANDARD_TONES,
  StatusDotChip,
  StandardCountBadge,
  StandardPager,
  standardTableHeadCellSx,
  standardTableRowSx,
  standardPrimaryBtnSx,
  standardGhostBtnSx,
  standardOutlineBtnSx,
  StockStatusChip,
  type Tone,
} from "../ifas/components/StandardUI";

type WarehouseSection = "PREPARATIONS" | "MATERIAL" | "INVENTORY" | "REPORTS" | "STOCK_TAKE" | "CLEANING";
type DialogMode =
  | null
  | "CREATE_PREPARATION"
  | "EDIT_PREPARATION"
  | "CREATE_MATERIAL"
  | "EDIT_MATERIAL"
  | "CREATE_REPORT"
  | "EDIT_REPORT"
  | "CREATE_INVENTORY"
  | "EDIT_INVENTORY"
  | "CREATE_STOCK_TAKE"
  | "EDIT_STOCK_TAKE"
  | "CREATE_CLEANING_ROSTER";

type ChecklistActionIntent = "REQUEST_APPROVAL" | "DISPATCH" | "RETURN" | null;

const DIALOG_TITLES: Record<Exclude<DialogMode, null>, string> = {
  CREATE_PREPARATION: "Create Warehouse Preparation",
  EDIT_PREPARATION: "Edit Warehouse Preparation",
  CREATE_MATERIAL: "Create Funeral Material Checklist",
  EDIT_MATERIAL: "Edit Funeral Material Checklist",
  CREATE_REPORT: "Create Broken / Loss Report",
  EDIT_REPORT: "Edit Broken / Loss Report",
  CREATE_INVENTORY: "Create Inventory Material",
  EDIT_INVENTORY: "Edit Inventory Material",
  CREATE_STOCK_TAKE: "Create Stock Take",
  EDIT_STOCK_TAKE: "Capture Physical Stock Count",
  CREATE_CLEANING_ROSTER: "Create Cleaning / Storage Roster",
};

const PREPARATION_STATUS_OPTIONS = ["", "PENDING", "IN_PREPARATION", "READY"];
const MATERIAL_STATUS_OPTIONS = [
  "",
  "DRAFT",
  "DISPATCHED",
  "OUTSTANDING_MATERIALS",
  "RETURNED",
  "PARTIALLY_RETURNED",
  "LOSS_REPORTED",
];
const REPORT_STATUS_OPTIONS = ["", "DRAFT", "SUBMITTED_TO_INVENTORY"];

const formatDate = (value?: string | null) =>
  value ? dayjs(value).format("DD MMM YYYY") : "Not captured";
const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).format("DD MMM YYYY HH:mm") : "Not captured";
const formatBool = (value?: boolean | null) =>
  value === true ? "Yes" : value === false ? "No" : "Not captured";
const getEditableQuantityDisplayValue = (value: unknown, isEditable: boolean) => {
  const numericValue = Number(value || 0);
  return isEditable && numericValue === 0 ? "" : numericValue;
};
const sanitizeQuantityInput = (rawValue: string) => {
  const digitsOnly = String(rawValue || "").replace(/[^\d]/g, "");
  return digitsOnly.replace(/^0+(?=\d)/, "");
};
const parseQuantityInput = (rawValue: string) => {
  const normalizedValue = sanitizeQuantityInput(rawValue);
  return normalizedValue ? Number(normalizedValue) : 0;
};

const emptyPreparationForm = (userName: string) => ({
  caseNumber: "",
  funeralDate: dayjs().format("YYYY-MM-DD"),
  funeralSurnameArea: "",
  funeralCategory: "",
  claimId: null,
  warehouseBranch: "",
  serviceVenue: "",
  cemeteryDetails: "",
  casketType: "",
  hearseAllocation: "",
  generalAssistantNames: "",
  vehicleConfirmed: false,
  fleetCoordinatorConfirmation: userName || "",
  assignedDriverName: "",
  assignedVehicleRegistration: "",
  assignedVehicleType: "",
  transportRequired: true,
  transportVehicleCount: 1,
  transportRequirements: "",
  decorRequirements: "",
  photoAndFrameRequirements: "",
  funeralPackageDetails: "",
  materialsAndEquipmentRequired: "",
  specialInstructions: "",
  equipmentReadyChecklist: "",
  cleaningRosterTasks: "",
  stockCountData: "",
  equipmentStatus: "",
  notes: "",
  updatedBy: userName || "",
});

const emptyMaterialItem = () => ({
  itemCode: "",
  description: "",
  quantity: 1,
  colour: "",
  setupLocationType: "",
  sourceType: "",
  sourceReference: "",
  dispatched: false,
  returned: false,
  returnedQuantity: 0,
  lossOrBroken: false,
  lossOrBrokenQuantity: 0,
  outstandingWithFamily: false,
  outstandingQuantity: 0,
  outstandingCollectionDueDate: "",
  outstandingCollected: false,
  outstandingCollectedQuantity: 0,
  outstandingCollectedAt: "",
  outstandingCollectedBy: "",
});

const emptyMaterialForm = (userName: string, preparationNumber = "") => ({
  preparationNumber,
  driverName: "",
  generalAssistantNames: "",
  hearseTrailerRegistration: "",
  transportEquipment: "",
  items: [emptyMaterialItem()],
  dispatchSupervisorName: userName || "",
  dispatchSupervisorDesignation: "Warehouse Supervisor",
  dispatchSupervisorEmployeeNumber: "",
  dispatchSupervisorSignature: "",
  dispatchSupervisorDate: dayjs().format("YYYY-MM-DD"),
  dispatchSecurityOfficerName: "",
  dispatchSecurityOfficerSignature: "",
  dispatchSecurityOfficerDate: dayjs().format("YYYY-MM-DD"),
  dispatchDriverName: "",
  dispatchDriverSignature: "",
  dispatchDriverDate: dayjs().format("YYYY-MM-DD"),
  collectorName: "",
  collectorDesignation: "",
  collectorEmployeeNumber: "",
  collectorSignature: "",
  collectorDate: dayjs().format("YYYY-MM-DD"),
  vehicleRegistration: "",
  returnDateTime: dayjs().format("YYYY-MM-DDTHH:mm"),
  returningDriverOrGa: "",
  returnSupervisorName: userName || "",
  returnSupervisorSignature: "",
  returnSecurityOfficerName: "",
  returnSecurityOfficerSignature: "",
  returnDriverName: "",
  returnDriverSignature: "",
  returnDriverDate: dayjs().format("YYYY-MM-DD"),
  discrepancyNotes: "",
  notes: "",
  updatedBy: userName || "",
});

const emptyLossItem = () => ({
  itemCode: "",
  description: "",
  quantity: 1,
  colour: "",
});

const emptyInventoryForm = (userName: string, branch = "") => ({
  itemCode: "",
  description: "",
  category: "",
  uom: "EA",
  warehouseBranch: branch,
  storageLocation: "",
  totalQty: 0,
  reservedQty: 0,
  allocatedQty: 0,
  damagedQty: 0,
  reorderLevel: 0,
  reusable: true,
  status: "ACTIVE",
  performedBy: userName || "",
  notes: "",
});

const emptyReportForm = (userName: string, checklistNumber = "") => ({
  checklistNumber,
  dateOfReport: dayjs().format("YYYY-MM-DD"),
  responsibleBranch: "",
  items: [emptyLossItem()],
  supervisorName: userName || "",
  supervisorEmployeeNumber: "",
  supervisorSignature: "",
  supervisorDate: dayjs().format("YYYY-MM-DD"),
  itemsReturnedByName: "",
  itemsReturnedByEmployeeNumber: "",
  itemsReturnedBySignature: "",
  itemsReturnedByDate: dayjs().format("YYYY-MM-DD"),
  vehicleRegistrationNumber: "",
  varianceReportNotes: "",
  notes: "",
  updatedBy: userName || "",
});

const commandPanelSx = {
  p: { xs: 1.5, md: 1.9 },
  borderRadius: 2,
  border: `1px solid ${ifasColors.border}`,
  bgcolor: ifasColors.surface,
  boxShadow: "0 12px 28px rgba(20, 24, 31, 0.06)",
};

const inventoryFilterFieldSx = {
  minWidth: 0,
  bgcolor: ifasColors.surface,
  "& .MuiOutlinedInput-root": {
    minHeight: 44,
    borderRadius: "10px",
    "& fieldset": { borderColor: ifasColors.border },
    "&:hover fieldset": { borderColor: ifasColors.muted },
    "&.Mui-focused fieldset": { borderColor: ifasColors.orange },
  },
  "& .MuiInputLabel-root": {
    color: ifasColors.muted,
    fontWeight: 600,
  },
};

const subtleTileSx = {
  borderRadius: 2,
  border: `1px solid ${ifasColors.border}`,
  bgcolor: ifasColors.surface,
};

const detailActionStripSx = {
  p: 1.35,
  borderRadius: 2,
  border: `1px solid ${ifasColors.border}`,
  bgcolor: ifasColors.surface,
  boxShadow: "0 10px 24px rgba(20, 24, 31, 0.05)",
};

const headerActionButtonSx = {
  ...standardPrimaryBtnSx,
  minHeight: 42,
  px: 1.8,
  whiteSpace: "nowrap",
};

const headerEditButtonSx = {
  border: `1px solid ${ifasColors.border}`,
  bgcolor: ifasColors.surface,
  width: 42,
  height: 42,
  borderRadius: "10px",
  "&:hover": {
    bgcolor: ifasColors.softOrange,
    color: ifasColors.orange,
  },
};

const materialDetailTableScrollSx = {
  width: "100%",
  overflowX: "auto",
  overflowY: "hidden",
  pb: 0.5,
  "&::-webkit-scrollbar": {
    height: 10,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: alpha(ifasColors.orange, 0.35),
    borderRadius: 999,
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: ifasColors.page,
    borderRadius: 999,
  },
};

const materialDetailTableSx = {
  minWidth: 1360,
  tableLayout: "fixed",
  "& th": {
    whiteSpace: "nowrap",
    fontSize: 13.5,
    fontWeight: 900,
  },
  "& td, & th": {
    verticalAlign: "top",
  },
};

const materialStickyDescriptionCellSx = {
  width: 220,
  minWidth: 220,
  maxWidth: 220,
  bgcolor: ifasColors.surface,
  wordBreak: "break-word",
};

function WarehouseHeroCard({
  title,
  subtitle,
  status,
  meta,
}: {
  title: string;
  subtitle?: string;
  status?: string;
  meta?: ReactNode;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        ...commandPanelSx,
        background: `linear-gradient(135deg, ${ifasColors.surface} 0%, ${alpha(ifasColors.orange, 0.055)} 100%)`,
      }}
    >
      <Box display="flex" justifyContent="space-between" gap={1.5} flexWrap="wrap">
        <Box minWidth={0}>
          <Typography fontSize={11.5} fontWeight={900} color={ifasColors.muted} textTransform="uppercase" textAlign="left">
            Warehouse Record
          </Typography>
          <Typography fontSize={{ xs: 22, md: 28 }} fontWeight={900} color={ifasColors.text} mt={0.45} textAlign="left">
            {title}
          </Typography>
          {subtitle && (
            <Typography fontSize={13.5} color={ifasColors.muted} mt={0.55} textAlign="left">
              {subtitle}
            </Typography>
          )}
        </Box>
        {status && (
          <Chip
            label={formatOperationsStatus(status)}
            size="small"
            sx={{ ...getOperationsStatusSx(status), fontWeight: 900, alignSelf: "flex-start" }}
          />
        )}
      </Box>
      {meta && <Box display="flex" gap={1} flexWrap="wrap" mt={1.35}>{meta}</Box>}
    </Paper>
  );
}

function FormSection({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ ...subtleTileSx, p: 1.5 }}>
      <Box mb={1.25}>
        <Typography fontWeight={900} color={ifasColors.text} textAlign="left">
          {title}
        </Typography>
        {subtitle && (
          <Typography fontSize={12.5} color={ifasColors.muted} mt={0.35} textAlign="left">
            {subtitle}
          </Typography>
        )}
      </Box>
      {children}
    </Paper>
  );
}

function ModalStatTile({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: ReactNode;
  tone?: "neutral" | "accent" | "success" | "warning" | "danger";
}) {
  const tones: Record<string, { bg: string; fg: string }> = {
    neutral: { bg: ifasColors.page, fg: ifasColors.muted },
    accent: { bg: ifasColors.softOrange, fg: ifasColors.orange },
    success: { bg: STANDARD_TONES.green.bg, fg: STANDARD_TONES.green.color },
    warning: { bg: STANDARD_TONES.amber.bg, fg: STANDARD_TONES.amber.color },
    danger: { bg: STANDARD_TONES.red.bg, fg: STANDARD_TONES.red.color },
  };

  const resolvedTone = tones[tone] || tones.neutral;

  return (
    <Box
      sx={{
        borderRadius: 2,
        px: 1.15,
        py: 1,
        bgcolor: resolvedTone.bg,
        border: `1px solid ${alpha(resolvedTone.fg, 0.12)}`,
      }}
    >
      <Typography fontSize={11} fontWeight={900} color={resolvedTone.fg} textTransform="uppercase" textAlign="left">
        {label}
      </Typography>
      <Typography fontSize={18} fontWeight={900} color={ifasColors.text} mt={0.35} textAlign="left">
        {value}
      </Typography>
    </Box>
  );
}

const splitPipeParts = (value?: string | null) =>
  String(value || "")
    .split("|")
    .map((part) => part.trim())
    .filter(Boolean);

const parsePackageDetailParts = (value?: string | null) => {
  const parts = splitPipeParts(value);
  let version = "";

  const contentParts = parts.filter((part) => {
    if (/^version\s+\d+/i.test(part)) {
      version = part;
      return false;
    }
    return true;
  });

  return {
    packageName: contentParts[0] || "",
    summary: contentParts.slice(1).join(" | "),
    version,
  };
};

const NON_MATERIAL_REQUIREMENT_SECTIONS = new Set([
  "catering",
  "cemetery",
  "decor colours",
  "tombstone",
  "venue",
]);

const parseMaterialRequirementSections = (value?: string | null) =>
  String(value || "")
    .split(/[|\r\n]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part, index) => {
      const dividerIndex = part.indexOf(":");
      if (dividerIndex === -1) {
        return {
          title: index === 0 ? "Materials" : `Materials ${index + 1}`,
          items: part
            .split(",")
            .map((item) => item.trim())
            .filter(Boolean),
        };
      }

      return {
        title: part.slice(0, dividerIndex).trim(),
        items: part
          .slice(dividerIndex + 1)
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
      };
    })
    .filter((section) =>
      section.items.length
      && !NON_MATERIAL_REQUIREMENT_SECTIONS.has(section.title.toLowerCase()),
    );

const inferSetupLocationType = (sectionTitle?: string) => {
  const normalizedTitle = String(sectionTitle || "").toLowerCase();
  if (normalizedTitle.includes("cemetery")) {
    return "CEMETERY";
  }
  if (normalizedTitle.includes("home")) {
    return "HOME";
  }
  if (normalizedTitle.includes("hall") || normalizedTitle.includes("venue")) {
    return "VENUE";
  }
  return "VENUE";
};

const seedChecklistItemFromPackage = (rawItem: string, sectionTitle: string) => {
  const trimmedItem = rawItem.trim();
  let quantity = 1;
  let description = trimmedItem;
  let seedNote = `Package seed - ${sectionTitle}`;

  const rangeMatch = trimmedItem.match(/^(\d+)\s+to\s+(\d+)\s+(.+)$/i);
  if (rangeMatch) {
    quantity = Number(rangeMatch[1]) || 1;
    description = rangeMatch[3].trim();
    seedNote = `${seedNote} (range ${rangeMatch[1]} to ${rangeMatch[2]})`;
  } else {
    const includedMatch = trimmedItem.match(/^included\s+(.+)$/i);
    if (includedMatch) {
      description = includedMatch[1].trim();
      seedNote = `${seedNote} (included by package)`;
    } else {
      const quantityMatch = trimmedItem.match(/^(\d+)\s+(.+)$/);
      if (quantityMatch) {
        quantity = Number(quantityMatch[1]) || 1;
        description = quantityMatch[2].trim();
      }
    }
  }

  return {
    ...emptyMaterialItem(),
    description,
    quantity,
    setupLocationType: inferSetupLocationType(sectionTitle),
    sourceType: "INTERNAL_SUPPLY",
    sourceReference: seedNote,
  };
};

const buildSeededChecklistItems = (materialsValue?: string | null) =>
  parseMaterialRequirementSections(materialsValue).flatMap((section) =>
    section.items.map((item) => seedChecklistItemFromPackage(item, section.title)),
  );

const buildChecklistItemSignature = (item?: any) =>
  [
    String(item?.description || "").trim().toUpperCase(),
    String(item?.itemCode || "").trim().toUpperCase(),
    Number(item?.quantity || 0),
    String(item?.colour || "").trim().toUpperCase(),
    String(item?.setupLocationType || "").trim().toUpperCase(),
    String(item?.sourceType || "").trim().toUpperCase(),
    String(item?.sourceReference || "").trim().toUpperCase(),
  ].join("|");

const preferLaterValue = (...values: Array<string | null | undefined>) => {
  const populatedValues = values.filter((value) => String(value || "").trim().length > 0);
  return populatedValues.length ? populatedValues[populatedValues.length - 1] : "";
};

const preferLaterDateValue = (...values: Array<string | null | undefined>) => {
  const populatedValues = values.filter((value) => String(value || "").trim().length > 0);
  if (!populatedValues.length) {
    return "";
  }
  return populatedValues.sort((left, right) => String(left).localeCompare(String(right)))[populatedValues.length - 1];
};

const normalizeChecklistItems = (items?: any[]) => {
  const normalizedMap = new Map<string, any>();

  (items || []).forEach((rawItem: any) => {
    const item = {
      ...emptyMaterialItem(),
      ...rawItem,
      quantity: Number(rawItem?.quantity || 0),
      returnedQuantity: Number(rawItem?.returnedQuantity || 0),
      lossOrBrokenQuantity: Number(rawItem?.lossOrBrokenQuantity || 0),
      outstandingQuantity: Number(rawItem?.outstandingQuantity || 0),
      outstandingCollectedQuantity: Number(rawItem?.outstandingCollectedQuantity || 0),
    };
    const signature = buildChecklistItemSignature(item);
    const existingItem = normalizedMap.get(signature);

    if (!existingItem) {
      normalizedMap.set(signature, {
        ...item,
        returned: Number(item.returnedQuantity || 0) > 0 || Boolean(item.returned),
        lossOrBroken: Number(item.lossOrBrokenQuantity || 0) > 0 || Boolean(item.lossOrBroken),
        outstandingWithFamily:
          Number(item.outstandingQuantity || 0) > 0 || Boolean(item.outstandingWithFamily),
        outstandingCollected:
          Number(item.outstandingCollectedQuantity || 0) >= Number(item.outstandingQuantity || 0) &&
          Number(item.outstandingQuantity || 0) > 0,
      });
      return;
    }

    const returnedQuantity = Math.max(
      Number(existingItem.returnedQuantity || 0),
      Number(item.returnedQuantity || 0),
    );
    const lossOrBrokenQuantity = Math.max(
      Number(existingItem.lossOrBrokenQuantity || 0),
      Number(item.lossOrBrokenQuantity || 0),
    );
    const outstandingQuantity = Math.max(
      Number(existingItem.outstandingQuantity || 0),
      Number(item.outstandingQuantity || 0),
    );
    const outstandingCollectedQuantity = Math.max(
      Number(existingItem.outstandingCollectedQuantity || 0),
      Number(item.outstandingCollectedQuantity || 0),
    );

    normalizedMap.set(signature, {
      ...existingItem,
      ...item,
      description: preferLaterValue(existingItem.description, item.description),
      itemCode: preferLaterValue(existingItem.itemCode, item.itemCode),
      quantity: Math.max(Number(existingItem.quantity || 0), Number(item.quantity || 0)),
      colour: preferLaterValue(existingItem.colour, item.colour),
      setupLocationType: preferLaterValue(existingItem.setupLocationType, item.setupLocationType),
      sourceType: preferLaterValue(existingItem.sourceType, item.sourceType),
      sourceReference: preferLaterValue(existingItem.sourceReference, item.sourceReference),
      dispatched: Boolean(existingItem.dispatched) || Boolean(item.dispatched),
      returned: returnedQuantity > 0 || Boolean(existingItem.returned) || Boolean(item.returned),
      returnedQuantity,
      lossOrBroken:
        lossOrBrokenQuantity > 0 || Boolean(existingItem.lossOrBroken) || Boolean(item.lossOrBroken),
      lossOrBrokenQuantity,
      outstandingWithFamily:
        outstandingQuantity > 0 ||
        Boolean(existingItem.outstandingWithFamily) ||
        Boolean(item.outstandingWithFamily),
      outstandingQuantity,
      outstandingCollectionDueDate: preferLaterDateValue(
        existingItem.outstandingCollectionDueDate,
        item.outstandingCollectionDueDate,
      ),
      outstandingCollected:
        outstandingQuantity > 0 && outstandingCollectedQuantity >= outstandingQuantity,
      outstandingCollectedQuantity,
      outstandingCollectedAt: preferLaterDateValue(
        existingItem.outstandingCollectedAt,
        item.outstandingCollectedAt,
      ),
      outstandingCollectedBy: preferLaterValue(
        existingItem.outstandingCollectedBy,
        item.outstandingCollectedBy,
      ),
    });
  });

  return Array.from(normalizedMap.values());
};

const getItemReturnedQuantity = (item?: any) => Number(item?.returnedQuantity || 0);
const getItemLossQuantity = (item?: any) => Number(item?.lossOrBrokenQuantity || 0);
const getItemOutstandingQuantity = (item?: any) => Number(item?.outstandingQuantity || 0);
const getItemCollectedQuantity = (item?: any) => Number(item?.outstandingCollectedQuantity || 0);
const getItemPendingOutstandingQuantity = (item?: any) =>
  Math.max(0, getItemOutstandingQuantity(item) - getItemCollectedQuantity(item));

const hasPendingOutstandingItems = (items?: any[]) =>
  (items || []).some(
    (item: any) =>
      Boolean(item?.dispatched) &&
      getItemPendingOutstandingQuantity(item) > 0,
  );

const countPendingOutstandingItems = (items?: any[]) =>
  (items || []).filter(
    (item: any) =>
      Boolean(item?.dispatched) &&
      getItemPendingOutstandingQuantity(item) > 0,
  ).length;

const hasLossFollowupItems = (items?: any[]) =>
  (items || []).some(
    (item: any) =>
      Boolean(item?.dispatched) &&
      getItemLossQuantity(item) > 0,
  );

const resolveChecklistUiStatus = (
  checklist?: {
    status?: string | null;
    items?: any[];
    outstandingReconciled?: boolean | null;
  } | null,
) => {
  const rawStatus = String(checklist?.status || "DRAFT").toUpperCase();
  if (rawStatus === "DRAFT" || rawStatus === "DISPATCHED") {
    return rawStatus;
  }
  if (hasPendingOutstandingItems(checklist?.items)) {
    return "OUTSTANDING_MATERIALS";
  }
  if (checklist?.outstandingReconciled === false) {
    return "OUTSTANDING_MATERIALS";
  }
  return rawStatus;
};

const hasApplicableWarehousePackage = (value?: string | null) => {
  const normalized = String(value || "").trim().toUpperCase();
  return Boolean(normalized) && normalized !== "N/A" && normalized !== "NA" && normalized !== "NOT APPLICABLE";
};

function PreparationNoteCard({
  label,
  value,
  helper,
}: {
  label: string;
  value?: string | null;
  helper?: string;
}) {
  const displayValue = value && value.trim() ? value.trim() : "Not captured";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${ifasColors.border}`,
        bgcolor: ifasColors.surface,
      }}
    >
      <Typography
        fontSize={11.5}
        fontWeight={900}
        color={ifasColors.orange}
        textTransform="uppercase"
        textAlign="left"
      >
        {label}
      </Typography>
      <Typography
        fontSize={14.5}
        fontWeight={800}
        color={displayValue === "Not captured" ? ifasColors.muted : ifasColors.text}
        lineHeight={1.55}
        textAlign="left"
        mt={0.7}
        sx={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}
      >
        {displayValue}
      </Typography>
      {helper && displayValue !== "Not captured" && (
        <Typography fontSize={12.5} color={ifasColors.muted} mt={0.85} textAlign="left">
          {helper}
        </Typography>
      )}
    </Paper>
  );
}

function PreparationPackageCard({
  category,
  details,
}: {
  category?: string | null;
  details?: string | null;
}) {
  const parsedDetails = parsePackageDetailParts(details);
  const hasDetails = Boolean(parsedDetails.packageName || parsedDetails.summary || parsedDetails.version);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${ifasColors.border}`,
        bgcolor: ifasColors.surface,
      }}
    >
      <Typography
        fontSize={11.5}
        fontWeight={900}
        color={ifasColors.orange}
        textTransform="uppercase"
        textAlign="left"
      >
        Funeral Package
      </Typography>

      {!hasDetails ? (
        <Typography fontSize={14.5} fontWeight={800} color={ifasColors.muted} mt={0.7} textAlign="left">
          Not captured
        </Typography>
      ) : (
        <Stack spacing={1.1} mt={0.9}>
          <Box display="flex" gap={0.8} flexWrap="wrap">
            {category && (
              <Chip
                label={category}
                size="small"
                sx={{ bgcolor: ifasColors.softOrange, color: ifasColors.orange, fontWeight: 900 }}
              />
            )}
            {parsedDetails.version && (
              <Chip
                label={parsedDetails.version}
                size="small"
                sx={{ bgcolor: ifasColors.page, color: ifasColors.muted, fontWeight: 900 }}
              />
            )}
          </Box>

          {parsedDetails.packageName && (
            <Typography fontSize={16} fontWeight={900} color={ifasColors.text} textAlign="left">
              {parsedDetails.packageName}
            </Typography>
          )}

          {parsedDetails.summary && (
            <Typography fontSize={13.5} color={ifasColors.muted} lineHeight={1.6} textAlign="left">
              {parsedDetails.summary}
            </Typography>
          )}
        </Stack>
      )}
    </Paper>
  );
}

function MaterialRequirementBreakdown({
  value,
}: {
  value?: string | null;
}) {
  const sections = parseMaterialRequirementSections(value);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${ifasColors.border}`,
        bgcolor: ifasColors.surface,
      }}
    >
      <Box
        display="flex"
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        gap={1}
        flexWrap="wrap"
      >
        <Box minWidth={0}>
          <Typography
            fontSize={11.5}
            fontWeight={900}
            color={ifasColors.orange}
            textTransform="uppercase"
            textAlign="left"
          >
            Materials / Equipment Required
          </Typography>
          <Typography fontSize={12.5} color={ifasColors.muted} mt={0.35} textAlign="left">
            Package-linked setup items shown in a scan-friendly operational list.
          </Typography>
        </Box>
        {sections.length > 0 && (
          <Chip
            label={`${sections.reduce((count, section) => count + section.items.length, 0)} listed items`}
            size="small"
            sx={{ bgcolor: ifasColors.page, color: ifasColors.muted, fontWeight: 900 }}
          />
        )}
      </Box>

      {sections.length === 0 ? (
        <Typography fontSize={14.5} fontWeight={800} color={ifasColors.muted} mt={1.1} textAlign="left">
          Not captured
        </Typography>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" }}
          gap={1.25}
          mt={1.3}
        >
          {sections.map((section) => (
            <Paper
              key={section.title}
              variant="outlined"
              sx={{
                p: 1.2,
                borderRadius: 2,
                borderColor: alpha(ifasColors.orange, 0.16),
                bgcolor: ifasColors.page,
                boxShadow: "none",
              }}
            >
              <Typography fontSize={12.5} fontWeight={900} color={ifasColors.text} textAlign="left">
                {section.title}
              </Typography>
              <Stack spacing={0.7} mt={0.95}>
                {section.items.map((item) => (
                  <Box key={`${section.title}-${item}`} display="flex" alignItems="flex-start" gap={0.8}>
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: 999,
                        bgcolor: ifasColors.orange,
                        mt: "8px",
                        flex: "0 0 auto",
                      }}
                    />
                    <Typography fontSize={13.5} color={ifasColors.text} lineHeight={1.55} textAlign="left">
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          ))}
        </Box>
      )}
    </Paper>
  );
}

function ChecklistStageBanner({
  status,
  mode = "detail",
}: {
  status?: string | null;
  mode?: "detail" | "dialog";
}) {
  const normalizedStatus = String(status || "").toUpperCase();

  const config =
    normalizedStatus === "DISPATCHED"
      ? {
          toneBg: STANDARD_TONES.amber.bg,
          toneBorder: alpha(STANDARD_TONES.amber.color, 0.16),
          title: "Return stage",
          message:
            "Before you confirm return, allocate each dispatched item into Returned Qty, Outstanding Qty, and Loss / Broken Qty so the full dispatched quantity is accounted for.",
        }
      : normalizedStatus === "OUTSTANDING_MATERIALS"
        ? {
            toneBg: STANDARD_TONES.green.bg,
            toneBorder: alpha(STANDARD_TONES.green.color, 0.16),
            title: "Collection stage",
            message:
              "This checklist already has outstanding items. Once those items come back from the family, capture Collected Qty and complete Collected At / Collected By before reconciling outstanding materials.",
          }
        : normalizedStatus === "RETURNED"
          ? {
              toneBg: ifasColors.page,
              toneBorder: alpha(ifasColors.muted, 0.14),
              title: "Checklist closed",
              message:
                "This checklist has already been fully returned and reconciled. No outstanding collection step remains on this record.",
            }
          : normalizedStatus === "PARTIALLY_RETURNED"
            ? {
                toneBg: STANDARD_TONES.red.bg,
                toneBorder: alpha(STANDARD_TONES.red.color, 0.14),
                title: "Loss / broken follow-up",
                message:
                  "Some items were not fully returned. Review the flagged lines and continue with the broken / loss report flow.",
              }
            : {
                toneBg: STANDARD_TONES.blue.bg,
                toneBorder: alpha(STANDARD_TONES.blue.color, 0.14),
                title: "Dispatch stage",
                message:
                  "At checklist creation, tick only the items that are actually leaving the warehouse. Return, outstanding, broken, and collected quantities are captured later after the service.",
              };

  return (
    <Paper
      elevation={0}
      sx={{
        p: mode === "dialog" ? 1.4 : 1.5,
        borderRadius: 2,
        border: `1px solid ${config.toneBorder}`,
        bgcolor: config.toneBg,
      }}
    >
      <Typography fontSize={12.5} fontWeight={900} color={ifasColors.text} textAlign="left">
        {config.title}
      </Typography>
      <Typography fontSize={13} color={ifasColors.muted} lineHeight={1.65} mt={0.45} textAlign="left">
        {config.message}
      </Typography>
    </Paper>
  );
}

export default function WarehousePortal() {
  const { userName, userRole } = useSessionStore();
  const storedRole = useUserStore((state) => state.role);
  const effectiveRole = userRole || storedRole;
  const canViewWarehouse = canAccessIfasModule(effectiveRole, "warehouse");
  const canWritePreparation = hasIfasCapability(
    effectiveRole,
    "warehousePreparationWrite",
  );
  const canApprovePreparation = hasIfasCapability(
    effectiveRole,
    "warehousePreparationApprove",
  );
  const canWriteChecklist = hasIfasCapability(
    effectiveRole,
    "warehouseChecklistWrite",
  );
  const canApproveChecklist = hasIfasCapability(
    effectiveRole,
    "warehouseChecklistApprove",
  );
  const canWriteReport = hasIfasCapability(effectiveRole, "warehouseReportWrite");
  const canSubmitReport = hasIfasCapability(
    effectiveRole,
    "warehouseReportSubmit",
  );
  const canManageInventory = hasIfasCapability(effectiveRole, "inventoryManage");
  const normalizedWarehouseRole = String(effectiveRole || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  const canReviewWeeklyStockTake = ["inventory_stock_control_officer", "operations_manager", "ifas_administrator"].includes(normalizedWarehouseRole);
  const canReviewMonthlyStockTake = ["regional_operations_supervisor", "regional_manager", "operations_manager", "ifas_administrator"].includes(normalizedWarehouseRole);
  const [section, setSection] = useState<WarehouseSection>("INVENTORY");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [checklistActionIntent, setChecklistActionIntent] = useState<ChecklistActionIntent>(null);
  const [formState, setFormState] = useState<any>({});
const [dialogFormLoading, setDialogFormLoading] = useState(false);

  const [preparations, setPreparations] = useState<any[]>([]);
  const [preparationCounts, setPreparationCounts] = useState<Record<string, number>>({});
  const [preparationsTotal, setPreparationsTotal] = useState(0);
  const [selectedPreparation, setSelectedPreparation] = useState<any>(null);
  const [selectedPreparationNumber, setSelectedPreparationNumber] = useState("");

  const [checklists, setChecklists] = useState<any[]>([]);
  const [materialCounts, setMaterialCounts] = useState<Record<string, number>>({});
  const [checklistsTotal, setChecklistsTotal] = useState(0);
  const [selectedChecklist, setSelectedChecklist] = useState<any>(null);
  const [selectedChecklistNumber, setSelectedChecklistNumber] = useState("");

  const [reports, setReports] = useState<any[]>([]);
  const [reportCounts, setReportCounts] = useState<Record<string, number>>({});
  const [reportsTotal, setReportsTotal] = useState(0);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [selectedReportNumber, setSelectedReportNumber] = useState("");
  const [stockTakes, setStockTakes] = useState<any[]>([]);
  const [selectedStockTake, setSelectedStockTake] = useState<any>(null);
  const [stockTakeReviewRecord, setStockTakeReviewRecord] = useState<any>(null);
  const [stockTakeReviewNotes, setStockTakeReviewNotes] = useState("");
  const [cleaningRosters, setCleaningRosters] = useState<any[]>([]);
  const [cleaningCompletionRecord, setCleaningCompletionRecord] = useState<any>(null);
  const [cleaningCompletionForm, setCleaningCompletionForm] = useState<any>({});
  const [inventoryMaterials, setInventoryMaterials] = useState<any[]>([]);
  const [warehouseLocationOptions, setWarehouseLocationOptions] = useState<string[]>([]);
  const [inventoryLookupOptions, setInventoryLookupOptions] = useState<Record<number, any[]>>({});
  const [inventoryLookupLoading, setInventoryLookupLoading] = useState<Record<number, boolean>>({});
  const [inventoryResolveLoading, setInventoryResolveLoading] = useState(false);
  const inventoryLookupTimersRef = useRef<Record<number, number>>({});
  const inventoryLookupCacheRef = useRef<Map<string, any[]>>(new Map());
  const inventoryLookupQueryRef = useRef<Record<number, string>>({});
  const inventoryLookupBranchRef = useRef("");
  const inventoryResolveRequestRef = useRef(0);
  const [inventoryMovements, setInventoryMovements] = useState<any[]>([]);
  const [inventoryDashboard, setInventoryDashboard] = useState<any>(null);
  const [selectedInventoryMaterial, setSelectedInventoryMaterial] = useState<any>(null);
  const [inventoryImportOpen, setInventoryImportOpen] = useState(false);
  const [inventoryImportFile, setInventoryImportFile] = useState<File | null>(null);
  const [inventoryImportPreview, setInventoryImportPreview] = useState<any>(null);
  const [inventoryImportLoading, setInventoryImportLoading] = useState(false);
  const [inventoryCategoryFilter, setInventoryCategoryFilter] = useState("");
  const [inventoryStockFilter, setInventoryStockFilter] = useState("");
  const [inventoryPage, setInventoryPage] = useState(0);
  const [inventoryRowsPerPage] = useState(25);
  const [inventoryHistoryOpen, setInventoryHistoryOpen] = useState(false);
  const [inventoryHistoryLoading, setInventoryHistoryLoading] = useState(false);
  const [inventoryHistory, setInventoryHistory] = useState<any>(null);
  const [inventoryHistoryMortuary, setInventoryHistoryMortuary] = useState("");
  const [inventoryHistoryMovementType, setInventoryHistoryMovementType] = useState("");
  const [inventoryHistoryStartDate, setInventoryHistoryStartDate] = useState("");
  const [inventoryHistoryEndDate, setInventoryHistoryEndDate] = useState("");
  const [inventoryHistorySearch, setInventoryHistorySearch] = useState("");
  const [inventoryHistoryAppliedSearch, setInventoryHistoryAppliedSearch] = useState("");
  const [inventoryHistoryPage, setInventoryHistoryPage] = useState(0);
  const [inventoryHistoryRowsPerPage, setInventoryHistoryRowsPerPage] = useState(20);

  const isPreparationDetails = section === "PREPARATIONS" && Boolean(selectedPreparationNumber);
  const isMaterialDetails = section === "MATERIAL" && Boolean(selectedChecklistNumber);
  const isReportDetails = section === "REPORTS" && Boolean(selectedReportNumber);
  const getWarehouseActorName = () =>
    userName ||
    selectedChecklist?.dispatchSupervisorName ||
    selectedChecklist?.returnSupervisorName ||
    selectedChecklist?.driverName ||
    selectedPreparation?.plannerSubmittedToWarehouseBy ||
    selectedPreparation?.familyRepresentativeName ||
    "Warehouse User";

  const preparationMetrics = useMemo(
    () => [
      { label: "Pending", value: preparationCounts.PENDING || 0, icon: <Inventory2OutlinedIcon />, tone: "amber" as Tone, caption: "Awaiting preparation" },
      { label: "In Preparation", value: preparationCounts.IN_PREPARATION || 0, icon: <LocalShippingOutlinedIcon />, tone: "blue" as Tone, caption: "Being packed" },
      { label: "Ready", value: preparationCounts.READY || 0, icon: <AssignmentTurnedInOutlinedIcon />, tone: "green" as Tone, caption: "Ready for dispatch" },
    ],
    [preparationCounts],
  );

  const materialMetrics = useMemo(
    () => [
      { label: "Draft", value: materialCounts.DRAFT || 0, icon: <Inventory2OutlinedIcon />, tone: "amber" as Tone, caption: "Not yet dispatched" },
      { label: "Dispatched", value: materialCounts.DISPATCHED || 0, icon: <LocalShippingOutlinedIcon />, tone: "blue" as Tone, caption: "Out with fleet" },
      { label: "Returned", value: materialCounts.RETURNED || 0, icon: <AssignmentTurnedInOutlinedIcon />, tone: "green" as Tone, caption: "Reconciled" },
      {
        label: "At Risk",
        value:
          (materialCounts.OUTSTANDING_MATERIALS || 0) +
          (materialCounts.PARTIALLY_RETURNED || 0) +
          (materialCounts.LOSS_REPORTED || 0),
        icon: <ReportProblemOutlinedIcon />,
        tone: "red" as Tone,
        caption: "Loss / broken flagged",
      },
    ],
    [materialCounts],
  );

  const reportMetrics = useMemo(
    () => [
      { label: "Draft Reports", value: reportCounts.DRAFT || 0, icon: <ReportProblemOutlinedIcon />, tone: "amber" as Tone, caption: "Being compiled" },
      { label: "Submitted", value: reportCounts.SUBMITTED_TO_INVENTORY || 0, icon: <AssignmentTurnedInOutlinedIcon />, tone: "blue" as Tone, caption: "Sent to inventory" },
    ],
    [reportCounts],
  );

  const inventoryMetrics = useMemo(
    () => [
      { label: "Materials", value: inventoryDashboard?.itemCount || 0, icon: <Inventory2OutlinedIcon />, tone: "orange" as Tone, caption: "Active item codes" },
      { label: "Available", value: inventoryDashboard?.availableQuantity || 0, icon: <AssignmentTurnedInOutlinedIcon />, tone: "blue" as Tone, caption: "Units available" },
      { label: "Reserved", value: inventoryDashboard?.reservedQuantity || 0, icon: <LocalShippingOutlinedIcon />, tone: "purple" as Tone, caption: "Held against quotes" },
      { label: "Low Stock", value: inventoryDashboard?.lowStockCount || 0, icon: <ReportProblemOutlinedIcon />, tone: "amber" as Tone, caption: "At or below reorder level" },
    ],
    [inventoryDashboard],
  );

  const stockTakeMetrics = useMemo(
    () => [
      { label: "Scheduled", value: stockTakes.filter((item: any) => item.status === "DRAFT").length, icon: <Inventory2OutlinedIcon />, tone: "amber" as Tone, caption: "Upcoming counts" },
      { label: "In Progress", value: stockTakes.filter((item: any) => item.status === "IN_PROGRESS").length, icon: <LocalShippingOutlinedIcon />, tone: "blue" as Tone, caption: "Being counted" },
      { label: "Completed", value: stockTakes.filter((item: any) => item.status === "REVIEWED").length, icon: <AssignmentTurnedInOutlinedIcon />, tone: "green" as Tone, caption: "This period" },
    ],
    [stockTakes],
  );

  const cleaningMetrics = useMemo(
    () => [
      { label: "Scheduled", value: cleaningRosters.filter((item: any) => ["SCHEDULED", "OVERDUE"].includes(item.status)).length, icon: <Inventory2OutlinedIcon />, tone: "amber" as Tone, caption: "This week" },
      { label: "Completed", value: cleaningRosters.filter((item: any) => item.status === "COMPLETED").length, icon: <AssignmentTurnedInOutlinedIcon />, tone: "green" as Tone, caption: "This period" },
    ],
    [cleaningRosters],
  );

  const inventoryBranchOptions = useMemo(
    () => Array.from(new Set([
      ...warehouseLocationOptions,
      ...(inventoryMaterials || []).map((item: any) => String(item.warehouseBranch || "").trim()),
    ].filter(Boolean))).sort((first, second) => first.localeCompare(second)),
    [inventoryMaterials, warehouseLocationOptions],
  );

  const inventoryCategoryOptions = useMemo(
    () => Array.from(new Set((inventoryMaterials || [])
      .map((item: any) => String(item.category || "Uncategorised").trim())
      .filter(Boolean))).sort((first, second) => first.localeCompare(second)),
    [inventoryMaterials],
  );

  const filteredInventoryMaterials = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return (inventoryMaterials || []).filter((item: any) => {
      const matchesSearch = !query || [
        item.itemCode,
        item.description,
        item.category,
        item.warehouseBranch,
        item.storageLocation,
      ].some((value) => String(value || "").toLowerCase().includes(query));
      const matchesBranch = !branchFilter || String(item.warehouseBranch || "").toLowerCase() === branchFilter.toLowerCase();
      const matchesCategory = !inventoryCategoryFilter || String(item.category || "Uncategorised") === inventoryCategoryFilter;
      const matchesStatus = !statusFilter || String(item.status || "ACTIVE").toUpperCase() === statusFilter.toUpperCase();
      const available = Number(item.availableQty ?? 0);
      const damaged = Number(item.damagedQty ?? 0);
      const matchesStock = !inventoryStockFilter
        || (inventoryStockFilter === "IN_STOCK" && available > 0)
        || (inventoryStockFilter === "ZERO_STOCK" && available === 0)
        || (inventoryStockFilter === "LOW_STOCK" && Boolean(item.lowStock))
        || (inventoryStockFilter === "DAMAGED" && damaged > 0);
      return matchesSearch && matchesBranch && matchesCategory && matchesStatus && matchesStock;
    });
  }, [inventoryMaterials, searchText, branchFilter, inventoryCategoryFilter, statusFilter, inventoryStockFilter]);

  const paginatedInventoryMaterials = useMemo(
    () => filteredInventoryMaterials.slice(
      inventoryPage * inventoryRowsPerPage,
      inventoryPage * inventoryRowsPerPage + inventoryRowsPerPage,
    ),
    [filteredInventoryMaterials, inventoryPage, inventoryRowsPerPage],
  );

  const resetDialog = () => {
    Object.values(inventoryLookupTimersRef.current).forEach((timer) => window.clearTimeout(timer));
    inventoryLookupTimersRef.current = {};
    inventoryResolveRequestRef.current += 1;
    setInventoryLookupOptions({});
    setInventoryLookupLoading({});
    setInventoryResolveLoading(false);
    inventoryLookupBranchRef.current = "";
    setDialogMode(null);
    setChecklistActionIntent(null);
    setFormState({});
    setDialogFormLoading (false);
  };

  useEffect(() => () => {
    Object.values(inventoryLookupTimersRef.current).forEach((timer) => window.clearTimeout(timer));
    inventoryResolveRequestRef.current += 1;
  }, []);

  useEffect(() => {
    let active = true;
    void getOperationalLocations("WAREHOUSE").then((response: any) => {
      if (active && response?.status === 200 && Array.isArray(response.data)) {
        setWarehouseLocationOptions(response.data.map((location: any) => location.branchName));
      }
    });
    return () => { active = false; };
  }, []);

  const updateField = (field: string, value: any) => {
    setFormState((prev: any) => ({ ...prev, [field]: value }));
  };

  const buildPreparationPayload = (state: any) => {
    const { claimId, ...payload } = state || {};
    return payload;
  };

  const buildChecklistDetailPreviewFromFormState = (state: any) => ({
    preparationNumber: state?.preparationNumber || "",
    driverName: state?.driverName || "",
    generalAssistantNames: state?.generalAssistantNames || "",
    hearseTrailerRegistration: state?.hearseTrailerRegistration || "",
    transportEquipment: state?.transportEquipment || "",
    vehicleRegistration: state?.vehicleRegistration || "",
    collectorName: state?.collectorName || "",
    collectorDesignation: state?.collectorDesignation || "",
    collectorEmployeeNumber: state?.collectorEmployeeNumber || "",
    collectorSignature: state?.collectorSignature || "",
    collectorDate: state?.collectorDate || "",
    returnDateTime: state?.returnDateTime || "",
    returningDriverOrGa: state?.returningDriverOrGa || "",
    returnSupervisorName: state?.returnSupervisorName || "",
    returnSupervisorSignature: state?.returnSupervisorSignature || "",
    discrepancyNotes: state?.discrepancyNotes || "",
    notes: state?.notes || "",
    dispatchSupervisorName: state?.dispatchSupervisorName || "",
    dispatchSupervisorDesignation: state?.dispatchSupervisorDesignation || "",
    dispatchSupervisorEmployeeNumber: state?.dispatchSupervisorEmployeeNumber || "",
    dispatchSupervisorSignature: state?.dispatchSupervisorSignature || "",
    dispatchSupervisorDate: state?.dispatchSupervisorDate || "",
    items: normalizeChecklistItems(state?.items || []).map((item: any) => ({
      description: item.description || "",
      itemCode: item.itemCode || "",
      quantity: Number(item.quantity || 0),
      colour: item.colour || "",
      setupLocationType: item.setupLocationType || "",
      sourceType: item.sourceType || "",
      sourceReference: item.sourceReference || "",
      dispatched: Boolean(item.dispatched),
      returned: Number(item.returnedQuantity || 0) > 0,
      returnedQuantity: Number(item.returnedQuantity || 0),
      lossOrBroken: Number(item.lossOrBrokenQuantity || 0) > 0,
      lossOrBrokenQuantity: Number(item.lossOrBrokenQuantity || 0),
      outstandingWithFamily: Number(item.outstandingQuantity || 0) > 0,
      outstandingQuantity: Number(item.outstandingQuantity || 0),
      outstandingCollectionDueDate: item.outstandingCollectionDueDate || "",
      outstandingCollected: Number(item.outstandingCollectedQuantity || 0) >= Number(item.outstandingQuantity || 0) && Number(item.outstandingQuantity || 0) > 0,
      outstandingCollectedQuantity: Number(item.outstandingCollectedQuantity || 0),
      outstandingCollectedAt: item.outstandingCollectedAt || "",
      outstandingCollectedBy: item.outstandingCollectedBy || "",
    })),
  });

  const updateArrayItem = (field: string, index: number, childField: string, value: any) => {
    setFormState((prev: any) => ({
      ...prev,
      [field]: (prev[field] || []).map((item: any, itemIndex: number) =>
        itemIndex === index ? { ...item, [childField]: value } : item,
      ),
    }));
  };

  const addArrayItem = (field: string, template: any) => {
    setFormState((prev: any) => ({
      ...prev,
      [field]: [...(prev[field] || []), template],
    }));
  };

  const removeArrayItem = (field: string, index: number) => {
    setFormState((prev: any) => ({
      ...prev,
      [field]: (prev[field] || []).filter((_: any, itemIndex: number) => itemIndex !== index),
    }));
  };

  const loadPreparations = useCallback(async () => {
    setLoading(true);
    const response = await getWarehousePreparations({
      status: statusFilter || undefined,
      branch: branchFilter || undefined,
      searchText: searchText || undefined,
      page,
      size: 10,
    });
    if (response?.status === 200) {
      setPreparations(response.data?.preparations || []);
      setPreparationCounts(response.data?.statusCounts || {});
      setPreparationsTotal(Number(response.data?.totalCount || 0));
    } else {
      toast.error(response?.data?.message || "Failed to load warehouse preparations");
    }
    setLoading(false);
  }, [statusFilter, branchFilter, searchText, page]);

  const loadMaterialChecklists = useCallback(async () => {
    setLoading(true);
    const response = await getWarehouseMaterialChecklists({
      status: statusFilter || undefined,
      searchText: searchText || undefined,
      page,
      size: 10,
    });
    if (response?.status === 200) {
      setChecklists(response.data?.checklists || []);
      setMaterialCounts(response.data?.statusCounts || {});
      setChecklistsTotal(Number(response.data?.totalCount || 0));
    } else {
      toast.error(response?.data?.message || "Failed to load material checklists");
    }
    setLoading(false);
  }, [statusFilter, searchText, page]);

  const loadReports = useCallback(async () => {
    setLoading(true);
    const response = await getWarehouseBrokenLossReports({
      status: statusFilter || undefined,
      searchText: searchText || undefined,
      page,
      size: 10,
    });
    if (response?.status === 200) {
      setReports(response.data?.reports || []);
      setReportCounts(response.data?.statusCounts || {});
      setReportsTotal(Number(response.data?.totalCount || 0));
    } else {
      toast.error(response?.data?.message || "Failed to load broken/loss reports");
    }
    setLoading(false);
  }, [statusFilter, searchText, page]);

  const loadStockTakes = useCallback(async () => {
    setLoading(true);
    const response = await getWarehouseStockTakes(statusFilter || undefined);
    if (response?.status === 200) {
      setStockTakes(response.data || []);
    } else {
      toast.error(response?.data?.message || "Failed to load stock takes");
    }
    setLoading(false);
  }, [statusFilter]);

  const loadCleaningRosters = useCallback(async () => {
    setLoading(true);
    const response = await getWarehouseCleaningRosters(statusFilter || undefined);
    if (response?.status === 200) {
      setCleaningRosters(response.data || []);
    } else {
      toast.error(response?.data?.message || "Failed to load cleaning rosters");
    }
    setLoading(false);
  }, [statusFilter]);

  const loadInventory = useCallback(async () => {
    setLoading(true);
    const inventoryBranchFilter = section === "INVENTORY" ? branchFilter || undefined : undefined;
    const [materialsResponse, dashboardResponse, movementsResponse] = await Promise.all([
      getInventoryMaterials({}),
      getInventoryDashboard(inventoryBranchFilter),
      getInventoryMovements(inventoryBranchFilter),
    ]);
    if (materialsResponse?.status === 200) {
      setInventoryMaterials(materialsResponse.data || []);
    } else {
      toast.error(materialsResponse?.data?.message || "Failed to load inventory materials");
    }
    if (dashboardResponse?.status === 200) {
      setInventoryDashboard(dashboardResponse.data || null);
    }
    if (movementsResponse?.status === 200) {
      setInventoryMovements(movementsResponse.data || []);
    }
    setLoading(false);
  }, [branchFilter, section]);

  const loadInventoryHistory = useCallback(async () => {
    if (!inventoryHistoryOpen) return;
    setInventoryHistoryLoading(true);
    const response = await getInventoryMovementHistory({
      branch: inventoryHistoryMortuary || undefined,
      movementType: inventoryHistoryMovementType || undefined,
      searchText: inventoryHistoryAppliedSearch || undefined,
      startDate: inventoryHistoryStartDate || undefined,
      endDate: inventoryHistoryEndDate || undefined,
      page: inventoryHistoryPage,
      size: inventoryHistoryRowsPerPage,
    });
    if (response?.status === 200) {
      setInventoryHistory(response.data);
    } else {
      toast.error(response?.data?.message || "Failed to load inventory movement history.");
    }
    setInventoryHistoryLoading(false);
  }, [
    inventoryHistoryOpen,
    inventoryHistoryMortuary,
    inventoryHistoryMovementType,
    inventoryHistoryAppliedSearch,
    inventoryHistoryStartDate,
    inventoryHistoryEndDate,
    inventoryHistoryPage,
    inventoryHistoryRowsPerPage,
  ]);

  const closeInventoryHistory = () => {
    if (inventoryHistoryLoading) return;
    setInventoryHistoryOpen(false);
  };

  const closeInventoryImport = (force = false) => {
    if (inventoryImportLoading && !force) return;
    setInventoryImportOpen(false);
    setInventoryImportFile(null);
    setInventoryImportPreview(null);
  };

  const validateInventoryImport = async () => {
    if (!inventoryImportFile) {
      toast.error("Select an Excel inventory file first.");
      return;
    }
    setInventoryImportLoading(true);
    const response = await previewInventoryImport(inventoryImportFile);
    if (response?.status === 200) {
      setInventoryImportPreview(response.data);
      toast.success("Inventory file validated. Review the preview before importing.");
    } else {
      setInventoryImportPreview(null);
      toast.error(response?.data?.message || "Inventory Excel validation failed.");
    }
    setInventoryImportLoading(false);
  };

  const confirmInventoryImport = async () => {
    if (!inventoryImportFile || !inventoryImportPreview?.readyRecords) return;
    setInventoryImportLoading(true);
    const response = await importInventoryMaterials(inventoryImportFile, userName || "Inventory / Stock Control");
    if (response?.status === 200) {
      const created = response.data?.createdRecords || 0;
      const unresolved = response.data?.errorRecords || 0;
      toast.success(`${created} inventory records imported successfully${unresolved ? `; ${unresolved} unresolved rows were skipped` : ""}.`);
      setInventoryImportLoading(false);
      closeInventoryImport(true);
      await loadInventory();
      return;
    } else {
      toast.error(response?.data?.message || "Inventory import failed.");
    }
    setInventoryImportLoading(false);
  };

  const loadPreparation = useCallback(async (preparationNumber: string) => {
    setDetailLoading(true);
    const response = await getWarehousePreparation(preparationNumber);
    if (response?.status === 200) {
      setSelectedPreparation(response.data);
    } else {
      toast.error(response?.data?.message || "Failed to load preparation details");
    }
    setDetailLoading(false);
  }, []);

  const loadChecklist = useCallback(async (checklistNumber: string) => {
    setDetailLoading(true);
    const response = await getWarehouseMaterialChecklist(checklistNumber);
    if (response?.status === 200) {
      setSelectedChecklist({
        ...response.data,
        items: normalizeChecklistItems(response.data?.items || []),
      });
    } else {
      toast.error(response?.data?.message || "Failed to load material checklist details");
    }
    setDetailLoading(false);
  }, []);

  const loadReport = useCallback(async (reportNumber: string) => {
    setDetailLoading(true);
    const response = await getWarehouseBrokenLossReport(reportNumber);
    if (response?.status === 200) {
      setSelectedReport(response.data);
    } else {
      toast.error(response?.data?.message || "Failed to load broken/loss report details");
    }
    setDetailLoading(false);
  }, []);

  useEffect(() => {
    setPage(0);
  }, [section, statusFilter, branchFilter, searchText]);

  useEffect(() => {
    setInventoryPage(0);
  }, [searchText, branchFilter, statusFilter, inventoryCategoryFilter, inventoryStockFilter]);

  useEffect(() => {
    if (section === "PREPARATIONS" && !selectedPreparationNumber) loadPreparations();
  }, [section, selectedPreparationNumber, loadPreparations]);

  useEffect(() => {
    if (section === "MATERIAL" && !selectedChecklistNumber) loadMaterialChecklists();
  }, [section, selectedChecklistNumber, loadMaterialChecklists]);

  useEffect(() => {
    if (section === "REPORTS" && !selectedReportNumber) loadReports();
  }, [section, selectedReportNumber, loadReports]);

  useEffect(() => {
    if (section === "INVENTORY") loadInventory();
  }, [section, loadInventory]);

  useEffect(() => {
    if (section === "STOCK_TAKE") loadStockTakes();
  }, [section, loadStockTakes]);

  useEffect(() => {
    if (section === "CLEANING") loadCleaningRosters();
  }, [section, loadCleaningRosters]);

  useEffect(() => {
    loadInventoryHistory();
  }, [loadInventoryHistory]);

  useEffect(() => {
    if (section === "PREPARATIONS" && selectedPreparationNumber) {
      loadPreparation(selectedPreparationNumber);
    }
  }, [section, selectedPreparationNumber, loadPreparation]);

  useEffect(() => {
    if (section === "MATERIAL" && selectedChecklistNumber) {
      loadChecklist(selectedChecklistNumber);
    }
  }, [section, selectedChecklistNumber, loadChecklist]);

  useEffect(() => {
    if (section === "REPORTS" && selectedReportNumber) {
      loadReport(selectedReportNumber);
    }
  }, [section, selectedReportNumber, loadReport]);

  const resetDetailsForSection = (nextSection: WarehouseSection) => {
    if (nextSection !== "PREPARATIONS") {
      setSelectedPreparationNumber("");
      setSelectedPreparation(null);
    }
    if (nextSection !== "MATERIAL") {
      setSelectedChecklistNumber("");
      setSelectedChecklist(null);
    }
    if (nextSection !== "REPORTS") {
      setSelectedReportNumber("");
      setSelectedReport(null);
    }
    if (nextSection !== "INVENTORY") {
      setSelectedInventoryMaterial(null);
    }
  };

  const handleSectionChange = (_: any, value: WarehouseSection) => {
    setSection(value);
    setStatusFilter("");
    setBranchFilter("");
    setSearchText("");
    setInventoryCategoryFilter("");
    setInventoryStockFilter("");
    setInventoryPage(0);
    resetDetailsForSection(value);
  };

  const openPreparationDialog = (isEdit = false) => {
    if (isEdit && selectedPreparation) {
      setDialogFormLoading(true);  
      setDialogMode("EDIT_PREPARATION");
      window.setTimeout(() => {
      setFormState({
        caseNumber: selectedPreparation.caseNumber || "",
        funeralDate: selectedPreparation.funeralDate || dayjs().format("YYYY-MM-DD"),
        funeralSurnameArea: selectedPreparation.funeralSurnameArea || "",
        funeralCategory: selectedPreparation.funeralCategory || "",
        claimId: selectedPreparation.claimId || null,
        warehouseBranch: selectedPreparation.warehouseBranch || "",
        serviceVenue: selectedPreparation.serviceVenue || "",
        cemeteryDetails: selectedPreparation.cemeteryDetails || "",
        casketType: selectedPreparation.casketType || "",
        hearseAllocation: selectedPreparation.hearseAllocation || "",
        generalAssistantNames: selectedPreparation.generalAssistantNames || "",
        vehicleConfirmed: Boolean(selectedPreparation.vehicleConfirmed),
        fleetCoordinatorConfirmation: selectedPreparation.fleetCoordinatorConfirmation || "",
        assignedDriverName: selectedPreparation.assignedDriverName || "",
        assignedVehicleRegistration: selectedPreparation.assignedVehicleRegistration || "",
        assignedVehicleType: selectedPreparation.assignedVehicleType || "",
        transportRequired: selectedPreparation.transportRequired !== false,
        transportVehicleCount: Math.max(1, Number(selectedPreparation.transportVehicleCount || 1)),
        transportRequirements: selectedPreparation.transportRequirements || "",
        decorRequirements: selectedPreparation.decorRequirements || "",
        photoAndFrameRequirements: selectedPreparation.photoAndFrameRequirements || "",
        funeralPackageDetails: selectedPreparation.funeralPackageDetails || "",
        materialsAndEquipmentRequired: selectedPreparation.materialsAndEquipmentRequired || "",
        specialInstructions: selectedPreparation.specialInstructions || "",
        equipmentReadyChecklist: selectedPreparation.equipmentReadyChecklist || "",
        cleaningRosterTasks: selectedPreparation.cleaningRosterTasks || "",
        stockCountData: selectedPreparation.stockCountData || "",
        equipmentStatus: selectedPreparation.equipmentStatus || "",
        notes: selectedPreparation.notes || "",
        updatedBy: userName || "",
      });
      setDialogMode("EDIT_PREPARATION");
      setDialogFormLoading(false); 
          }, 0);
      return;
    }
    setFormState(emptyPreparationForm(userName || ""));
    setDialogMode("CREATE_PREPARATION");
  };

  const openMaterialDialog = async (
    isEdit = false,
    preparationNumber = "",
    actionIntent: ChecklistActionIntent = null,
  ) => {
    setChecklistActionIntent(actionIntent);
    if (isEdit && selectedChecklist) {
      setDialogFormLoading (true);
      setDialogMode("EDIT_MATERIAL");
      const preparationResponse = selectedChecklist.preparationNumber
        ? await getWarehousePreparation(selectedChecklist.preparationNumber)
        : null;
      const linkedPreparation = preparationResponse?.status === 200
        ? preparationResponse.data
        : null;
      if (linkedPreparation) {
        setSelectedPreparation(linkedPreparation);
      }
      window.setTimeout(() => {
          setFormState({
            preparationNumber: selectedChecklist.preparationNumber || "",
          warehouseBranch: selectedChecklist.warehouseBranch || "",
          driverName: linkedPreparation?.assignedDriverName || selectedChecklist.driverName || "",
          warehouseTransportRequired: linkedPreparation?.transportRequired !== false,
          warehouseTransportRequestNumber: linkedPreparation?.transportRequestNumber || "",
          warehouseTransportAllocated: Boolean(
            linkedPreparation?.vehicleConfirmed
              && linkedPreparation?.assignedDriverName
              && linkedPreparation?.assignedVehicleRegistration,
          ),
          generalAssistantNames: selectedChecklist.generalAssistantNames || "",
          hearseTrailerRegistration: selectedChecklist.hearseTrailerRegistration || "",
          transportEquipment: selectedChecklist.transportEquipment || "",
          items: normalizeChecklistItems(selectedChecklist.items || []).map((item: any) => ({
            description: item.description || "",
            itemCode: item.itemCode || (String(item.sourceType || "").toUpperCase() === "INVENTORY" ? item.sourceReference || "" : ""),
            quantity: item.quantity || 1,
            colour: item.colour || "",
            setupLocationType: item.setupLocationType || "",
            sourceType: item.sourceType || "",
            sourceReference: item.sourceReference || "",
            dispatched: Boolean(item.dispatched),
            returned: Boolean(item.returned),
            returnedQuantity: Number(item.returnedQuantity || 0),
            lossOrBroken: Boolean(item.lossOrBroken),
            lossOrBrokenQuantity: Number(item.lossOrBrokenQuantity || 0),
            outstandingWithFamily: Boolean(item.outstandingWithFamily),
            outstandingQuantity: Number(item.outstandingQuantity || 0),
            outstandingCollectionDueDate: item.outstandingCollectionDueDate || "",
            outstandingCollected: Boolean(item.outstandingCollected),
            outstandingCollectedQuantity: Number(item.outstandingCollectedQuantity || 0),
            outstandingCollectedAt: item.outstandingCollectedAt
              ? dayjs(item.outstandingCollectedAt).format("YYYY-MM-DDTHH:mm")
              : "",
            outstandingCollectedBy: item.outstandingCollectedBy || "",
          })),
          dispatchSupervisorName: selectedChecklist.dispatchSupervisorName || userName || "",
          dispatchSupervisorDesignation: selectedChecklist.dispatchSupervisorDesignation || "Warehouse Supervisor",
          dispatchSupervisorEmployeeNumber: selectedChecklist.dispatchSupervisorEmployeeNumber || "",
          dispatchSupervisorSignature: selectedChecklist.dispatchSupervisorSignature || "",
          dispatchSupervisorDate: selectedChecklist.dispatchSupervisorDate || dayjs().format("YYYY-MM-DD"),
          dispatchSecurityOfficerName: selectedChecklist.dispatchSecurityOfficerName || "",
          dispatchSecurityOfficerSignature: selectedChecklist.dispatchSecurityOfficerSignature || "",
          dispatchSecurityOfficerDate: selectedChecklist.dispatchSecurityOfficerDate || dayjs().format("YYYY-MM-DD"),
          dispatchDriverName: linkedPreparation?.assignedDriverName || selectedChecklist.dispatchDriverName || "",
          dispatchDriverSignature: selectedChecklist.dispatchDriverSignature || "",
          dispatchDriverDate: selectedChecklist.dispatchDriverDate || dayjs().format("YYYY-MM-DD"),
          collectorName: selectedChecklist.collectorName || "",
          collectorDesignation: selectedChecklist.collectorDesignation || "",
          collectorEmployeeNumber: selectedChecklist.collectorEmployeeNumber || "",
          collectorSignature: selectedChecklist.collectorSignature || "",
          collectorDate: selectedChecklist.collectorDate || dayjs().format("YYYY-MM-DD"),
          vehicleRegistration: linkedPreparation?.assignedVehicleRegistration || selectedChecklist.vehicleRegistration || "",
          returnDateTime: selectedChecklist.returnDateTime
            ? dayjs(selectedChecklist.returnDateTime).format("YYYY-MM-DDTHH:mm")
            : dayjs().format("YYYY-MM-DDTHH:mm"),
          returningDriverOrGa: selectedChecklist.returningDriverOrGa || "",
          returnSupervisorName: selectedChecklist.returnSupervisorName || userName || "",
          returnSupervisorSignature: selectedChecklist.returnSupervisorSignature || "",
          returnSecurityOfficerName: selectedChecklist.returnSecurityOfficerName || "",
          returnSecurityOfficerSignature: selectedChecklist.returnSecurityOfficerSignature || "",
          returnDriverName: selectedChecklist.returnDriverName || "",
          returnDriverSignature: selectedChecklist.returnDriverSignature || "",
          returnDriverDate: selectedChecklist.returnDriverDate || dayjs().format("YYYY-MM-DD"),
          discrepancyNotes: selectedChecklist.discrepancyNotes || "",
          notes: selectedChecklist.notes || "",
          updatedBy: userName || "",
        });
        setDialogFormLoading (false);
      }, 0);
      return;
    }
    const seededForm = emptyMaterialForm(userName || "", preparationNumber);
    const linkedPreparation =
      selectedPreparation?.preparationNumber === preparationNumber ? selectedPreparation : null;
    const seededItems = linkedPreparation
      ? buildSeededChecklistItems(linkedPreparation.materialsAndEquipmentRequired)
      : [];
    const warehouseBranch = linkedPreparation?.warehouseBranch || "";
    const checklistItems = (seededItems.length ? seededItems : seededForm.items).map((item: any) => ({
      ...item,
      packageSeeded: String(item.sourceReference || "").startsWith("Package seed"),
      sourceType: item.description ? "INVENTORY" : item.sourceType,
      inventoryResolutionStatus: item.description
        ? warehouseBranch ? "RESOLVING" : "BRANCH_REQUIRED"
        : undefined,
      inventorySelection: null,
    }));
    inventoryResolveRequestRef.current += 1;
    inventoryLookupBranchRef.current = warehouseBranch;
    setInventoryLookupOptions({});
    setInventoryLookupLoading({});
    setDialogFormLoading(false);
    setDialogMode("CREATE_MATERIAL");
    setFormState({
      ...seededForm,
      warehouseBranch,
      items: checklistItems,
      driverName: linkedPreparation?.assignedDriverName || "",
      warehouseTransportRequired: linkedPreparation?.transportRequired !== false,
      warehouseTransportRequestNumber: linkedPreparation?.transportRequestNumber || "",
      warehouseTransportAllocated: Boolean(
        linkedPreparation?.vehicleConfirmed
          && linkedPreparation?.assignedDriverName
          && linkedPreparation?.assignedVehicleRegistration,
      ),
      generalAssistantNames: linkedPreparation?.generalAssistantNames || "",
      vehicleRegistration: linkedPreparation?.assignedVehicleRegistration || "",
      dispatchDriverName: linkedPreparation?.assignedDriverName || "",
      returningDriverOrGa: linkedPreparation?.assignedDriverName || "",
    });
    if (warehouseBranch && seededItems.length) {
      void resolveSeededInventoryItems(warehouseBranch, checklistItems);
    }
  };

  const openReportDialog = (isEdit = false, checklistNumber = "") => {
    if (isEdit && selectedReport) {
      setFormState({
        checklistNumber: selectedReport.checklistNumber || "",
        dateOfReport: selectedReport.dateOfReport || dayjs().format("YYYY-MM-DD"),
        responsibleBranch: selectedReport.responsibleBranch || "",
        items: (selectedReport.items || []).map((item: any) => ({
          itemCode: item.itemCode || "",
          description: item.description || "",
          quantity: item.quantity || 1,
          colour: item.colour || "",
        })),
        supervisorName: selectedReport.supervisorName || userName || "",
        supervisorEmployeeNumber: selectedReport.supervisorEmployeeNumber || "",
        supervisorSignature: selectedReport.supervisorSignature || "",
        supervisorDate: selectedReport.supervisorDate || dayjs().format("YYYY-MM-DD"),
        itemsReturnedByName: selectedReport.itemsReturnedByName || "",
        itemsReturnedByEmployeeNumber: selectedReport.itemsReturnedByEmployeeNumber || "",
        itemsReturnedBySignature: selectedReport.itemsReturnedBySignature || "",
        itemsReturnedByDate: selectedReport.itemsReturnedByDate || dayjs().format("YYYY-MM-DD"),
        vehicleRegistrationNumber: selectedReport.vehicleRegistrationNumber || "",
        varianceReportNotes: selectedReport.varianceReportNotes || "",
        notes: selectedReport.notes || "",
        updatedBy: userName || "",
      });
      setDialogMode("EDIT_REPORT");
      return;
    }
    setFormState(emptyReportForm(userName || "", checklistNumber));
    setDialogMode("CREATE_REPORT");
  };

  const refreshCurrentView = async () => {
    if (isPreparationDetails && selectedPreparationNumber) {
      await loadPreparation(selectedPreparationNumber);
      return;
    }
    if (isMaterialDetails && selectedChecklistNumber) {
      await loadChecklist(selectedChecklistNumber);
      return;
    }
    if (isReportDetails && selectedReportNumber) {
      await loadReport(selectedReportNumber);
      return;
    }
    if (section === "PREPARATIONS") await loadPreparations();
    if (section === "MATERIAL") await loadMaterialChecklists();
    if (section === "REPORTS") await loadReports();
    if (section === "STOCK_TAKE") await loadStockTakes();
    if (section === "CLEANING") await loadCleaningRosters();
  };

  const resolveSeededInventoryItems = async (branch: string, items: any[]) => {
    const resolvableItems = items
      .map((item: any, index: number) => ({ index, description: String(item.description || "").trim() }))
      .filter(({ index, description }: { index: number; description: string }) => {
        const item = items[index];
        return description && (
          Boolean(item?.packageSeeded)
          || String(item?.sourceReference || "").startsWith("Package seed")
          || String(item?.sourceType || "").toUpperCase() === "INVENTORY"
        );
      });
    if (!branch || !resolvableItems.length) return;

    const requestId = ++inventoryResolveRequestRef.current;
    setInventoryResolveLoading(true);
    const response = await resolveInventoryMaterials(branch, resolvableItems);
    if (requestId !== inventoryResolveRequestRef.current) return;

    if (response?.status !== 200) {
      setFormState((previous: any) => ({
        ...previous,
        items: (previous.items || []).map((item: any) => {
          const isInventoryItem = Boolean(item.packageSeeded)
            || String(item.sourceReference || "").startsWith("Package seed")
            || String(item.sourceType || "").toUpperCase() === "INVENTORY";
          return isInventoryItem && item.description
            ? { ...item, inventoryResolutionStatus: "ERROR" }
            : item;
        }),
      }));
      setInventoryResolveLoading(false);
      toast.error(response?.data?.message || "Could not match package items with branch inventory");
      return;
    }

    const resolutions = response.data?.resolutions || [];
    const optionsByIndex: Record<number, any[]> = {};
    resolutions.forEach((resolution: any) => {
      optionsByIndex[resolution.index] = resolution.match
        ? [resolution.match]
        : resolution.candidates || [];
    });
    setInventoryLookupOptions((previous) => ({ ...previous, ...optionsByIndex }));
    setFormState((previous: any) => {
      const nextItems = [...(previous.items || [])];
      resolutions.forEach((resolution: any) => {
        const current = nextItems[resolution.index];
        if (!current) return;
        const match = resolution.status === "MATCHED" ? resolution.match : null;
        nextItems[resolution.index] = {
          ...current,
          itemCode: match?.itemCode || "",
          sourceType: "INVENTORY",
          sourceReference: match?.itemCode || "",
          inventoryResolutionStatus: resolution.status,
          inventorySelection: match || null,
        };
      });
      return { ...previous, items: nextItems };
    });
    setInventoryResolveLoading(false);
  };

  const handleInventoryLookupInput = (index: number, value: string) => {
    const query = value.trim();
    const branch = String(formState.warehouseBranch || "").trim();
    inventoryLookupQueryRef.current[index] = query;
    if (inventoryLookupTimersRef.current[index]) {
      window.clearTimeout(inventoryLookupTimersRef.current[index]);
    }

    setFormState((previous: any) => {
      const items = [...(previous.items || [])];
      if (items[index]) {
        items[index] = {
          ...items[index],
          itemCode: value,
          sourceReference: "",
          inventorySelection: null,
          inventoryResolutionStatus: query ? "SEARCHING" : "NOT_FOUND",
        };
      }
      return { ...previous, items };
    });

    if (!branch || query.length < 2) {
      setInventoryLookupOptions((previous) => ({ ...previous, [index]: [] }));
      setInventoryLookupLoading((previous) => ({ ...previous, [index]: false }));
      return;
    }

    const cacheKey = `${branch.toUpperCase()}|${query.toUpperCase()}`;
    const cached = inventoryLookupCacheRef.current.get(cacheKey);
    if (cached) {
      setInventoryLookupOptions((previous) => ({ ...previous, [index]: cached }));
      setInventoryLookupLoading((previous) => ({ ...previous, [index]: false }));
      return;
    }

    setInventoryLookupLoading((previous) => ({ ...previous, [index]: true }));
    inventoryLookupTimersRef.current[index] = window.setTimeout(async () => {
      const response = await lookupInventoryMaterials({ branch, query, size: 25 });
      if (inventoryLookupQueryRef.current[index] !== query || inventoryLookupBranchRef.current !== branch) return;
      const options = response?.status === 200 ? response.data?.materials || [] : [];
      if (response?.status === 200) {
        inventoryLookupCacheRef.current.set(cacheKey, options);
      } else {
        toast.error(response?.data?.message || "Inventory item search failed");
      }
      setInventoryLookupOptions((previous) => ({ ...previous, [index]: options }));
      setInventoryLookupLoading((previous) => ({ ...previous, [index]: false }));
    }, 300);
  };

  const handleInventoryLookupOpen = async (index: number) => {
    const branch = String(formState.warehouseBranch || "").trim();
    const currentQuery = String(formState.items?.[index]?.itemCode || "").trim();

    if (!branch || currentQuery.length >= 2 || inventoryLookupLoading[index]) return;
    if ((inventoryLookupOptions[index] || []).length > 0) return;

    inventoryLookupQueryRef.current[index] = currentQuery;
    const cacheKey = `${branch.toUpperCase()}|__BRANCH_DEFAULT__`;
    const cached = inventoryLookupCacheRef.current.get(cacheKey);
    if (cached) {
      setInventoryLookupOptions((previous) => ({ ...previous, [index]: cached }));
      return;
    }

    setInventoryLookupLoading((previous) => ({ ...previous, [index]: true }));
    const response = await lookupInventoryMaterials({ branch, size: 50 });

    if (
      inventoryLookupBranchRef.current !== branch
      || inventoryLookupQueryRef.current[index] !== currentQuery
    ) return;
    const options = response?.status === 200 ? response.data?.materials || [] : [];
    if (response?.status === 200) {
      inventoryLookupCacheRef.current.set(cacheKey, options);
    } else {
      toast.error(response?.data?.message || "Inventory items could not be loaded for this branch");
    }
    setInventoryLookupOptions((previous) => ({ ...previous, [index]: options }));
    setInventoryLookupLoading((previous) => ({ ...previous, [index]: false }));
  };

  const handleChecklistBranchChange = (branch: string) => {
    inventoryResolveRequestRef.current += 1;
    setInventoryResolveLoading(false);
    inventoryLookupBranchRef.current = branch;
    setInventoryLookupOptions({});
    setInventoryLookupLoading({});
    const nextItems = (formState.items || []).map((item: any) => {
      const shouldRevalidate = Boolean(item.packageSeeded)
        || String(item.sourceReference || "").startsWith("Package seed")
        || String(item.sourceType || "").toUpperCase() === "INVENTORY";
      return shouldRevalidate
        ? {
            ...item,
            itemCode: "",
            sourceType: "INVENTORY",
            sourceReference: "",
            inventorySelection: null,
            inventoryResolutionStatus: branch && item.description ? "RESOLVING" : "BRANCH_REQUIRED",
          }
        : item;
    });
    setFormState((previous: any) => ({ ...previous, warehouseBranch: branch, items: nextItems }));
    if (inventoryLookupTimersRef.current[-1]) {
      window.clearTimeout(inventoryLookupTimersRef.current[-1]);
    }
    if (branch) {
      inventoryLookupTimersRef.current[-1] = window.setTimeout(() => {
        void resolveSeededInventoryItems(branch, nextItems);
      }, 350);
    }
  };

  const handleChecklistInventoryItemChange = (index: number, selectedValue: any) => {
    setFormState((previous: any) => {
      const items = [...(previous.items || [])];
      const current = items[index];
      if (!current) return previous;
      items[index] = selectedValue
        ? {
            ...current,
            itemCode: selectedValue.itemCode || "",
            description: selectedValue.description || current.description || "",
            sourceType: "INVENTORY",
            sourceReference: selectedValue.itemCode || "",
            inventoryResolutionStatus: "MATCHED",
            inventorySelection: selectedValue,
          }
        : {
            ...current,
            itemCode: "",
            sourceReference: "",
            inventoryResolutionStatus: "NOT_FOUND",
            inventorySelection: null,
          };
      return { ...previous, items };
    });
  };

  const handleChecklistSourceTypeChange = (index: number, sourceType: string) => {
    setFormState((previous: any) => {
      const items = [...(previous.items || [])];
      const current = items[index];
      if (!current) return previous;
      const inventorySource = sourceType === "INVENTORY";
      items[index] = {
        ...current,
        sourceType,
        itemCode: inventorySource ? current.itemCode || "" : "",
        sourceReference: inventorySource ? current.sourceReference || "" : "",
        inventorySelection: inventorySource ? current.inventorySelection || null : null,
        inventoryResolutionStatus: inventorySource
          ? current.itemCode ? "MATCHED" : "NOT_FOUND"
          : undefined,
      };
      return { ...previous, items };
    });
  };

  const buildMaterialChecklistPayload = (state: any) => ({
    ...state,
    items: (state.items || []).map((item: any) => {
      const requestItem = { ...item };
      delete requestItem.inventoryResolutionStatus;
      delete requestItem.inventorySelection;
      delete requestItem.packageSeeded;
      return requestItem;
    }),
  });

  const submitDialog = async () => {
    const canSubmitCurrentDialog =
      (dialogMode === "CREATE_PREPARATION" || dialogMode === "EDIT_PREPARATION")
        ? canWritePreparation
        : (dialogMode === "CREATE_MATERIAL" || dialogMode === "EDIT_MATERIAL")
          ? checklistActionIntent === "REQUEST_APPROVAL"
            ? canWriteChecklist
            : checklistActionIntent
              ? canApproveChecklist
              : canWriteChecklist
          : (dialogMode === "CREATE_INVENTORY" || dialogMode === "EDIT_INVENTORY")
            ? canManageInventory
          : (dialogMode === "CREATE_REPORT" || dialogMode === "EDIT_REPORT")
            ? canWriteReport
            : (dialogMode === "CREATE_STOCK_TAKE" || dialogMode === "EDIT_STOCK_TAKE" || dialogMode === "CREATE_CLEANING_ROSTER")
              ? canWritePreparation
            : false;

    if (!canSubmitCurrentDialog) {
      toast.error("Your role cannot update this warehouse record.");
      return;
    }

    if (["CREATE_MATERIAL", "EDIT_MATERIAL"].includes(dialogMode || "")) {
      const unresolvedInventoryItem = (formState.items || []).find(
        (item: any) => String(item.sourceType || "").toUpperCase() === "INVENTORY"
          && !String(item.itemCode || item.sourceReference || "").trim(),
      );
      if (unresolvedInventoryItem) {
        toast.error(`Select an inventory item code for ${unresolvedInventoryItem.description || "every inventory row"}`);
        return;
      }
    }

    setSaving(true);
    let response: any;
    if (dialogMode === "CREATE_PREPARATION") {
      response = await createWarehousePreparation(buildPreparationPayload(formState));
      if (response?.status === 200) {
        setSelectedPreparationNumber(response.data?.referenceNumber || "");
        setSection("PREPARATIONS");
      }
    } else if (dialogMode === "EDIT_PREPARATION") {
      response = await updateWarehousePreparation(selectedPreparationNumber, buildPreparationPayload(formState));
    } else if (dialogMode === "CREATE_MATERIAL") {
      response = await createWarehouseMaterialChecklist(buildMaterialChecklistPayload(formState));
      if (response?.status === 200) {
        setSelectedChecklistNumber(response.data?.referenceNumber || "");
        setSection("MATERIAL");
      }
    } else if (dialogMode === "EDIT_MATERIAL") {
      response = await updateWarehouseMaterialChecklist(selectedChecklistNumber, {
        ...buildMaterialChecklistPayload(formState),
        preserveDispatchApproval: checklistActionIntent === "DISPATCH",
      });
    } else if (dialogMode === "CREATE_INVENTORY") {
      response = await createInventoryMaterial({ ...formState, performedBy: userName || "" });
      if (response?.status === 200) {
        setSection("INVENTORY");
      }
    } else if (dialogMode === "EDIT_INVENTORY") {
      response = await updateInventoryMaterial(
        selectedInventoryMaterial?.itemCode || formState.itemCode,
        selectedInventoryMaterial?.warehouseBranch || formState.warehouseBranch,
        { ...formState, performedBy: userName || "" },
      );
      if (response?.status === 200) {
        setSection("INVENTORY");
      }
    } else if (dialogMode === "CREATE_REPORT") {
      response = await createWarehouseBrokenLossReport(formState);
      if (response?.status === 200) {
        setSelectedReportNumber(response.data?.referenceNumber || "");
        setSection("REPORTS");
      }
    } else if (dialogMode === "EDIT_REPORT") {
      response = await updateWarehouseBrokenLossReport(selectedReportNumber, formState);
    } else if (dialogMode === "CREATE_STOCK_TAKE") {
      response = await createWarehouseStockTake({ ...formState, performedBy: userName || "" });
      if (response?.status === 200) {
        setSection("STOCK_TAKE");
      }
    } else if (dialogMode === "EDIT_STOCK_TAKE") {
      response = await updateWarehouseStockTakeCounts(selectedStockTake?.stockTakeNumber, {
        items: formState.items || [],
        varianceNotes: formState.varianceNotes || "",
        counterName: formState.counterName || "",
        counterSignature: formState.counterSignature || "",
        supervisorName: formState.supervisorName || "",
        supervisorSignature: formState.supervisorSignature || "",
        performedBy: userName || "",
      });
      if (response?.status === 200) {
        setSection("STOCK_TAKE");
      }
    } else if (dialogMode === "CREATE_CLEANING_ROSTER") {
      response = await createWarehouseCleaningRoster({ ...formState, performedBy: userName || "" });
      if (response?.status === 200) {
        setSection("CLEANING");
      }
    }

    if (response?.status === 200) {
      if (dialogMode === "EDIT_MATERIAL" && checklistActionIntent === "REQUEST_APPROVAL") {
        const actionResponse = await requestWarehouseDispatchApproval(selectedChecklistNumber, {
          performedBy: getWarehouseActorName(),
        });
        if (actionResponse?.status === 200) {
          toast.success(actionResponse.data?.message || "Dispatch approval requested");
          resetDialog();
          await refreshCurrentView();
        } else {
          toast.error(actionResponse?.data?.message || "Could not request dispatch approval");
        }
        setSaving(false);
        return;
      }

      if (dialogMode === "EDIT_MATERIAL" && checklistActionIntent === "DISPATCH") {
        const actionResponse = await confirmWarehouseMaterialDispatch(selectedChecklistNumber, {
          performedBy: getWarehouseActorName(),
        });
        if (actionResponse?.status === 200) {
          toast.success(actionResponse.data?.message || "Dispatch confirmed");
          resetDialog();
          await refreshCurrentView();
        } else {
          toast.error(actionResponse?.data?.message || "Dispatch confirmation failed");
        }
        setSaving(false);
        return;
      }

      if (dialogMode === "EDIT_MATERIAL" && checklistActionIntent === "RETURN") {
        const actionResponse = await confirmWarehouseMaterialReturn(selectedChecklistNumber, {
          performedBy: getWarehouseActorName(),
        });
        if (actionResponse?.status === 200) {
          toast.success(actionResponse.data?.message || "Return confirmed");
          resetDialog();
          await refreshCurrentView();
        } else {
          toast.error(actionResponse?.data?.message || "Return confirmation failed");
        }
        setSaving(false);
        return;
      }

      if (dialogMode === "EDIT_MATERIAL") {
        setSelectedChecklist((prev: any) =>
          prev
            ? {
                ...prev,
                ...buildChecklistDetailPreviewFromFormState(formState),
              }
            : prev,
        );
      }
      if (dialogMode === "CREATE_INVENTORY" || dialogMode === "EDIT_INVENTORY") {
        setSelectedInventoryMaterial(null);
      }
      if (dialogMode === "EDIT_STOCK_TAKE") setSelectedStockTake(null);
      toast.success(response.data?.message || "Saved successfully");
      resetDialog();
      await refreshCurrentView();
    } else {
      toast.error(response?.data?.message || "Save failed");
    }
    setSaving(false);
  };

  const openStockTakeCount = (stockTake: any) => {
    setSelectedStockTake(stockTake);
    setFormState({
      ...stockTake,
      items: (stockTake.items || []).map((item: any) => ({ ...item })),
      varianceNotes: stockTake.varianceNotes || "",
    });
    setDialogMode("EDIT_STOCK_TAKE");
  };

  const submitStockTakeForReview = async (stockTake: any) => {
    setSaving(true);
    const response: any = await submitWarehouseStockTake(stockTake.stockTakeNumber, {
      items: stockTake.items || [],
      varianceNotes: stockTake.varianceNotes || "",
      counterName: stockTake.counterName || "",
      counterSignature: stockTake.counterSignature || "",
      supervisorName: stockTake.supervisorName || "",
      supervisorSignature: stockTake.supervisorSignature || "",
      performedBy: userName || "",
    });
    setSaving(false);
    if (response?.status === 200) {
      toast.success("Stock take submitted for inventory review");
      await loadStockTakes();
    } else {
      toast.error(response?.data?.message || "Unable to submit stock take");
    }
  };

  const startCleaningRoster = async (roster: any) => {
    setSaving(true);
    const response: any = await startWarehouseCleaningRoster(roster.rosterNumber, { performedBy: userName || roster.assignedTo || "" });
    setSaving(false);
    if (response?.status === 200) {
      toast.success("Cleaning roster started");
      await loadCleaningRosters();
    } else {
      toast.error(response?.data?.message || "Unable to start cleaning roster");
    }
  };

  const openCleaningCompletion = (roster: any) => {
    setCleaningCompletionRecord(roster);
    setCleaningCompletionForm({
      ppeConfirmed: false,
      equipmentCleaned: false,
      areaSanitised: false,
      equipmentInspected: false,
      storageConfirmed: false,
      issuesFound: roster.issuesFound || "",
      cleaningChecklist: roster.cleaningChecklist || "",
      notes: roster.notes || "",
      supervisorVerifiedBy: "",
      supervisorSignature: "",
    });
  };

  const finishCleaningRoster = async () => {
    if (!cleaningCompletionRecord) return;
    setSaving(true);
    const response: any = await completeWarehouseCleaningRoster(cleaningCompletionRecord.rosterNumber, {
      ...cleaningCompletionForm,
      performedBy: userName || cleaningCompletionRecord.startedBy || cleaningCompletionRecord.assignedTo || "",
    });
    setSaving(false);
    if (response?.status === 200) {
      toast.success("Cleaning and supervisor verification completed");
      setCleaningCompletionRecord(null);
      setCleaningCompletionForm({});
      await loadCleaningRosters();
    } else {
      toast.error(response?.data?.message || "Unable to complete cleaning roster");
    }
  };

  const decideStockTakeReview = async (approved: boolean) => {
    if (!stockTakeReviewRecord) return;
    setSaving(true);
    const response: any = await reviewWarehouseStockTake(stockTakeReviewRecord.stockTakeNumber, {
      approved,
      notes: stockTakeReviewNotes,
      performedBy: userName || "",
    });
    setSaving(false);
    if (response?.status === 200) {
      toast.success(approved ? "Stock variances approved and applied" : "Stock take returned for recount");
      setStockTakeReviewRecord(null);
      setStockTakeReviewNotes("");
      await Promise.all([loadStockTakes(), loadInventory()]);
    } else {
      toast.error(response?.data?.message || "Unable to review stock take");
    }
  };

  const runPreparationAction = async () => {
    if (!selectedPreparationNumber) return;
    if (!canApprovePreparation) {
      toast.error("Your role cannot submit warehouse preparations.");
      return;
    }
    setSaving(true);
    const response = await submitWarehousePreparation(selectedPreparationNumber, {
      performedBy: userName || "",
    });
    if (response?.status === 200) {
      toast.success(response.data?.message || "Preparation submitted");
      await refreshCurrentView();
    } else {
      toast.error(response?.data?.message || "Preparation action failed");
    }
    setSaving(false);
  };

  const runMarkReadyAction = async () => {
    if (!selectedPreparationNumber) return;
    if (!canApprovePreparation) {
      toast.error("Your role cannot mark warehouse preparations ready.");
      return;
    }
    setSaving(true);
    const response = await markWarehousePreparationReady(selectedPreparationNumber, {
      performedBy: userName || "",
    });
    if (response?.status === 200) {
      toast.success(response.data?.message || "Preparation marked ready");
      await refreshCurrentView();
    } else {
      toast.error(response?.data?.message || "Could not mark preparation ready");
    }
    setSaving(false);
  };

  const runDispatchAction = async () => {
    if (!selectedChecklistNumber) return;
    if (!canApproveChecklist) {
      toast.error("Your role cannot confirm warehouse dispatch.");
      return;
    }
    openMaterialDialog(true, "", "DISPATCH");
  };

  const runRequestDispatchApprovalAction = async () => {
    if (!selectedChecklistNumber) return;
    if (!canWriteChecklist) {
      toast.error("Your role cannot request dispatch approval.");
      return;
    }
    openMaterialDialog(true, "", "REQUEST_APPROVAL");
  };

  const runDecideDispatchApprovalAction = async (approved: boolean) => {
    if (!selectedChecklistNumber) return;
    if (!canApproveChecklist) {
      toast.error("Your role cannot decide dispatch approvals.");
      return;
    }
    setSaving(true);
    const response = await decideWarehouseDispatchApproval(selectedChecklistNumber, {
      performedBy: getWarehouseActorName(),
      approved,
    });
    if (response?.status === 200) {
      toast.success(
        response.data?.message ||
          (approved ? "Dispatch approval granted" : "Dispatch approval rejected"),
      );
      await refreshCurrentView();
    } else {
      toast.error(response?.data?.message || "Could not update dispatch approval");
    }
    setSaving(false);
  };

  const runReturnAction = async () => {
    if (!selectedChecklistNumber) return;
    if (!canApproveChecklist) {
      toast.error("Your role cannot confirm warehouse return.");
      return;
    }
    openMaterialDialog(true, "", "RETURN");
  };

  const runReconcileOutstandingAction = async () => {
    if (!selectedChecklistNumber) return;
    if (!canApproveChecklist) {
      toast.error("Your role cannot reconcile outstanding materials.");
      return;
    }
    setSaving(true);
    const response = await reconcileWarehouseOutstandingMaterials(selectedChecklistNumber, {
      performedBy: getWarehouseActorName(),
    });
    if (response?.status === 200) {
      toast.success(response.data?.message || "Outstanding materials reconciled");
      await refreshCurrentView();
    } else {
      toast.error(response?.data?.message || "Could not reconcile outstanding materials");
    }
    setSaving(false);
  };

  const runTriggerLossReport = async () => {
    if (!selectedChecklistNumber) return;
    if (!canWriteReport) {
      toast.error("Your role cannot create a broken / loss report.");
      return;
    }
    setSaving(true);
    const response = await triggerWarehouseLossReport(selectedChecklistNumber, {
      performedBy: getWarehouseActorName(),
    });
    if (response?.status === 200) {
      toast.success(response.data?.message || "Loss report created");
      if (hasPendingOutstandingItems(selectedChecklist?.items)) {
        await refreshCurrentView();
      } else {
        setSection("REPORTS");
        setSelectedReportNumber(response.data?.referenceNumber || "");
        setSelectedChecklistNumber("");
      }
    } else {
      toast.error(response?.data?.message || "Could not create loss report");
    }
    setSaving(false);
  };

  const runSubmitReport = async () => {
    if (!selectedReportNumber) return;
    if (!canSubmitReport) {
      toast.error("Your role cannot submit reports to inventory.");
      return;
    }
    setSaving(true);
    const response = await submitWarehouseBrokenLossReport(selectedReportNumber, {
      performedBy: userName || "",
    });
    if (response?.status === 200) {
      toast.success(response.data?.message || "Report submitted");
      await refreshCurrentView();
    } else {
      toast.error(response?.data?.message || "Report submission failed");
    }
    setSaving(false);
  };

  const preparationPrimaryAction = useMemo(() => {
    if (!canApprovePreparation) {
      return null;
    }

    if (selectedPreparation?.status === "PENDING") {
      return {
        label: "Submit To Warehouse",
        onClick: runPreparationAction,
      };
    }

    if (selectedPreparation?.status === "IN_PREPARATION") {
      return {
        label: "Mark Ready",
        onClick: runMarkReadyAction,
      };
    }

    if (
      selectedPreparation?.status === "READY" &&
      !selectedPreparation?.materialChecklistCreated &&
      hasApplicableWarehousePackage(selectedPreparation?.funeralCategory) &&
      canWriteChecklist
    ) {
      return {
        label: "New Material Checklist",
        onClick: () =>
          openMaterialDialog(false, selectedPreparation?.preparationNumber || ""),
      };
    }

    return null;
  }, [
    openMaterialDialog,
    runMarkReadyAction,
    runPreparationAction,
    canApprovePreparation,
    canWriteChecklist,
    selectedPreparation?.funeralCategory,
    selectedPreparation?.materialChecklistCreated,
    selectedPreparation?.preparationNumber,
    selectedPreparation?.status,
  ]);

  const checklistPrimaryAction = useMemo(() => {
    const approvalStatus = String(
      selectedChecklist?.dispatchApprovalStatus || "",
    ).toUpperCase();
    const approvalPending = approvalStatus === "PENDING";
    const approvalApproved = approvalStatus === "APPROVED";

    if (!canApproveChecklist && !canWriteChecklist) {
      return null;
    }

    const effectiveChecklistStatus = resolveChecklistUiStatus(selectedChecklist);
    const pendingOutstandingCount = countPendingOutstandingItems(selectedChecklist?.items);

    if (effectiveChecklistStatus === "DRAFT") {
      if (!approvalApproved) {
        if (approvalPending && canApproveChecklist) {
          return {
            label: "Approve Dispatch",
            onClick: () => runDecideDispatchApprovalAction(true),
            variant: "contained" as const,
          };
        }

        if (canWriteChecklist) {
          return {
            label:
              approvalStatus === "REJECTED"
                ? "Request Dispatch Approval Again"
                : "Request Dispatch Approval",
            onClick: runRequestDispatchApprovalAction,
            variant: "contained" as const,
          };
        }

        return null;
      }

      if (!canApproveChecklist) {
        return null;
      }

      return {
        label: "Confirm Dispatch",
        onClick: runDispatchAction,
        variant: "contained" as const,
      };
    }

    if (effectiveChecklistStatus === "DISPATCHED") {
      return {
        label: "Confirm Return",
        onClick: runReturnAction,
        variant: "contained" as const,
      };
    }

    if (effectiveChecklistStatus === "OUTSTANDING_MATERIALS") {
      return {
        label: "Reconcile Outstanding",
        onClick: runReconcileOutstandingAction,
        variant: "contained" as const,
        disabled: pendingOutstandingCount > 0,
      };
    }

    if (effectiveChecklistStatus === "PARTIALLY_RETURNED") {
      return {
        label: "Report Loss",
        onClick: runTriggerLossReport,
        variant: "contained" as const,
      };
    }

    return null;
  }, [
    canApproveChecklist,
    canWriteChecklist,
    runDecideDispatchApprovalAction,
    runDispatchAction,
    runRequestDispatchApprovalAction,
    runReconcileOutstandingAction,
    runReturnAction,
    runTriggerLossReport,
    selectedChecklist,
  ]);

  const checklistSecondaryAction = useMemo(() => {
    const approvalStatus = String(
      selectedChecklist?.dispatchApprovalStatus || "",
    ).toUpperCase();
    const approvalPending = approvalStatus === "PENDING";
    if (!canWriteReport) {
      if (
        resolveChecklistUiStatus(selectedChecklist) === "DRAFT" &&
        approvalPending &&
        canApproveChecklist
      ) {
        return {
          label: "Reject Dispatch",
          onClick: () => runDecideDispatchApprovalAction(false),
          variant: "outlined" as const,
        };
      }
      return null;
    }

    const effectiveChecklistStatus = resolveChecklistUiStatus(selectedChecklist);
    const reportAlreadyCreated = Boolean(selectedChecklist?.brokenLossReportCreated);
    const canTriggerLoss = hasLossFollowupItems(selectedChecklist?.items) && !reportAlreadyCreated;

    if (effectiveChecklistStatus === "DRAFT" && approvalPending && canApproveChecklist) {
      return {
        label: "Reject Dispatch",
        onClick: () => runDecideDispatchApprovalAction(false),
        variant: "outlined" as const,
      };
    }

    if (effectiveChecklistStatus === "OUTSTANDING_MATERIALS" && canTriggerLoss) {
      return {
        label: "Report Loss",
        onClick: runTriggerLossReport,
        variant: "outlined" as const,
      };
    }

    return null;
  }, [
    canApproveChecklist,
    canWriteReport,
    runDecideDispatchApprovalAction,
    runTriggerLossReport,
    selectedChecklist,
  ]);

  const renderPreparationDetails = () => {
    if (detailLoading) return <Loader />;
    if (!selectedPreparation) return <OperationsEmptyState title="No preparation selected" description="Choose a warehouse preparation from the list." />;
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <WarehouseHeroCard
          title={selectedPreparation.preparationNumber || "Warehouse Preparation"}
          subtitle={`${selectedPreparation.caseNumber || "No case"} | ${selectedPreparation.deceasedFullName || "No deceased linked"}`}
          status={selectedPreparation.status}
          meta={
            <>
              <Chip label={`Funeral ${formatDate(selectedPreparation.funeralDate)}`} size="small" sx={{ bgcolor: ifasColors.softOrange, color: ifasColors.orange, fontWeight: 900 }} />
              <Chip label={`Branch ${selectedPreparation.warehouseBranch || selectedPreparation.branchName || "Not captured"}`} size="small" sx={{ bgcolor: ifasColors.page, color: ifasColors.muted, fontWeight: 900 }} />
              <Chip label={`${selectedPreparation.transportVehicleCount || 1} cargo vehicle(s) required`} size="small" sx={{ bgcolor: STANDARD_TONES.blue.bg, color: STANDARD_TONES.blue.color, fontWeight: 900 }} />
              <Chip label={selectedPreparation.bodyReadyForFuneral ? "Body Ready" : "Body Not Ready"} size="small" sx={{ bgcolor: selectedPreparation.bodyReadyForFuneral ? STANDARD_TONES.green.bg : STANDARD_TONES.amber.bg, color: selectedPreparation.bodyReadyForFuneral ? STANDARD_TONES.green.color : STANDARD_TONES.amber.color, fontWeight: 900 }} />
            </>
          }
        />

        <Paper elevation={0} sx={operationsDetailPaperSx}>
          <OperationsSectionHeader title="Warehouse Preparation Overview" meta="Operational record linked from case and funeral work" />
          <OperationsDetailGrid>
            <OperationsDetailField label="Preparation Number" value={selectedPreparation.preparationNumber} />
            <OperationsDetailField label="Status" value={formatOperationsStatus(selectedPreparation.status)} />
            <OperationsDetailField label="Case Number" value={selectedPreparation.caseNumber} />
            <OperationsDetailField label="Funeral File Number" value={selectedPreparation.funeralFileNumber} />
            <OperationsDetailField label="Body Removal Number" value={selectedPreparation.bodyRemovalNumber} />
            <OperationsDetailField label="Deceased" value={selectedPreparation.deceasedFullName} />
            <OperationsDetailField label="Family Representative" value={selectedPreparation.familyRepresentativeName} />
            <OperationsDetailField label="Family Contact" value={selectedPreparation.familyContactNumber} />
            <OperationsDetailField label="Branch" value={selectedPreparation.branchName} />
            <OperationsDetailField label="Funeral Date" value={formatDate(selectedPreparation.funeralDate)} />
            <OperationsDetailField label="Funeral Surname / Area" value={selectedPreparation.funeralSurnameArea} />
            <OperationsDetailField label="Funeral Category" value={selectedPreparation.funeralCategory} />
            <OperationsDetailField label="Warehouse Branch" value={selectedPreparation.warehouseBranch} />
            <OperationsDetailField label="Service Venue" value={selectedPreparation.serviceVenue} />
            <OperationsDetailField label="Cemetery Details" value={selectedPreparation.cemeteryDetails} />
            <OperationsDetailField label="Casket Type" value={selectedPreparation.casketType} />
            <OperationsDetailField label="Material Transport Required" value={formatBool(selectedPreparation.transportRequired)} />
            <OperationsDetailField label="Cargo Vehicles Required" value={selectedPreparation.transportRequired === false ? "Not required" : selectedPreparation.transportVehicleCount || 1} />
            <OperationsDetailField label="Transport Instructions" value={selectedPreparation.transportRequirements} />
            <OperationsDetailField label="Transport Request" value={selectedPreparation.transportRequestNumber} />
            <OperationsDetailField label="Transport Requested At" value={formatDateTime(selectedPreparation.transportRequestedAt)} />
            <OperationsDetailField label="GAs Allocated" value={selectedPreparation.generalAssistantNames} />
            <OperationsDetailField label="Vehicle Confirmed" value={formatBool(selectedPreparation.vehicleConfirmed)} />
            <OperationsDetailField label="Fleet Coordinator Confirmation" value={selectedPreparation.fleetCoordinatorConfirmation} />
            <OperationsDetailField label="Fleet Confirmed At" value={formatDateTime(selectedPreparation.fleetCoordinatorConfirmedAt)} />
            <OperationsDetailField label="Assigned Driver" value={selectedPreparation.assignedDriverName} />
            <OperationsDetailField label="Assigned Vehicle" value={selectedPreparation.assignedVehicleRegistration} />
            <OperationsDetailField label="Assigned Vehicle Type" value={selectedPreparation.assignedVehicleType} />
            <OperationsDetailField label="Current Mortuary" value={selectedPreparation.currentMortuaryName} />
            <OperationsDetailField label="Body Ready For Funeral" value={formatBool(selectedPreparation.bodyReadyForFuneral)} />
            <OperationsDetailField label="Material Checklist Created" value={formatBool(selectedPreparation.materialChecklistCreated)} />
            <OperationsDetailField label="Planner Submitted By" value={selectedPreparation.plannerSubmittedToWarehouseBy} />
            <OperationsDetailField label="Planner Submitted At" value={formatDateTime(selectedPreparation.plannerSubmittedToWarehouseAt)} />
          </OperationsDetailGrid>
        </Paper>

        <Paper elevation={0} sx={operationsDetailPaperSx}>
          <OperationsSectionHeader title="Funeral Preparation Requirements" meta="Service-day materials and special setup context" />
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", xl: "minmax(0, 0.88fr) minmax(0, 1.12fr)" }}
            gap={1.5}
            mt={1.5}
          >
            <Stack spacing={1.5}>
              <PreparationNoteCard
                label="Decor Requirements"
                value={selectedPreparation.decorRequirements}
                helper="Use this area for color, draping, and ceremony styling notes."
              />
              <PreparationNoteCard
                label="Photo / Frame Requirements"
                value={selectedPreparation.photoAndFrameRequirements}
                helper="Capture printed-photo, frame, and memorial-display needs here."
              />
              <PreparationNoteCard
                label="Special Instructions"
                value={selectedPreparation.specialInstructions}
                helper="Operational notes that the warehouse or setup team must not miss."
              />
            </Stack>

            <Stack spacing={1.5}>
              <PreparationPackageCard
                category={selectedPreparation.funeralCategory}
                details={selectedPreparation.funeralPackageDetails}
              />
              <MaterialRequirementBreakdown
                value={selectedPreparation.materialsAndEquipmentRequired}
              />
            </Stack>
          </Box>
        </Paper>

        <Paper elevation={0} sx={operationsDetailPaperSx}>
          <OperationsSectionHeader title="Operational Notes" meta="Warehouse readiness and local control notes" />
          <OperationsDetailGrid>
            <OperationsDetailField label="Equipment Ready Checklist" value={selectedPreparation.equipmentReadyChecklist} />
            <OperationsDetailField label="Cleaning Roster Tasks" value={selectedPreparation.cleaningRosterTasks} />
            <OperationsDetailField label="Stock Count Data" value={selectedPreparation.stockCountData} />
            <OperationsDetailField label="Equipment Status" value={selectedPreparation.equipmentStatus} />
            <OperationsDetailField label="Notes" value={selectedPreparation.notes} />
          </OperationsDetailGrid>
        </Paper>
      </Box>
    );
  };

  const renderChecklistDetails = () => {
    if (detailLoading) return <Loader />;
    if (!selectedChecklist) return <OperationsEmptyState title="No checklist selected" description="Choose a material checklist from the list." />;
    const effectiveChecklistStatus = resolveChecklistUiStatus(selectedChecklist);
    const normalizedChecklistItems = normalizeChecklistItems(selectedChecklist.items || []);
    const dispatchedChecklistItems = normalizedChecklistItems.filter((item: any) => Boolean(item.dispatched));
    const checklistItemsForDisplay = dispatchedChecklistItems.length
      ? dispatchedChecklistItems
      : normalizedChecklistItems;
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <WarehouseHeroCard
          title={selectedChecklist.checklistNumber || "Material Checklist"}
          subtitle={`${selectedChecklist.preparationNumber || "No preparation"} | ${selectedChecklist.caseNumber || "No case linked"}`}
          status={effectiveChecklistStatus}
          meta={
            <>
              <Chip label={`Driver ${selectedChecklist.driverName || "Not assigned"}`} size="small" sx={{ bgcolor: STANDARD_TONES.blue.bg, color: STANDARD_TONES.blue.color, fontWeight: 900 }} />
              <Chip label={`Vehicle ${selectedChecklist.vehicleRegistration || "Not captured"}`} size="small" sx={{ bgcolor: ifasColors.page, color: ifasColors.muted, fontWeight: 900 }} />
              <Chip label={`Items ${checklistItemsForDisplay.length}`} size="small" sx={{ bgcolor: ifasColors.softOrange, color: ifasColors.orange, fontWeight: 900 }} />
              {selectedChecklist.hearseTrailerRegistration && (
                <Chip label={`Trailer ${selectedChecklist.hearseTrailerRegistration}`} size="small" sx={{ bgcolor: STANDARD_TONES.red.bg, color: STANDARD_TONES.amber.color, fontWeight: 900 }} />
              )}
              {selectedChecklist.brokenLossReportCreated && (
                <Chip label={`Loss Report ${selectedChecklist.brokenLossReportNumber || "Created"}`} size="small" sx={{ bgcolor: STANDARD_TONES.red.bg, color: STANDARD_TONES.red.color, fontWeight: 900 }} />
              )}
            </>
          }
        />

        <ChecklistStageBanner status={effectiveChecklistStatus} />

        <Paper elevation={0} sx={operationsDetailPaperSx}>
          <OperationsSectionHeader title="Material Checklist" meta="Dispatch and recollection context" />
          <OperationsDetailGrid>
            <OperationsDetailField label="Checklist Number" value={selectedChecklist.checklistNumber} />
            <OperationsDetailField label="Status" value={formatOperationsStatus(effectiveChecklistStatus)} />
            <OperationsDetailField label="Preparation Number" value={selectedChecklist.preparationNumber} />
            <OperationsDetailField label="Case Number" value={selectedChecklist.caseNumber} />
            <OperationsDetailField label="Funeral Date" value={formatDate(selectedChecklist.funeralDate)} />
            <OperationsDetailField label="Funeral Surname / Area" value={selectedChecklist.funeralSurnameArea} />
            <OperationsDetailField label="Driver" value={selectedChecklist.driverName} />
            <OperationsDetailField label="GAs" value={selectedChecklist.generalAssistantNames} />
            <OperationsDetailField label="Hearse Trailer" value={selectedChecklist.hearseTrailerRegistration} />
            <OperationsDetailField label="Transport Equipment" value={selectedChecklist.transportEquipment} />
            <OperationsDetailField label="Vehicle Registration" value={selectedChecklist.vehicleRegistration} />
            <OperationsDetailField label="Collector" value={selectedChecklist.collectorName} />
            <OperationsDetailField label="Return Date / Time" value={formatDateTime(selectedChecklist.returnDateTime)} />
            <OperationsDetailField label="Returning Driver / GA" value={selectedChecklist.returningDriverOrGa} />
            <OperationsDetailField label="Loss Report" value={selectedChecklist.brokenLossReportNumber || "Not created"} />
          </OperationsDetailGrid>
        </Paper>

        <Paper elevation={0} sx={operationsDetailPaperSx}>
          <OperationsSectionHeader title="Dispatch Approval" meta="Supervisor confirmation captured before material leaves the warehouse" />
          <OperationsDetailGrid>
            <OperationsDetailField label="Supervisor" value={selectedChecklist.dispatchSupervisorName} />
            <OperationsDetailField label="Supervisor Designation" value={selectedChecklist.dispatchSupervisorDesignation} />
            <OperationsDetailField label="Supervisor Employee No." value={selectedChecklist.dispatchSupervisorEmployeeNumber} />
            <OperationsDetailField label="Supervisor Signed" value={selectedChecklist.dispatchSupervisorSignature} />
            <OperationsDetailField label="Supervisor Date" value={formatDate(selectedChecklist.dispatchSupervisorDate)} />
            <OperationsDetailField label="Approval Status" value={selectedChecklist.dispatchApprovalStatus || "Not requested"} />
            <OperationsDetailField label="Requested By" value={selectedChecklist.dispatchApprovalRequestedBy} />
            <OperationsDetailField label="Requested At" value={formatDateTime(selectedChecklist.dispatchApprovalRequestedAt)} />
            <OperationsDetailField label="SLA Due At" value={formatDateTime(selectedChecklist.dispatchApprovalSlaDueAt)} />
            <OperationsDetailField label="Decided By" value={selectedChecklist.dispatchApprovedBy} />
            <OperationsDetailField label="Decided At" value={formatDateTime(selectedChecklist.dispatchApprovedAt)} />
            <OperationsDetailField label="Approval Notes" value={selectedChecklist.dispatchApprovalNotes} span={2} />
          </OperationsDetailGrid>
        </Paper>

        <Paper elevation={0} sx={operationsDetailPaperSx}>
          <OperationsSectionHeader title="Return Verification" meta="Supervisor review after service-day return and later recollection" />
          <OperationsDetailGrid>
            <OperationsDetailField label="Collector Name" value={selectedChecklist.collectorName} />
            <OperationsDetailField label="Collector Designation" value={selectedChecklist.collectorDesignation} />
            <OperationsDetailField label="Collector Employee No." value={selectedChecklist.collectorEmployeeNumber} />
            <OperationsDetailField label="Collector Signed" value={selectedChecklist.collectorSignature} />
            <OperationsDetailField label="Collector Date" value={formatDate(selectedChecklist.collectorDate)} />
            <OperationsDetailField label="Return Supervisor" value={selectedChecklist.returnSupervisorName} />
            <OperationsDetailField label="Return Supervisor Signed" value={selectedChecklist.returnSupervisorSignature} />
            <OperationsDetailField label="Discrepancy Notes" value={selectedChecklist.discrepancyNotes} span={2} />
          </OperationsDetailGrid>
        </Paper>

        <Paper elevation={0} sx={operationsDetailPaperSx}>
          <OperationsSectionHeader
            title="Material Items"
            meta={
              dispatchedChecklistItems.length
                ? "Showing dispatched lines only so return and outstanding quantities stay readable."
                : "Track setup, source, dispatch, return, and outstanding follow-up. Scroll horizontally to see the full return breakdown."
            }
          />
          <Box sx={materialDetailTableScrollSx}>
            <Table size="small" sx={materialDetailTableSx}>
              <TableHead sx={operationsTableHeadSx}>
                <TableRow>
                  <TableCell sx={materialStickyDescriptionCellSx}>Description</TableCell>
                  <TableCell sx={{ width: 110 }}>Dispatch Qty</TableCell>
                  <TableCell sx={{ width: 110 }}>Colour</TableCell>
                  <TableCell sx={{ width: 100 }}>Setup</TableCell>
                  <TableCell sx={{ width: 220 }}>Source</TableCell>
                  <TableCell sx={{ width: 110 }}>Dispatched</TableCell>
                  <TableCell sx={{ width: 120 }}>Returned Qty</TableCell>
                  <TableCell sx={{ width: 140 }}>Loss/Broken Qty</TableCell>
                  <TableCell sx={{ width: 140 }}>Outstanding Qty</TableCell>
                  <TableCell sx={{ width: 130 }}>Due Date</TableCell>
                  <TableCell sx={{ width: 120 }}>Collected Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {checklistItemsForDisplay.map((item: any, index: number) => (
                  <TableRow key={`${item.description}-${index}`} sx={operationsRowSx}>
                    <TableCell sx={materialStickyDescriptionCellSx}>
                      <Typography fontWeight={800} color={ifasColors.text} textAlign="left">
                        {item.description || "Not captured"}
                      </Typography>
                    </TableCell>
                    <TableCell>{item.quantity ?? "Not captured"}</TableCell>
                    <TableCell>{item.colour || "Not captured"}</TableCell>
                    <TableCell>{item.setupLocationType || "Not captured"}</TableCell>
                    <TableCell>
                      <Typography fontSize={13} color={ifasColors.text} textAlign="left" sx={{ wordBreak: "break-word" }}>
                        {item.sourceType || "Not captured"}
                      </Typography>
                      {item.sourceReference && (
                        <Typography fontSize={12.5} color={ifasColors.muted} mt={0.35} textAlign="left" sx={{ wordBreak: "break-word" }}>
                          {item.sourceReference}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{formatBool(item.dispatched)}</TableCell>
                    <TableCell>{item.returnedQuantity ?? 0}</TableCell>
                    <TableCell>{item.lossOrBrokenQuantity ?? 0}</TableCell>
                    <TableCell>{item.outstandingQuantity ?? 0}</TableCell>
                    <TableCell>{formatDate(item.outstandingCollectionDueDate)}</TableCell>
                    <TableCell>{item.outstandingCollectedQuantity ?? 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Box>
    );
  };

  const renderReportDetails = () => {
    if (detailLoading) return <Loader />;
    if (!selectedReport) return <OperationsEmptyState title="No report selected" description="Choose a broken/loss report from the list." />;
    return (
      <Box display="flex" flexDirection="column" gap={2}>
        <WarehouseHeroCard
          title={selectedReport.reportNumber || "Broken / Loss Report"}
          subtitle={`${selectedReport.checklistNumber || "No checklist"} | ${selectedReport.caseNumber || "No case linked"}`}
          status={selectedReport.status}
          meta={
            <>
              <Chip label={`Branch ${selectedReport.responsibleBranch || "Not captured"}`} size="small" sx={{ bgcolor: ifasColors.softOrange, color: ifasColors.orange, fontWeight: 900 }} />
              <Chip label={`Items ${(selectedReport.items || []).length}`} size="small" sx={{ bgcolor: STANDARD_TONES.red.bg, color: STANDARD_TONES.red.color, fontWeight: 900 }} />
              <Chip label={`Reported ${formatDate(selectedReport.dateOfReport)}`} size="small" sx={{ bgcolor: ifasColors.page, color: ifasColors.muted, fontWeight: 900 }} />
            </>
          }
        />

        <Paper elevation={0} sx={operationsDetailPaperSx}>
          <OperationsSectionHeader title="Broken / Loss Report" meta="Inventory follow-up and accountability record" />
          <OperationsDetailGrid>
            <OperationsDetailField label="Report Number" value={selectedReport.reportNumber} />
            <OperationsDetailField label="Status" value={formatOperationsStatus(selectedReport.status)} />
            <OperationsDetailField label="Checklist Number" value={selectedReport.checklistNumber} />
            <OperationsDetailField label="Case Number" value={selectedReport.caseNumber} />
            <OperationsDetailField label="Funeral Date" value={formatDate(selectedReport.funeralDate)} />
            <OperationsDetailField label="Date Of Report" value={formatDate(selectedReport.dateOfReport)} />
            <OperationsDetailField label="Responsible Branch" value={selectedReport.responsibleBranch} />
            <OperationsDetailField label="Supervisor" value={selectedReport.supervisorName} />
            <OperationsDetailField label="Supervisor Employee No." value={selectedReport.supervisorEmployeeNumber} />
            <OperationsDetailField label="Items Returned By" value={selectedReport.itemsReturnedByName} />
            <OperationsDetailField label="Vehicle Registration" value={selectedReport.vehicleRegistrationNumber} />
            <OperationsDetailField label="Inventory Acknowledged By" value={selectedReport.inventoryAcknowledgedBy} />
            <OperationsDetailField label="Inventory Acknowledged At" value={formatDateTime(selectedReport.inventoryAcknowledgedAt)} />
          </OperationsDetailGrid>
        </Paper>

        <Paper elevation={0} sx={operationsDetailPaperSx}>
          <OperationsSectionHeader title="Reported Items" meta="Line items recorded for missing, damaged, or unreturned stock" />
          <Table size="small">
            <TableHead sx={operationsTableHeadSx}>
              <TableRow>
                <TableCell>Item Code</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Colour</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(selectedReport.items || []).map((item: any, index: number) => (
                <TableRow key={`${item.itemCode}-${index}`} sx={operationsRowSx}>
                  <TableCell>{item.itemCode || "Not captured"}</TableCell>
                  <TableCell>{item.description || "Not captured"}</TableCell>
                  <TableCell>{item.quantity ?? "Not captured"}</TableCell>
                  <TableCell>{item.colour || "Not captured"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    );
  };

  // Per-section banner + table-card metadata, straight from the approved
  // warehouse mock (banner label/description, table-card title + count badge).
  const sectionMeta: Record<WarehouseSection, { label: string; desc: string; icon: ReactNode; count: number }> = {
    PREPARATIONS: {
      label: "Preparation",
      desc: "Funeral preparation jobs from allocation to ready-for-dispatch, with driver and branch accountability.",
      icon: <Inventory2OutlinedIcon />,
      count: preparationsTotal,
    },
    MATERIAL: {
      label: "Material Dispatch",
      desc: "Dispatch checklists, signatures and returns for every set of materials that leaves the warehouse.",
      icon: <LocalShippingOutlinedIcon />,
      count: checklistsTotal,
    },
    INVENTORY: {
      label: "Inventory",
      desc: "The material master — Warehouse is the single source of truth for stock levels and availability.",
      icon: <Inventory2OutlinedIcon />,
      count: inventoryMaterials.length,
    },
    REPORTS: {
      label: "Broken / Loss",
      desc: "Breakage and loss reports raised against dispatched materials, and their inventory follow-up.",
      icon: <ReportProblemOutlinedIcon />,
      count: reportsTotal,
    },
    STOCK_TAKE: {
      label: "Stock Take",
      desc: "Scheduled counts, variances and review sign-off per branch and counting period.",
      icon: <AssignmentTurnedInOutlinedIcon />,
      count: stockTakes.length,
    },
    CLEANING: {
      label: "Cleaning Roster",
      desc: "Cleaning and storage rosters per branch, with assigned staff, chemicals and completion status.",
      icon: <AssignmentTurnedInOutlinedIcon />,
      count: cleaningRosters.length,
    },
  };
  const activeSectionMeta = sectionMeta[section];

  // Standard table-card head: title + orange count badge, as the mock shows on
  // every section's table card.
  const listCardHead = (
    <Box sx={{ px: 2.5, py: 2.25, borderBottom: `1px solid ${ifasColors.border}`, display: "flex", alignItems: "center", gap: 1.25 }}>
      <Typography sx={{ fontSize: 17, fontWeight: 800, color: ifasColors.text }}>
        {activeSectionMeta.label}
      </Typography>
      <StandardCountBadge count={activeSectionMeta.count} />
    </Box>
  );

  const renderListTable = () => {
    if (loading) return <Loader />;
    if (section === "PREPARATIONS") {
      return (
        <Paper elevation={0} sx={operationsTablePaperSx}>
          {listCardHead}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={standardTableHeadCellSx}>Preparation</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Case</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Funeral File</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Funeral Date</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Funeral / Area</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Branch</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Driver</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preparations.length ? preparations.map((item) => (
                <TableRow
                  key={item.preparationNumber}
                  hover
                  sx={standardTableRowSx}
                  onClick={() => setSelectedPreparationNumber(item.preparationNumber)}
                  style={{ cursor: "pointer" }}
                >
                  <TableCell>{item.preparationNumber}</TableCell>
                  <TableCell>{item.caseNumber}</TableCell>
                  <TableCell>{item.funeralFileNumber || "Not linked"}</TableCell>
                  <TableCell>{formatDate(item.funeralDate)}</TableCell>
                  <TableCell>{item.funeralSurnameArea || "Not captured"}</TableCell>
                  <TableCell>{item.warehouseBranch || "Not captured"}</TableCell>
                  <TableCell>{item.assignedDriverName || "Not captured"}</TableCell>
                  <TableCell><StatusDotChip status={item.status} /></TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={8}>
                    <OperationsEmptyState title="No warehouse preparations yet" description="Create the first preparation record to start warehouse coordination." />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <StandardPager page={page} total={preparationsTotal} onPage={setPage} />
        </Paper>
      );
    }

    if (section === "MATERIAL") {
      return (
        <Paper elevation={0} sx={operationsTablePaperSx}>
          {listCardHead}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={standardTableHeadCellSx}>Checklist</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Preparation</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Case</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Funeral Date</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Driver</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Vehicle</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {checklists.length ? checklists.map((item) => (
                <TableRow
                  key={item.checklistNumber}
                  hover
                  sx={standardTableRowSx}
                  onClick={() => setSelectedChecklistNumber(item.checklistNumber)}
                  style={{ cursor: "pointer" }}
                >
                  <TableCell>{item.checklistNumber}</TableCell>
                  <TableCell>{item.preparationNumber}</TableCell>
                  <TableCell>{item.caseNumber}</TableCell>
                  <TableCell>{formatDate(item.funeralDate)}</TableCell>
                  <TableCell>{item.driverName || "Not captured"}</TableCell>
                  <TableCell>{item.vehicleRegistration || "Not captured"}</TableCell>
                  <TableCell><StatusDotChip status={resolveChecklistUiStatus(item)} /></TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={7}>
                    <OperationsEmptyState title="No material checklists yet" description="Create a material dispatch checklist once warehouse preparation is underway." />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <StandardPager page={page} total={checklistsTotal} onPage={setPage} />
        </Paper>
      );
    }

    if (section === "INVENTORY") {
      return (
        <Stack spacing={2}>
        <Paper elevation={0} sx={{ ...commandPanelSx, display: "flex", gap: { xs: 1.25, md: 2 }, alignItems: "center", flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase", color: ifasColors.muted, mr: { md: 0.5 } }}>
            Stock status vocabulary
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <StatusDotChip status="Normal" tone="green" />
            <Typography sx={{ fontSize: 12, color: ifasColors.muted, whiteSpace: "nowrap" }}>available above reorder level</Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <StatusDotChip status="Low Stock" tone="amber" />
            <Typography sx={{ fontSize: 12, color: ifasColors.muted, whiteSpace: "nowrap" }}>at or below reorder level</Typography>
          </Stack>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <StatusDotChip status="Out of Stock" tone="red" />
            <Typography sx={{ fontSize: 12, color: ifasColors.muted, whiteSpace: "nowrap" }}>available quantity is zero</Typography>
          </Stack>
        </Paper>

          <Paper elevation={0} sx={operationsTablePaperSx}>
            <Box px={2.5} py={2.25} display="flex" alignItems="center" justifyContent="space-between" gap={1.25} flexWrap="wrap" borderBottom={`1px solid ${ifasColors.border}`}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 800, color: ifasColors.text }}>
                  {activeSectionMeta.label}
                </Typography>
                <StandardCountBadge count={filteredInventoryMaterials.length} />
              </Box>
              <Typography fontSize={11.5} color={ifasColors.muted}>
                {canManageInventory ? "Select a row to review or edit the material." : "Inventory master is read-only for your role."}
              </Typography>
            </Box>
            <Box sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 1120 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={standardTableHeadCellSx}>Material</TableCell>
                    <TableCell sx={standardTableHeadCellSx}>Classification</TableCell>
                    <TableCell sx={standardTableHeadCellSx}>Branch / Storage</TableCell>
                    <TableCell sx={standardTableHeadCellSx} align="center">Total</TableCell>
                    <TableCell sx={standardTableHeadCellSx} align="center">Available</TableCell>
                    <TableCell sx={standardTableHeadCellSx} align="center">Reserved</TableCell>
                    <TableCell sx={standardTableHeadCellSx} align="center">Allocated</TableCell>
                    <TableCell sx={standardTableHeadCellSx} align="center">Damaged</TableCell>
                    <TableCell sx={standardTableHeadCellSx}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedInventoryMaterials.length ? paginatedInventoryMaterials.map((item: any) => (
                    <TableRow
                      key={`${item.itemCode}-${item.warehouseBranch}`}
                      hover
                      sx={standardTableRowSx}
                      style={{ cursor: canManageInventory ? "pointer" : "default" }}
                      onClick={() => {
                        if (!canManageInventory) return;
                        setSelectedInventoryMaterial(item);
                        setFormState({
                          ...emptyInventoryForm(userName || "", item.warehouseBranch || ""),
                          ...item,
                          performedBy: userName || "",
                        });
                        setDialogMode("EDIT_INVENTORY");
                      }}
                    >
                      <TableCell sx={{ minWidth: 235 }}>
                        <Typography fontSize={12.5} fontWeight={900} color={ifasColors.text}>{item.description}</Typography>
                        <Typography fontSize={10.5} color={ifasColors.orange} fontFamily="monospace" mt={0.25}>{item.itemCode}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 175 }}>
                        <Typography fontSize={11.5} fontWeight={800}>{formatOperationsStatus(item.category || "Uncategorised")}</Typography>
                        <Typography fontSize={10.5} color={ifasColors.muted}>{item.uom || "EA"}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Typography fontSize={11.5} fontWeight={800}>{item.warehouseBranch || "Not captured"}</Typography>
                        <Typography fontSize={10.5} color={ifasColors.muted}>{item.storageLocation || "Storage location not captured"}</Typography>
                      </TableCell>
                      <TableCell align="center"><Typography fontWeight={800}>{item.totalQty ?? 0}</Typography></TableCell>
                      <TableCell align="center"><Typography sx={{ fontWeight: 900, color: ifasColors.text }}>{item.availableQty ?? 0}</Typography></TableCell>
                      <TableCell align="center">{item.reservedQty ?? 0}</TableCell>
                      <TableCell align="center">{item.allocatedQty ?? 0}</TableCell>
                      <TableCell align="center">
                        {Number(item.damagedQty || 0) > 0
                          ? <StatusDotChip status={String(item.damagedQty)} tone="red" />
                          : <Typography color={ifasColors.muted}>0</Typography>}
                      </TableCell>
                      <TableCell><StockStatusChip availableQty={item.availableQty ?? 0} lowStock={Boolean(item.lowStock)} /></TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <OperationsEmptyState
                          title={inventoryMaterials.length ? "No inventory matches these filters" : "No inventory materials yet"}
                          description={inventoryMaterials.length ? "Clear or change the search, branch, category, stock, or status filters." : "Create material master records per warehouse branch before dispatch allocation."}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
            <StandardPager page={inventoryPage} total={filteredInventoryMaterials.length} pageSize={inventoryRowsPerPage} onPage={setInventoryPage} />
          </Paper>

          <Paper elevation={0} sx={operationsTablePaperSx}>
            <Box display="flex" alignItems={{ xs: "flex-start", md: "center" }} justifyContent="space-between" gap={1.5} flexDirection={{ xs: "column", md: "row" }} mb={1}>
              <Box>
                <OperationsSectionHeader title="Recent Inventory Activity" />
                <Typography fontSize={11.5} color={ifasColors.muted} ml={1.75}>
                  Latest auditable stock changes across mortuaries. Open full history for branch-wise tracking.
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setInventoryHistoryPage(0);
                  setInventoryHistoryOpen(true);
                }}
                sx={{ textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
              >
                View Mortuary History
              </Button>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={standardTableHeadCellSx}>Movement</TableCell>
                  <TableCell sx={standardTableHeadCellSx}>Type</TableCell>
                  <TableCell sx={standardTableHeadCellSx}>Item</TableCell>
                  <TableCell sx={standardTableHeadCellSx}>Branch</TableCell>
                  <TableCell sx={standardTableHeadCellSx}>Reference</TableCell>
                  <TableCell sx={standardTableHeadCellSx}>Qty</TableCell>
                  <TableCell sx={standardTableHeadCellSx}>Available After</TableCell>
                  <TableCell sx={standardTableHeadCellSx}>By</TableCell>
                  <TableCell sx={standardTableHeadCellSx}>Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inventoryMovements.length ? inventoryMovements.slice(0, 6).map((movement) => (
                  <TableRow key={movement.movementNumber} sx={standardTableRowSx}>
                    <TableCell>{movement.movementNumber}</TableCell>
                    <TableCell><Chip size="small" label={formatOperationsStatus(movement.movementType)} sx={getOperationsStatusSx(movement.movementType)} /></TableCell>
                    <TableCell>{movement.itemCode} - {movement.description}</TableCell>
                    <TableCell>{movement.warehouseBranch}</TableCell>
                    <TableCell>{movement.referenceNumber || movement.referenceType || "Manual"}</TableCell>
                    <TableCell>{movement.quantity ?? 0}</TableCell>
                    <TableCell>{movement.balanceAfter ?? 0}</TableCell>
                    <TableCell>{movement.performedBy || "System"}</TableCell>
                    <TableCell>{formatDateTime(movement.createdAt)}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <OperationsEmptyState title="No inventory movements yet" description="Movements will appear when stock is created, reserved, dispatched, returned, adjusted, or written off." />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
        </Stack>
      );
    }

    if (section === "STOCK_TAKE") {
      const pagedStockTakes = stockTakes.slice(page * 10, page * 10 + 10);
      return (
        <Paper elevation={0} sx={operationsTablePaperSx}>
          {listCardHead}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={standardTableHeadCellSx}>Stock Take</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Branch</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Frequency</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Period</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Due</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Counted</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Variance</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Status</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedStockTakes.length ? pagedStockTakes.map((item) => (
                <TableRow key={item.stockTakeNumber} hover sx={standardTableRowSx}>
                  <TableCell>{item.stockTakeNumber}</TableCell>
                  <TableCell>{item.warehouseBranch}</TableCell>
                  <TableCell>{formatOperationsStatus(item.frequency)}</TableCell>
                  <TableCell>{formatDate(item.periodStart)} - {formatDate(item.periodEnd)}</TableCell>
                  <TableCell>{formatDate(item.countDueDate)}</TableCell>
                  <TableCell>{item.countedItems || 0}/{item.items?.length || 0}</TableCell>
                  <TableCell>
                    <StatusDotChip
                      status={`${item.varianceItems || 0} item(s) / ${Number(item.netVarianceQuantity || 0) > 0 ? "+" : ""}${item.netVarianceQuantity || 0}`}
                      tone={item.varianceItems ? "amber" : "green"}
                    />
                  </TableCell>
                  <TableCell><StatusDotChip status={item.status} /></TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.75}>
                      {["IN_PROGRESS", "REJECTED"].includes(item.status) && (
                        <Button size="small" variant="outlined" disabled={saving} onClick={() => openStockTakeCount(item)} sx={standardOutlineBtnSx}>Count Stock</Button>
                      )}
                      {item.status === "IN_PROGRESS" && (
                        <Button size="small" variant="contained" disabled={saving || !item.items?.length || item.countedItems !== item.items.length || !item.counterSignature || !item.supervisorSignature} onClick={() => submitStockTakeForReview(item)} sx={standardPrimaryBtnSx}>Submit</Button>
                      )}
                      {item.status === "SUBMITTED" && (item.frequency === "MONTHLY" ? canReviewMonthlyStockTake : canReviewWeeklyStockTake) && (
                        <Button size="small" variant="contained" disabled={saving} onClick={() => { setStockTakeReviewRecord(item); setStockTakeReviewNotes(item.varianceNotes || ""); }} sx={standardPrimaryBtnSx}>Review</Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={9}>
                    <OperationsEmptyState title="No stock takes yet" description="Create weekly or monthly stock takes for warehouse accountability." />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <StandardPager page={page} total={stockTakes.length} onPage={setPage} />
        </Paper>
      );
    }

    if (section === "CLEANING") {
      const pagedCleaningRosters = cleaningRosters.slice(page * 10, page * 10 + 10);
      return (
        <Paper elevation={0} sx={operationsTablePaperSx}>
          {listCardHead}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={standardTableHeadCellSx}>Roster</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Branch</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Equipment / Area</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Scheduled</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Assigned To</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Chemical</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Status</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedCleaningRosters.length ? pagedCleaningRosters.map((item) => (
                <TableRow key={item.rosterNumber} hover sx={standardTableRowSx}>
                  <TableCell>{item.rosterNumber}</TableCell>
                  <TableCell>{item.warehouseBranch}</TableCell>
                  <TableCell>{item.equipmentArea}</TableCell>
                  <TableCell>{formatDate(item.scheduledDate)}</TableCell>
                  <TableCell>{item.assignedTo || "Not assigned"}</TableCell>
                  <TableCell>{item.chemicalName || "Not assigned"}{item.chemicalDilution ? ` (${item.chemicalDilution})` : ""}</TableCell>
                  <TableCell><StatusDotChip status={item.status} /></TableCell>
                  <TableCell>
                    {["SCHEDULED", "OVERDUE"].includes(item.status) && (
                      <Button size="small" variant="outlined" disabled={saving} onClick={() => startCleaningRoster(item)} sx={standardOutlineBtnSx}>Start</Button>
                    )}
                    {item.status === "IN_PROGRESS" && (
                      <Button size="small" variant="contained" disabled={saving} onClick={() => openCleaningCompletion(item)} sx={standardPrimaryBtnSx}>Complete & Verify</Button>
                    )}
                    {item.status === "COMPLETED" && <Typography variant="caption">Verified by {item.supervisorVerifiedBy || "Supervisor"}</Typography>}
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={8}>
                    <OperationsEmptyState title="No cleaning rosters yet" description="Schedule equipment cleaning and storage checks for the warehouse team." />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <StandardPager page={page} total={cleaningRosters.length} onPage={setPage} />
        </Paper>
      );
    }

    return (
      <Paper elevation={0} sx={operationsTablePaperSx}>
        {listCardHead}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={standardTableHeadCellSx}>Report</TableCell>
              <TableCell sx={standardTableHeadCellSx}>Checklist</TableCell>
              <TableCell sx={standardTableHeadCellSx}>Case</TableCell>
              <TableCell sx={standardTableHeadCellSx}>Date Of Report</TableCell>
              <TableCell sx={standardTableHeadCellSx}>Branch</TableCell>
              <TableCell sx={standardTableHeadCellSx}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.length ? reports.map((item) => (
              <TableRow
                key={item.reportNumber}
                hover
                sx={standardTableRowSx}
                onClick={() => setSelectedReportNumber(item.reportNumber)}
                style={{ cursor: "pointer" }}
              >
                <TableCell>{item.reportNumber}</TableCell>
                <TableCell>{item.checklistNumber}</TableCell>
                <TableCell>{item.caseNumber}</TableCell>
                <TableCell>{formatDate(item.dateOfReport)}</TableCell>
                <TableCell>{item.responsibleBranch || "Not captured"}</TableCell>
                <TableCell><StatusDotChip status={item.status} /></TableCell>
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={6}>
                  <OperationsEmptyState title="No broken/loss reports yet" description="Loss reporting will appear here once discrepancies are flagged on material return." />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <StandardPager page={page} total={reportsTotal} onPage={setPage} />
      </Paper>
    );
  };

  const renderDialogContent = () => {
    if (isInventoryDialog) {
      const availableQty = Math.max(
        0,
        Number(formState.totalQty || 0)
          - Number(formState.reservedQty || 0)
          - Number(formState.allocatedQty || 0)
          - Number(formState.damagedQty || 0),
      );
      return (
        <Stack spacing={2} mt={1}>
          <FormSection title="Material Master" subtitle="Maintain one stock record per item code and warehouse branch.">
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
              <TextField label="Item Code" value={formState.itemCode || ""} disabled={dialogMode === "EDIT_INVENTORY"} onChange={(e) => updateField("itemCode", e.target.value)} />
              <TextField label="Description" value={formState.description || ""} onChange={(e) => updateField("description", e.target.value)} />
              <TextField label="Category" value={formState.category || ""} onChange={(e) => updateField("category", e.target.value)} />
              <TextField label="Unit Of Measure" value={formState.uom || ""} onChange={(e) => updateField("uom", e.target.value)} />
              <TextField select label="Warehouse Branch" value={formState.warehouseBranch || ""} disabled={dialogMode === "EDIT_INVENTORY"} onChange={(e) => updateField("warehouseBranch", e.target.value)}>
                <MenuItem value="">Select warehouse location</MenuItem>
                {inventoryBranchOptions.map((branch) => <MenuItem key={branch} value={branch}>{branch}</MenuItem>)}
              </TextField>
              <TextField label="Storage Location" value={formState.storageLocation || ""} onChange={(e) => updateField("storageLocation", e.target.value)} />
              <TextField select label="Status" value={formState.status || "ACTIVE"} onChange={(e) => updateField("status", e.target.value)}>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
              </TextField>
              <Paper
                variant="outlined"
                sx={{
                  p: 1,
                  borderRadius: 2,
                  bgcolor: formState.reusable ? STANDARD_TONES.green.bg : ifasColors.page,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <FormControlLabel
                  control={<Checkbox checked={Boolean(formState.reusable)} onChange={(e) => updateField("reusable", e.target.checked)} />}
                  label="Reusable Item"
                />
              </Paper>
            </Box>
          </FormSection>

          <FormSection title="Stock Position" subtitle="Available quantity is calculated as total minus reserved, allocated, and damaged stock.">
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(3, 1fr)" }} gap={2}>
              <TextField type="number" label="Total Qty" value={formState.totalQty ?? 0} onChange={(e) => updateField("totalQty", Number(e.target.value || 0))} />
              <TextField type="number" label="Reserved Qty" value={formState.reservedQty ?? 0} onChange={(e) => updateField("reservedQty", Number(e.target.value || 0))} />
              <TextField type="number" label="Allocated Qty" value={formState.allocatedQty ?? 0} onChange={(e) => updateField("allocatedQty", Number(e.target.value || 0))} />
              <TextField type="number" label="Damaged Qty" value={formState.damagedQty ?? 0} onChange={(e) => updateField("damagedQty", Number(e.target.value || 0))} />
              <TextField type="number" label="Reorder Level" value={formState.reorderLevel ?? 0} onChange={(e) => updateField("reorderLevel", Number(e.target.value || 0))} />
              <TextField label="Available Qty" value={availableQty} disabled />
              <TextField multiline minRows={2} label="Inventory Notes" value={formState.notes || ""} onChange={(e) => updateField("notes", e.target.value)} sx={{ gridColumn: "1 / -1" }} />
            </Box>
          </FormSection>
        </Stack>
      );
    }

    if (isStockTakeDialog) {
      const stockTakeItems = formState.items || [];
      return (
        <Stack spacing={2} mt={1}>
          <FormSection title="Stock Take Period" subtitle="Weekly counts are normally opened Monday and submitted by Wednesday; monthly counts follow month-end stock control.">
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
              <TextField select label="Warehouse Branch" value={formState.warehouseBranch || ""} disabled={dialogMode === "EDIT_STOCK_TAKE"} onChange={(e) => updateField("warehouseBranch", e.target.value)}>
                <MenuItem value="">Select warehouse location</MenuItem>
                {inventoryBranchOptions.map((branch) => <MenuItem key={branch} value={branch}>{branch}</MenuItem>)}
              </TextField>
              <TextField select label="Frequency" value={formState.frequency || "WEEKLY"} disabled={dialogMode === "EDIT_STOCK_TAKE"} onChange={(e) => updateField("frequency", e.target.value)}>
                <MenuItem value="WEEKLY">Weekly</MenuItem>
                <MenuItem value="MONTHLY">Monthly</MenuItem>
              </TextField>
              <TextField type="date" label="Period Start" value={formState.periodStart || ""} disabled={dialogMode === "EDIT_STOCK_TAKE"} onChange={(e) => updateField("periodStart", e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField type="date" label="Period End" value={formState.periodEnd || ""} disabled={dialogMode === "EDIT_STOCK_TAKE"} onChange={(e) => updateField("periodEnd", e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField type="date" label="Count Due Date" value={formState.countDueDate || ""} disabled={dialogMode === "EDIT_STOCK_TAKE"} onChange={(e) => updateField("countDueDate", e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField multiline minRows={3} label="Variance Notes" value={formState.varianceNotes || ""} onChange={(e) => updateField("varianceNotes", e.target.value)} />
            </Box>
          </FormSection>
          {dialogMode === "CREATE_STOCK_TAKE" ? (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: ifasColors.page }}>
              <Typography fontWeight={700}>Inventory snapshot will be created automatically</Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Save this stock take first. Every active material at the selected branch will be loaded for physical counting.
              </Typography>
            </Paper>
          ) : (
            <FormSection title="Physical Count" subtitle="Expected on hand excludes stock currently dispatched. Enter the quantity physically present in the warehouse.">
              <Paper variant="outlined" sx={{ overflowX: "auto", borderRadius: 2 }}>
                <Table size="small" sx={{ minWidth: 850 }}>
                  <TableHead sx={operationsTableHeadSx}>
                    <TableRow>
                      <TableCell>Item</TableCell><TableCell>Expected On Hand</TableCell><TableCell>Physical Count</TableCell>
                      <TableCell>Variance</TableCell><TableCell>Count Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {stockTakeItems.map((item: any, index: number) => {
                      const physical = item.physicalOnHandQty;
                      const variance = physical === null || physical === undefined || physical === ""
                        ? null : Number(physical) - Number(item.expectedOnHandQty || 0);
                      return (
                        <TableRow key={item.itemCode}>
                          <TableCell><Typography fontWeight={700}>{item.itemCode}</Typography><Typography variant="caption" color="text.secondary">{item.description} ({item.uom || "EA"})</Typography></TableCell>
                          <TableCell>{item.expectedOnHandQty ?? 0}</TableCell>
                          <TableCell><TextField size="small" type="number" value={physical ?? ""} inputProps={{ min: 0 }} onChange={(e) => updateArrayItem("items", index, "physicalOnHandQty", e.target.value === "" ? null : Number(e.target.value))} sx={{ width: 120 }} /></TableCell>
                          <TableCell><Chip size="small" label={variance === null ? "Not counted" : variance > 0 ? `+${variance}` : variance} color={variance === 0 ? "success" : variance === null ? "default" : "warning"} /></TableCell>
                          <TableCell><TextField size="small" value={item.notes || ""} onChange={(e) => updateArrayItem("items", index, "notes", e.target.value)} placeholder="Reason for variance" fullWidth /></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
              <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2} mt={2}>
                <TextField label="Physical Counter Name" required value={formState.counterName || ""} onChange={(e) => updateField("counterName", e.target.value)} />
                <TextField label="Counter Signature" required value={formState.counterSignature || ""} onChange={(e) => updateField("counterSignature", e.target.value)} helperText="Type the counter's full-name confirmation." />
                <TextField label="Warehouse Supervisor Name" required value={formState.supervisorName || ""} onChange={(e) => updateField("supervisorName", e.target.value)} />
                <TextField label="Supervisor Signature" required value={formState.supervisorSignature || ""} onChange={(e) => updateField("supervisorSignature", e.target.value)} helperText="Required before submission for review." />
              </Box>
            </FormSection>
          )}
        </Stack>
      );
    }

    if (isCleaningDialog) {
      return (
        <Stack spacing={2} mt={1}>
          <FormSection title="Weekly Cleaning / Storage Schedule" subtitle="Assign the correct chemical, cleaner, equipment area and post-clean storage location.">
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
              <TextField select label="Warehouse Branch" value={formState.warehouseBranch || ""} onChange={(e) => updateField("warehouseBranch", e.target.value)}>
                <MenuItem value="">Select warehouse location</MenuItem>
                {inventoryBranchOptions.map((branch) => <MenuItem key={branch} value={branch}>{branch}</MenuItem>)}
              </TextField>
              <TextField label="Equipment / Area" value={formState.equipmentArea || ""} onChange={(e) => updateField("equipmentArea", e.target.value)} />
              <TextField select label="Frequency" value={formState.cleaningFrequency || "WEEKLY"} onChange={(e) => updateField("cleaningFrequency", e.target.value)}>
                <MenuItem value="WEEKLY">Weekly</MenuItem>
                <MenuItem value="AFTER_USE">After Use</MenuItem>
                <MenuItem value="MONTHLY">Monthly</MenuItem>
              </TextField>
              <TextField type="date" label="Scheduled Date" value={formState.scheduledDate || ""} onChange={(e) => updateField("scheduledDate", e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField label="Assigned To" value={formState.assignedTo || ""} onChange={(e) => updateField("assignedTo", e.target.value)} />
              <TextField label="Storage Location" value={formState.storageLocation || ""} onChange={(e) => updateField("storageLocation", e.target.value)} />
              <TextField required label="Cleaning Chemical" value={formState.chemicalName || ""} onChange={(e) => updateField("chemicalName", e.target.value)} placeholder="e.g. QAC disinfectant" />
              <TextField label="Chemical Dilution / Instructions" value={formState.chemicalDilution || ""} onChange={(e) => updateField("chemicalDilution", e.target.value)} placeholder="e.g. 1:100; 10-minute contact time" />
              <TextField multiline minRows={4} label="Cleaning Checklist" value={formState.cleaningChecklist || ""} onChange={(e) => updateField("cleaningChecklist", e.target.value)} sx={{ gridColumn: "1 / -1" }} />
              <TextField multiline minRows={3} label="Notes" value={formState.notes || ""} onChange={(e) => updateField("notes", e.target.value)} sx={{ gridColumn: "1 / -1" }} />
            </Box>
          </FormSection>
        </Stack>
      );
    }

    if (dialogMode === "CREATE_PREPARATION" || dialogMode === "EDIT_PREPARATION") {
       if (dialogFormLoading) {
    return (
      <Box minHeight={320} display="flex" alignItems="center" justifyContent="center">
        <Loader />
      </Box>
    );
  }
      return (
        <Stack
          spacing={2}
          mt={1}
          sx={{
            "& .MuiTextField-root .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: ifasColors.surface,
            },
            "& .MuiTextField-root .MuiInputBase-input": {
              fontSize: 14.5,
            },
            "& .MuiTextField-root .MuiInputLabel-root": {
              fontWeight: 700,
              color: ifasColors.muted,
            },
            "& .MuiFormControlLabel-label": {
              fontSize: 13.5,
              fontWeight: 700,
              color: ifasColors.text,
            },
          }}
        >
          <FormSection title="Funeral Context" subtitle="Primary burial-form details that warehouse needs to prepare the service.">
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
              <TextField label="Case Number" value={formState.caseNumber || ""} onChange={(e) => updateField("caseNumber", e.target.value)} />
              <TextField type="date" label="Funeral Date" value={formState.funeralDate || ""} onChange={(e) => updateField("funeralDate", e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField label="Funeral Surname / Area" value={formState.funeralSurnameArea || ""} onChange={(e) => updateField("funeralSurnameArea", e.target.value)} />
              <TextField select label="Warehouse Branch" value={formState.warehouseBranch || ""} onChange={(e) => updateField("warehouseBranch", e.target.value)}>
                <MenuItem value="">Select warehouse location</MenuItem>
                {inventoryBranchOptions.map((branch) => <MenuItem key={branch} value={branch}>{branch}</MenuItem>)}
              </TextField>
              <TextField label="Service Venue" value={formState.serviceVenue || ""} onChange={(e) => updateField("serviceVenue", e.target.value)} />
              <TextField label="Cemetery Details" value={formState.cemeteryDetails || ""} onChange={(e) => updateField("cemeteryDetails", e.target.value)} />
              <TextField label="Casket Type" value={formState.casketType || ""} onChange={(e) => updateField("casketType", e.target.value)} />
              <TextField label="GAs Allocated" value={formState.generalAssistantNames || ""} onChange={(e) => updateField("generalAssistantNames", e.target.value)} helperText="Comma-separated names" />
              <Paper variant="outlined" sx={{ p: 1, borderRadius: 2, display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  control={<Checkbox checked={formState.transportRequired !== false} onChange={(e) => updateField("transportRequired", e.target.checked)} />}
                  label="Warehouse material transport required"
                />
              </Paper>
              <TextField
                type="number"
                label="Cargo Vehicles Required"
                value={formState.transportVehicleCount || 1}
                disabled={formState.transportRequired === false}
                inputProps={{ min: 1, max: 10 }}
                onChange={(e) => updateField("transportVehicleCount", Math.max(1, Math.min(10, Number(e.target.value || 1))))}
                helperText="Fleet Coordinator will assign one driver and vehicle for each requested slot"
              />
              <TextField
                multiline
                minRows={2}
                label="Transport Instructions"
                value={formState.transportRequirements || ""}
                disabled={formState.transportRequired === false}
                onChange={(e) => updateField("transportRequirements", e.target.value)}
                placeholder="Example: two trips required, large tent load, return collection after service"
              />
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: ifasColors.page,
                  borderColor: ifasColors.border,
                  gridColumn: { md: "span 2" },
                }}
              >
                <Typography fontSize={13} fontWeight={900} color={ifasColors.text}>Fleet assignment is handled in Fleet Portal</Typography>
                <Typography fontSize={12} color={ifasColors.muted} mt={0.35}>
                  Warehouse only specifies capacity and delivery needs. Driver names, vehicle registrations and allocation slots are selected by the Fleet Coordinator after the material checklist is saved.
                </Typography>
              </Paper>
            </Box>
          </FormSection>

          <FormSection title="Materials & Setup" subtitle="Capture funeral-day equipment, decor, and visual setup requirements.">
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
              <TextField
                label="Funeral Package"
                value={formState.funeralPackageDetails || formState.funeralCategory || "Not selected"}
                helperText="Selected by the Funeral Planner or the policy plan; Warehouse cannot change it."
                InputProps={{ readOnly: true }}
              />
              <TextField multiline minRows={2} label="Decor Requirements" value={formState.decorRequirements || ""} onChange={(e) => updateField("decorRequirements", e.target.value)} />
              <TextField multiline minRows={2} label="Photo / Frame Requirements" value={formState.photoAndFrameRequirements || ""} onChange={(e) => updateField("photoAndFrameRequirements", e.target.value)} />
              <TextField multiline minRows={2} label="Funeral Package Details" value={formState.funeralPackageDetails || ""} onChange={(e) => updateField("funeralPackageDetails", e.target.value)} />
              <TextField multiline minRows={2} label="Materials / Equipment Required" value={formState.materialsAndEquipmentRequired || ""} onChange={(e) => updateField("materialsAndEquipmentRequired", e.target.value)} />
              <TextField multiline minRows={3} label="Special Instructions" value={formState.specialInstructions || ""} onChange={(e) => updateField("specialInstructions", e.target.value)} sx={{ gridColumn: "1 / -1" }} />
            </Box>
          </FormSection>

          <FormSection title="Operational Notes" subtitle="These are internal warehouse notes. They help with local tracking, but they are not all part of the ready/not-ready decision.">
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
              <TextField multiline minRows={2} label="Equipment Ready Checklist" value={formState.equipmentReadyChecklist || ""} onChange={(e) => updateField("equipmentReadyChecklist", e.target.value)} />
              <TextField multiline minRows={2} label="Cleaning Roster Tasks" value={formState.cleaningRosterTasks || ""} onChange={(e) => updateField("cleaningRosterTasks", e.target.value)} />
              <TextField multiline minRows={2} label="Stock Count Data" value={formState.stockCountData || ""} onChange={(e) => updateField("stockCountData", e.target.value)} />
              <TextField multiline minRows={2} label="Equipment Status" value={formState.equipmentStatus || ""} onChange={(e) => updateField("equipmentStatus", e.target.value)} />
              <TextField multiline minRows={3} label="Notes" value={formState.notes || ""} onChange={(e) => updateField("notes", e.target.value)} sx={{ gridColumn: "1 / -1" }} />
            </Box>
          </FormSection>
        </Stack>
      );
    }

    if (dialogMode === "CREATE_MATERIAL" || dialogMode === "EDIT_MATERIAL") {
      if (dialogFormLoading) {
        return (
          <Box
            minHeight={320}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Loader />
          </Box>
        );
      }
      const materialItems = formState.items || [];
      const outstandingCount = materialItems.reduce(
        (sum: number, item: any) => sum + getItemOutstandingQuantity(item),
        0,
      );
      const collectionVisibleCount = materialItems.filter((item: any) => getItemOutstandingQuantity(item) > 0).length;
      const flaggedCount = materialItems.reduce(
        (sum: number, item: any) => sum + getItemLossQuantity(item),
        0,
      );
      const dispatchSignCount = [
        formState.dispatchSupervisorSignature,
        formState.dispatchSecurityOfficerSignature,
      ].filter(Boolean).length;
      const requiredDispatchSignCount = 2;
      const returnSignCount = [
        formState.returnSupervisorSignature,
        formState.returnSecurityOfficerSignature,
      ].filter(Boolean).length;
      const requiredReturnSignCount = 2;
      const initialChecklistDialogStatus =
        dialogMode === "EDIT_MATERIAL"
          ? String(selectedChecklist?.status || "DRAFT").toUpperCase()
          : "DRAFT";
      const materialDialogStatus =
        dialogMode === "CREATE_MATERIAL" ? "DRAFT" : initialChecklistDialogStatus;
      const isDispatchStage = materialDialogStatus === "DRAFT";
      const isReturnStage = materialDialogStatus === "DISPATCHED";
      const isCollectionStage = materialDialogStatus === "OUTSTANDING_MATERIALS";
      const isLossFollowupStage = materialDialogStatus === "PARTIALLY_RETURNED";
      const canEditDispatchSetup = isDispatchStage && checklistActionIntent !== "DISPATCH";
      const canEditDispatchSignOff = isDispatchStage;
      const canEditReturnDecisions = isReturnStage || isLossFollowupStage;
      const canEditCollectionFields = isCollectionStage;
      const canEditReturnSection = canEditReturnDecisions || canEditCollectionFields;
      const canEditOutstandingDueDate = canEditReturnDecisions || canEditCollectionFields;
      const handleOutcomeQuantityChange = (index: number, field: string, rawValue: string) => {
        const numericValue = Math.max(0, parseQuantityInput(rawValue));
        updateArrayItem("items", index, field, numericValue);
        if (field === "returnedQuantity") {
          updateArrayItem("items", index, "returned", numericValue > 0);
        }
        if (field === "lossOrBrokenQuantity") {
          updateArrayItem("items", index, "lossOrBroken", numericValue > 0);
        }
        if (field === "outstandingQuantity") {
          updateArrayItem("items", index, "outstandingWithFamily", numericValue > 0);
          const existingCollected = getItemCollectedQuantity(materialItems[index]);
          if (numericValue === 0) {
            updateArrayItem("items", index, "outstandingCollected", false);
            updateArrayItem("items", index, "outstandingCollectedQuantity", 0);
          } else if (existingCollected >= numericValue) {
            updateArrayItem("items", index, "outstandingCollected", true);
          }
        }
      };
      const handleCollectedQuantityChange = (index: number, rawValue: string) => {
        const numericValue = Math.max(0, parseQuantityInput(rawValue));
        updateArrayItem("items", index, "outstandingCollectedQuantity", numericValue);
        updateArrayItem(
          "items",
          index,
          "outstandingCollected",
          numericValue > 0 && numericValue >= getItemOutstandingQuantity(materialItems[index]),
        );
        if (numericValue > 0) {
          updateArrayItem("items", index, "returned", true);
        }
      };
      const indexedMaterialItems: Array<{ item: any; originalIndex: number }> = materialItems.map(
        (item: any, originalIndex: number) => ({ item, originalIndex }),
      );
      const visibleMaterialItems = isCollectionStage
        ? indexedMaterialItems.filter(
            ({ item }: { item: any; originalIndex: number }) =>
              getItemOutstandingQuantity(item) > 0,
          )
        : indexedMaterialItems;
      const getItemCodeHelperText = (item: any) => {
        const selectedOption = item.inventorySelection;
        if (selectedOption) {
          return `Available ${selectedOption.availableQty ?? selectedOption.availableQuantity ?? 0} at ${selectedOption.warehouseBranch || "selected warehouse"}`;
        }
        if (!formState.warehouseBranch) return "Select a warehouse branch before searching inventory";
        if (item.inventoryResolutionStatus === "RESOLVING") return "Matching package item with branch inventory...";
        if (item.inventoryResolutionStatus === "AMBIGUOUS") return "Multiple exact matches found. Select the correct item code.";
        if (item.inventoryResolutionStatus === "NOT_FOUND") return "No exact match found. Type at least 2 characters to search.";
        if (item.inventoryResolutionStatus === "ERROR") return "Inventory matching failed. Search and select the item manually.";
        if (String(item.sourceType || "").toUpperCase() !== "INVENTORY") return "This row will not reserve inventory stock";
        return "Type at least 2 characters and select from this branch's inventory";
      };
      const itemSectionSubtitle = isDispatchStage
        ? "Package-linked items are preloaded here. Verify them, adjust only what is truly leaving now, and tick Dispatched."
        : isReturnStage
          ? "The dispatch structure is now locked. Allocate Returned Qty, Outstanding Qty, and Loss / Broken Qty before confirming return."
        : isCollectionStage
            ? `This checklist is in outstanding follow-up. Only the ${collectionVisibleCount} outstanding row${collectionVisibleCount === 1 ? "" : "s"} are shown below. Complete Collected details for items that come back later.`
            : "The original dispatch structure is locked. Review flagged lines and update follow-up information only where needed.";
      const returnSectionSubtitle = isDispatchStage
        ? "You do not fill this section yet. Reopen the same checklist after the funeral to allocate returned, outstanding, and broken quantities."
        : isCollectionStage
          ? "Use this section only for later collection follow-up on items that were left with the family."
          : "After the funeral, use this section to capture what came back, what stayed with the family, and what was lost or broken.";

      return (
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", xl: "minmax(0, 1.6fr) 320px" }}
          gap={2}
          mt={1}
          sx={{
            "& .MuiTextField-root .MuiOutlinedInput-root": {
              borderRadius: 2,
              bgcolor: ifasColors.surface,
            },
            "& .MuiTextField-root .MuiInputBase-input": {
              fontSize: 14.5,
            },
            "& .MuiTextField-root .MuiInputLabel-root": {
              fontWeight: 700,
              color: ifasColors.muted,
            },
            "& .MuiFormControlLabel-label": {
              fontSize: 13.5,
              fontWeight: 700,
              color: ifasColors.text,
            },
          }}
        >
          <Stack spacing={2}>
            <ChecklistStageBanner status={materialDialogStatus} mode="dialog" />

            {checklistActionIntent && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: checklistActionIntent === "RETURN" ? alpha(STANDARD_TONES.green.color, 0.25) : alpha(ifasColors.orange, 0.35),
                  bgcolor: checklistActionIntent === "RETURN" ? STANDARD_TONES.green.bg : ifasColors.softOrange,
                }}
              >
                <Typography fontWeight={900} color={ifasColors.text} textAlign="left">
                  {checklistActionIntent === "REQUEST_APPROVAL"
                    ? "Dispatch approval request workflow"
                    : checklistActionIntent === "DISPATCH"
                    ? "Dispatch confirmation workflow"
                    : "Return reconciliation workflow"}
                </Typography>
                <Typography fontSize={12.5} color={ifasColors.muted} mt={0.35} textAlign="left">
                  {checklistActionIntent === "REQUEST_APPROVAL"
                    ? "Confirm the items that will leave the warehouse, capture Supervisor sign-off, capture Security Officer co-sign, then submit the approval request."
                    : checklistActionIntent === "DISPATCH"
                    ? "Confirm the items leaving the warehouse, capture Warehouse Supervisor sign-off, and capture Security Officer co-sign before the material leaves."
                    : "Allocate every dispatched item into returned, outstanding, or loss/broken quantities, then capture Supervisor and Security Officer return sign-off."}
                </Typography>
              </Paper>
            )}

            <FormSection
              title="Dispatch Context"
              subtitle={
                isDispatchStage
                  ? "Link this checklist to the funeral movement plan and transport setup."
                  : "Dispatch context is locked after material has left the warehouse so later updates stay focused on return and reconciliation."
              }
            >
              <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
                <TextField label="Preparation Number" value={formState.preparationNumber || ""} disabled={!canEditDispatchSetup} onChange={(e) => updateField("preparationNumber", e.target.value)} />
                <TextField
                  select
                  label="Warehouse Branch"
                  value={formState.warehouseBranch || ""}
                  disabled={!canEditDispatchSetup}
                  onChange={(e) => handleChecklistBranchChange(e.target.value)}
                  helperText="Inventory stock is matched against this exact branch"
                >
                  <MenuItem value="">Select warehouse location</MenuItem>
                  {inventoryBranchOptions.map((branch) => <MenuItem key={branch} value={branch}>{branch}</MenuItem>)}
                </TextField>
                <TextField
                  label="Fleet-assigned Driver(s)"
                  value={formState.driverName || ""}
                  disabled
                  placeholder="Pending Fleet allocation"
                  helperText={formState.warehouseTransportRequired === false
                    ? "Warehouse transport is not required for this preparation"
                    : formState.warehouseTransportAllocated
                      ? `Assigned by Fleet for ${formState.warehouseTransportRequestNumber || "this request"}`
                      : `Fleet allocation pending${formState.warehouseTransportRequestNumber ? ` · ${formState.warehouseTransportRequestNumber}` : " · request is created when this checklist is saved"}`}
                />
                <TextField label="GAs" value={formState.generalAssistantNames || ""} disabled={!canEditDispatchSetup} onChange={(e) => updateField("generalAssistantNames", e.target.value)} />
                <TextField
                  label="Fleet-assigned Vehicle(s)"
                  value={formState.vehicleRegistration || ""}
                  disabled
                  placeholder="Pending Fleet allocation"
                  helperText="Driver and vehicle are assigned only from Fleet Portal"
                />
                <TextField multiline minRows={2} label="Transport Equipment" value={formState.transportEquipment || ""} disabled={!canEditDispatchSetup} onChange={(e) => updateField("transportEquipment", e.target.value)} />
              </Box>
            </FormSection>

            <FormSection title="Material Items" subtitle={itemSectionSubtitle}>
              <Stack spacing={1.5}>
                {visibleMaterialItems.map(({ item, originalIndex }: any) => (
                  <Paper
                    key={`material-item-${originalIndex}`}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      borderColor: getItemLossQuantity(item) > 0
                        ? alpha(STANDARD_TONES.red.color, 0.22)
                        : getItemOutstandingQuantity(item) > 0
                          ? alpha(STANDARD_TONES.amber.color, 0.22)
                          : ifasColors.border,
                      background: getItemLossQuantity(item) > 0
                        ? `linear-gradient(135deg, ${ifasColors.surface} 0%, ${STANDARD_TONES.red.bg} 100%)`
                        : getItemOutstandingQuantity(item) > 0
                          ? `linear-gradient(135deg, ${ifasColors.surface} 0%, ${STANDARD_TONES.amber.bg} 100%)`
                          : ifasColors.surface,
                    }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1.25} flexWrap="wrap">
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography fontWeight={900} color={ifasColors.text}>
                          Item {originalIndex + 1}
                        </Typography>
                        {(item.packageSeeded || String(item.sourceReference || "").startsWith("Package seed")) && (
                          <Chip label="Auto-loaded" size="small" sx={{ bgcolor: STANDARD_TONES.blue.bg, color: STANDARD_TONES.blue.color, fontWeight: 900 }} />
                        )}
                        {item.inventoryResolutionStatus === "MATCHED" && (
                          <Chip label="Inventory matched" size="small" sx={{ bgcolor: STANDARD_TONES.green.bg, color: STANDARD_TONES.green.color, fontWeight: 900 }} />
                        )}
                        {item.inventoryResolutionStatus === "AMBIGUOUS" && (
                          <Chip label="Select correct item" size="small" sx={{ bgcolor: STANDARD_TONES.amber.bg, color: STANDARD_TONES.amber.color, fontWeight: 900 }} />
                        )}
                        {["NOT_FOUND", "ERROR", "BRANCH_REQUIRED"].includes(item.inventoryResolutionStatus) && (
                          <Chip label={item.inventoryResolutionStatus === "BRANCH_REQUIRED" ? "Branch required" : "Item code required"} size="small" sx={{ bgcolor: STANDARD_TONES.amber.bg, color: STANDARD_TONES.amber.color, fontWeight: 900 }} />
                        )}
                        {getItemOutstandingQuantity(item) > 0 && (
                          <Chip label={`Outstanding ${getItemOutstandingQuantity(item)}`} size="small" sx={{ bgcolor: STANDARD_TONES.amber.bg, color: STANDARD_TONES.amber.color, fontWeight: 900 }} />
                        )}
                        {getItemLossQuantity(item) > 0 && (
                          <Chip label={`Flagged ${getItemLossQuantity(item)}`} size="small" sx={{ bgcolor: STANDARD_TONES.red.bg, color: STANDARD_TONES.red.color, fontWeight: 900 }} />
                        )}
                        {isCollectionStage && getItemPendingOutstandingQuantity(item) > 0 && (
                          <Chip label={`Needs Collection ${getItemPendingOutstandingQuantity(item)}`} size="small" sx={{ bgcolor: STANDARD_TONES.green.bg, color: STANDARD_TONES.green.color, fontWeight: 900 }} />
                        )}
                      </Box>
                      {canEditDispatchSetup && (materialItems || []).length > 1 && (
                        <Button color="error" onClick={() => removeArrayItem("items", originalIndex)}>Remove</Button>
                      )}
                    </Box>
                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 2fr 0.8fr 1fr" }} gap={1.5}>
                      <Autocomplete
                        options={inventoryLookupOptions[originalIndex] || (item.inventorySelection ? [item.inventorySelection] : [])}
                        value={item.inventorySelection || null}
                        disabled={!canEditDispatchSetup || String(item.sourceType || "").toUpperCase() !== "INVENTORY"}
                        inputValue={item.itemCode || ""}
                        loading={Boolean(inventoryLookupLoading[originalIndex]) || (inventoryResolveLoading && item.inventoryResolutionStatus === "RESOLVING")}
                        filterOptions={(options) => options}
                        getOptionLabel={(option: any) =>
                          `${option.itemCode || ""} - ${option.description || ""}`
                        }
                        isOptionEqualToValue={(option: any, value: any) =>
                          option?.itemCode === value?.itemCode
                            && option?.warehouseBranch === value?.warehouseBranch
                        }
                        onOpen={() => void handleInventoryLookupOpen(originalIndex)}
                        onInputChange={(_, value, reason) => {
                          if (reason === "input") {
                            handleInventoryLookupInput(originalIndex, value);
                          }
                        }}
                        onChange={(_, value) => handleChecklistInventoryItemChange(originalIndex, value)}
                        getOptionDisabled={(option: any) => Number(option.availableQty ?? option.availableQuantity ?? 0) <= 0}
                        noOptionsText={!formState.warehouseBranch
                          ? "Select warehouse branch first"
                          : String(item.itemCode || "").trim().length < 2
                            ? "No active inventory items found at this branch"
                            : "No active items found at this branch"}
                        renderOption={(props, option: any) => (
                          <Box component="li" {...props} key={`${option.itemCode}-${option.warehouseBranch}`}>
                            <Box width="100%">
                              <Typography fontSize={13.5} fontWeight={900}>
                                {option.itemCode} - {option.description}
                              </Typography>
                              <Typography fontSize={12} color={ifasColors.muted}>
                                {option.warehouseBranch} | Available {option.availableQty ?? option.availableQuantity ?? 0} | Reserved {option.reservedQty ?? option.reservedQuantity ?? 0}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Inventory Item Code"
                            helperText={getItemCodeHelperText(item)}
                          />
                        )}
                      />
                      <TextField label="Description" value={item.description || ""} disabled={!canEditDispatchSetup} onChange={(e) => updateArrayItem("items", originalIndex, "description", e.target.value)} />
                      <TextField type="number" label="Qty" value={item.quantity || ""} disabled={!canEditDispatchSetup} onChange={(e) => updateArrayItem("items", originalIndex, "quantity", Number(e.target.value))} />
                      <TextField label="Colour" value={item.colour || ""} disabled={!canEditDispatchSetup} onChange={(e) => updateArrayItem("items", originalIndex, "colour", e.target.value)} />
                    </Box>
                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }} gap={1.5} mt={1.5}>
                      <TextField
                        select
                        label="Setup Location"
                        value={item.setupLocationType || ""}
                        disabled={!canEditDispatchSetup}
                        onChange={(e) => updateArrayItem("items", originalIndex, "setupLocationType", e.target.value)}
                      >
                        <MenuItem value="">Not specified</MenuItem>
                        <MenuItem value="HOME">Home</MenuItem>
                        <MenuItem value="VENUE">Venue</MenuItem>
                        <MenuItem value="CEMETERY">Cemetery</MenuItem>
                        <MenuItem value="WAREHOUSE">Warehouse</MenuItem>
                      </TextField>
                      <TextField
                        select
                        label="Source Type"
                        value={item.sourceType || ""}
                        disabled={!canEditDispatchSetup}
                        onChange={(e) => handleChecklistSourceTypeChange(originalIndex, e.target.value)}
                      >
                        <MenuItem value="">Not specified</MenuItem>
                        <MenuItem value="INVENTORY">Inventory</MenuItem>
                        <MenuItem value="INTERNAL_SUPPLY">Internal Supply</MenuItem>
                        <MenuItem value="EXTERNAL_SUPPLIER">External Supplier</MenuItem>
                      </TextField>
                      <TextField
                        label="Source Reference"
                        value={item.sourceReference || ""}
                        disabled={!canEditDispatchSetup || String(item.sourceType || "").toUpperCase() === "INVENTORY"}
                        helperText={String(item.sourceType || "").toUpperCase() === "INVENTORY" ? "Filled from the selected inventory item" : ""}
                        onChange={(e) => updateArrayItem("items", originalIndex, "sourceReference", e.target.value)}
                      />
                    </Box>
                    {isDispatchStage && (
                      <Box mt={1.5}>
                      <Paper variant="outlined" sx={{ p: 0.75, borderRadius: 2, bgcolor: ifasColors.page }}>
                        <FormControlLabel control={<Checkbox disabled={!canEditDispatchSetup} checked={Boolean(item.dispatched)} onChange={(e) => updateArrayItem("items", originalIndex, "dispatched", e.target.checked)} />} label="Dispatched" />
                      </Paper>
                      </Box>
                    )}
                    {canEditReturnDecisions && (
                      <Box
                        display="grid"
                        gridTemplateColumns={{ xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }}
                        gap={1.25}
                        mt={1.5}
                      >
                      <TextField
                        type="text"
                        label="Returned Qty"
                        value={getEditableQuantityDisplayValue(item.returnedQuantity, canEditReturnDecisions)}
                        disabled={!canEditReturnDecisions}
                        onChange={(e) => handleOutcomeQuantityChange(originalIndex, "returnedQuantity", e.target.value)}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      />
                      <TextField
                        type="text"
                        label="Loss / Broken Qty"
                        value={getEditableQuantityDisplayValue(item.lossOrBrokenQuantity, canEditReturnDecisions)}
                        disabled={!canEditReturnDecisions}
                        onChange={(e) => handleOutcomeQuantityChange(originalIndex, "lossOrBrokenQuantity", e.target.value)}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      />
                      <TextField
                        type="text"
                        label="Outstanding Qty"
                        value={getEditableQuantityDisplayValue(item.outstandingQuantity, canEditReturnDecisions)}
                        disabled={!canEditReturnDecisions}
                        onChange={(e) => handleOutcomeQuantityChange(originalIndex, "outstandingQuantity", e.target.value)}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      />
                      </Box>
                    )}
                    {isCollectionStage && (
                      <TextField
                        sx={{ mt: 1.5, maxWidth: { md: 320 } }}
                        type="text"
                        label="Collected Qty"
                        value={getEditableQuantityDisplayValue(item.outstandingCollectedQuantity, canEditCollectionFields)}
                        disabled={!canEditCollectionFields || getItemOutstandingQuantity(item) === 0}
                        onChange={(e) => handleCollectedQuantityChange(originalIndex, e.target.value)}
                        inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      />
                    )}
                    {!isDispatchStage && (
                      <Typography fontSize={12.5} color={ifasColors.muted} mt={1}>
                        Accounted: {getItemReturnedQuantity(item) + getItemLossQuantity(item) + getItemOutstandingQuantity(item)} / {Number(item.quantity || 0)}
                        {isCollectionStage && getItemOutstandingQuantity(item) > 0
                          ? ` | Collected: ${getItemCollectedQuantity(item)} / ${getItemOutstandingQuantity(item)}`
                          : ""}
                      </Typography>
                    )}
                    {canEditReturnDecisions && (
                      <Box mt={1.5} sx={{ maxWidth: { md: 360 } }}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Outstanding Due Date"
                        value={item.outstandingCollectionDueDate || ""}
                        disabled={!canEditOutstandingDueDate || getItemOutstandingQuantity(item) === 0}
                        onChange={(e) => updateArrayItem("items", originalIndex, "outstandingCollectionDueDate", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      </Box>
                    )}
                    {isCollectionStage && (
                      <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={1.5} mt={1.5}>
                      <TextField
                        type="datetime-local"
                        label="Collected At"
                        value={item.outstandingCollectedAt || ""}
                        disabled={!canEditCollectionFields || getItemOutstandingQuantity(item) === 0}
                        onChange={(e) => updateArrayItem("items", originalIndex, "outstandingCollectedAt", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField label="Collected By" value={item.outstandingCollectedBy || ""} disabled={!canEditCollectionFields || getItemOutstandingQuantity(item) === 0} onChange={(e) => updateArrayItem("items", originalIndex, "outstandingCollectedBy", e.target.value)} />
                      </Box>
                    )}
                  </Paper>
                ))}
                {!isCollectionStage && (
                  <Button startIcon={<AddOutlinedIcon />} disabled={!canEditDispatchSetup} onClick={() => addArrayItem("items", emptyMaterialItem())}>Add Item</Button>
                )}
              </Stack>
            </FormSection>

            {isDispatchStage && (
              <FormSection title="Dispatch Approval" subtitle="At dispatch time, mark what actually left the warehouse and capture supervisor confirmation.">
              <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
                <TextField label="Dispatch Supervisor Name" value={formState.dispatchSupervisorName || ""} disabled={!canEditDispatchSignOff} onChange={(e) => updateField("dispatchSupervisorName", e.target.value)} />
                <TextField label="Dispatch Supervisor Designation" value={formState.dispatchSupervisorDesignation || ""} disabled={!canEditDispatchSignOff} onChange={(e) => updateField("dispatchSupervisorDesignation", e.target.value)} />
                <TextField label="Supervisor Employee Number" value={formState.dispatchSupervisorEmployeeNumber || ""} disabled={!canEditDispatchSignOff} onChange={(e) => updateField("dispatchSupervisorEmployeeNumber", e.target.value)} />
                <TextField label="Supervisor Signature" value={formState.dispatchSupervisorSignature || ""} disabled={!canEditDispatchSignOff} onChange={(e) => updateField("dispatchSupervisorSignature", e.target.value)} />
                <TextField type="date" label="Supervisor Date" value={formState.dispatchSupervisorDate || ""} disabled={!canEditDispatchSignOff} onChange={(e) => updateField("dispatchSupervisorDate", e.target.value)} InputLabelProps={{ shrink: true }} />
                <Box sx={{ gridColumn: "1 / -1", mt: 0.5 }}>
                  <Typography fontSize={13} fontWeight={900} color={ifasColors.text} textAlign="left">
                    Security Officer Co-sign
                  </Typography>
                  <Typography fontSize={12} color={ifasColors.muted} mt={0.25} textAlign="left">
                    Required before dispatch can be approved because Security co-signs the material leaving the warehouse.
                  </Typography>
                </Box>
                <TextField label="Dispatch Security Officer Name" value={formState.dispatchSecurityOfficerName || ""} disabled={!canEditDispatchSignOff} onChange={(e) => updateField("dispatchSecurityOfficerName", e.target.value)} />
                <TextField label="Security Officer Signature" value={formState.dispatchSecurityOfficerSignature || ""} disabled={!canEditDispatchSignOff} onChange={(e) => updateField("dispatchSecurityOfficerSignature", e.target.value)} />
                <TextField type="date" label="Security Officer Date" value={formState.dispatchSecurityOfficerDate || ""} disabled={!canEditDispatchSignOff} onChange={(e) => updateField("dispatchSecurityOfficerDate", e.target.value)} InputLabelProps={{ shrink: true }} />
              </Box>
              </FormSection>
            )}

            {!isDispatchStage && (
              <FormSection title="Return & Reconciliation" subtitle={returnSectionSubtitle}>
              <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
                <TextField label="Collector Name" value={formState.collectorName || ""} disabled={!canEditReturnSection} onChange={(e) => updateField("collectorName", e.target.value)} />
                <TextField label="Collector Designation" value={formState.collectorDesignation || ""} disabled={!canEditReturnSection} onChange={(e) => updateField("collectorDesignation", e.target.value)} />
                <TextField label="Collector Employee Number" value={formState.collectorEmployeeNumber || ""} disabled={!canEditReturnSection} onChange={(e) => updateField("collectorEmployeeNumber", e.target.value)} />
                <TextField label="Collector Signature" value={formState.collectorSignature || ""} disabled={!canEditReturnSection} onChange={(e) => updateField("collectorSignature", e.target.value)} />
                <TextField type="date" label="Collector Date" value={formState.collectorDate || ""} disabled={!canEditReturnSection} onChange={(e) => updateField("collectorDate", e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField type="datetime-local" label="Return Date / Time" value={formState.returnDateTime || ""} disabled={!canEditReturnSection} onChange={(e) => updateField("returnDateTime", e.target.value)} InputLabelProps={{ shrink: true }} />
                <TextField label="Returning Driver / GA" value={formState.returningDriverOrGa || ""} disabled={!canEditReturnSection} onChange={(e) => updateField("returningDriverOrGa", e.target.value)} />
                <TextField label="Return Supervisor Name" value={formState.returnSupervisorName || ""} disabled={!canEditReturnSection} onChange={(e) => updateField("returnSupervisorName", e.target.value)} />
                <TextField label="Return Supervisor Signature" value={formState.returnSupervisorSignature || ""} disabled={!canEditReturnSection} onChange={(e) => updateField("returnSupervisorSignature", e.target.value)} />
                <Box sx={{ gridColumn: "1 / -1", mt: 0.5 }}>
                  <Typography fontSize={13} fontWeight={900} color={ifasColors.text} textAlign="left">
                    Return Security Officer Co-sign
                  </Typography>
                  <Typography fontSize={12} color={ifasColors.muted} mt={0.25} textAlign="left">
                    Required before return can be confirmed because Security verifies the items physically coming back into the warehouse.
                  </Typography>
                </Box>
                <TextField label="Return Security Officer Name" value={formState.returnSecurityOfficerName || ""} disabled={!canEditReturnSection} onChange={(e) => updateField("returnSecurityOfficerName", e.target.value)} />
                <TextField label="Return Security Officer Signature" value={formState.returnSecurityOfficerSignature || ""} disabled={!canEditReturnSection} onChange={(e) => updateField("returnSecurityOfficerSignature", e.target.value)} />
                <TextField multiline minRows={2} label="Discrepancy Notes" value={formState.discrepancyNotes || ""} disabled={!canEditReturnSection} onChange={(e) => updateField("discrepancyNotes", e.target.value)} />
                <TextField multiline minRows={3} label="Notes" value={formState.notes || ""} onChange={(e) => updateField("notes", e.target.value)} sx={{ gridColumn: "1 / -1" }} />
              </Box>
              </FormSection>
            )}
          </Stack>

          <Stack spacing={1.5} sx={{ position: { xl: "sticky" }, top: { xl: 8 }, alignSelf: "start" }}>
            <Paper elevation={0} sx={{ ...commandPanelSx, background: `linear-gradient(135deg, ${ifasColors.surface} 0%, ${ifasColors.softOrange} 100%)` }}>
              <Typography fontWeight={900} color={ifasColors.text} textAlign="left">
                Checklist Snapshot
              </Typography>
              <Typography fontSize={12.5} color={ifasColors.muted} mt={0.45} textAlign="left">
                Keep the operational picture visible while you fill the checklist.
              </Typography>
              <Box display="flex" gap={0.75} flexWrap="wrap" mt={1.25}>
                <Chip label={formState.preparationNumber || "No preparation"} size="small" sx={{ bgcolor: ifasColors.softOrange, color: ifasColors.orange, fontWeight: 900 }} />
                <Chip label={formState.driverName || "Driver pending"} size="small" sx={{ bgcolor: STANDARD_TONES.blue.bg, color: STANDARD_TONES.blue.color, fontWeight: 900 }} />
                <Chip label={formState.vehicleRegistration || "Vehicle pending"} size="small" sx={{ bgcolor: ifasColors.page, color: ifasColors.muted, fontWeight: 900 }} />
              </Box>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} mt={1.5}>
                <ModalStatTile label="Items" value={materialItems.length} tone="accent" />
                <ModalStatTile label="Outstanding" value={outstandingCount} tone={outstandingCount ? "warning" : "neutral"} />
                <ModalStatTile label="Flagged" value={flaggedCount} tone={flaggedCount ? "danger" : "neutral"} />
                <ModalStatTile label="Dispatch Sign" value={`${dispatchSignCount}/${requiredDispatchSignCount}`} tone={dispatchSignCount === requiredDispatchSignCount ? "success" : "warning"} />
                <ModalStatTile label="Return Sign" value={`${returnSignCount}/${requiredReturnSignCount}`} tone={returnSignCount === requiredReturnSignCount ? "success" : "neutral"} />
                <ModalStatTile label="Trailer" value={formState.hearseTrailerRegistration ? "Set" : "Pending"} tone={formState.hearseTrailerRegistration ? "success" : "neutral"} />
              </Box>
            </Paper>

            <Paper elevation={0} sx={commandPanelSx}>
              <Typography fontWeight={900} color={ifasColors.text} textAlign="left">
                Accountability Checks
              </Typography>
              <Stack spacing={1} mt={1.15}>
                <Box sx={{ ...subtleTileSx, px: 1.15, py: 1, bgcolor: dispatchSignCount === requiredDispatchSignCount ? STANDARD_TONES.green.bg : ifasColors.page }}>
                  <Typography fontSize={12.5} fontWeight={800} color={ifasColors.text} textAlign="left">
                    Dispatch stage
                  </Typography>
                  <Typography fontSize={12} color={ifasColors.muted} mt={0.3} textAlign="left">
                    While dispatching, tick only the items that actually left the warehouse and capture Supervisor plus Security Officer sign-off.
                  </Typography>
                </Box>
                <Box sx={{ ...subtleTileSx, px: 1.15, py: 1, bgcolor: ifasColors.page }}>
                  <Typography fontSize={12.5} fontWeight={800} color={ifasColors.text} textAlign="left">
                    Return stage
                  </Typography>
                  <Typography fontSize={12} color={ifasColors.muted} mt={0.3} textAlign="left">
                    After the funeral, open this same checklist again and allocate the dispatched quantity into returned, outstanding, and broken quantities before confirming return.
                  </Typography>
                </Box>
                <Box sx={{ ...subtleTileSx, px: 1.15, py: 1, bgcolor: outstandingCount ? STANDARD_TONES.amber.bg : ifasColors.page }}>
                  <Typography fontSize={12.5} fontWeight={800} color={ifasColors.text} textAlign="left">
                    Outstanding stage
                  </Typography>
                  <Typography fontSize={12} color={ifasColors.muted} mt={0.3} textAlign="left">
                    If something is left with family, capture Outstanding Qty. Later, after collection, capture Collected Qty and fill collected date and person.
                  </Typography>
                </Box>
                <Box sx={{ ...subtleTileSx, px: 1.15, py: 1, bgcolor: flaggedCount ? STANDARD_TONES.red.bg : ifasColors.page }}>
                  <Typography fontSize={12.5} fontWeight={800} color={ifasColors.text} textAlign="left">
                    Loss / broken items should be traceable
                  </Typography>
                  <Typography fontSize={12} color={ifasColors.muted} mt={0.3} textAlign="left">
                    Flag discrepancies here so the later broken/loss report is easy to prepare.
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </Box>
      );
    }

    return (
      <Stack
        spacing={2}
        mt={1}
        sx={{
          "& .MuiTextField-root .MuiOutlinedInput-root": {
            borderRadius: 2,
            bgcolor: ifasColors.surface,
          },
          "& .MuiTextField-root .MuiInputBase-input": {
            fontSize: 14.5,
          },
          "& .MuiTextField-root .MuiInputLabel-root": {
            fontWeight: 700,
            color: ifasColors.muted,
          },
        }}
      >
        <FormSection title="Report Context" subtitle="Record the branch, checklist, and vehicle context for the discrepancy.">
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
            <TextField label="Checklist Number" value={formState.checklistNumber || ""} onChange={(e) => updateField("checklistNumber", e.target.value)} />
            <TextField type="date" label="Date Of Report" value={formState.dateOfReport || ""} onChange={(e) => updateField("dateOfReport", e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField select label="Responsible Branch" value={formState.responsibleBranch || ""} onChange={(e) => updateField("responsibleBranch", e.target.value)}>
              <MenuItem value="">Select warehouse location</MenuItem>
              {inventoryBranchOptions.map((branch) => <MenuItem key={branch} value={branch}>{branch}</MenuItem>)}
            </TextField>
            <TextField label="Vehicle Registration" value={formState.vehicleRegistrationNumber || ""} onChange={(e) => updateField("vehicleRegistrationNumber", e.target.value)} />
          </Box>
        </FormSection>

        <FormSection title="Reported Items" subtitle="List each missing or damaged item that needs inventory follow-up.">
          <Stack spacing={1.5}>
            {(formState.items || []).map((item: any, index: number) => (
              <Paper
                key={`loss-item-${index}`}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  borderColor: alpha(STANDARD_TONES.red.color, 0.12),
                  background: `linear-gradient(135deg, ${ifasColors.surface} 0%, ${STANDARD_TONES.red.bg} 100%)`,
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1.25} flexWrap="wrap">
                  <Typography fontWeight={900} color={ifasColors.text}>
                    Report Item {index + 1}
                  </Typography>
                  {(formState.items || []).length > 1 && (
                    <Button color="error" onClick={() => removeArrayItem("items", index)}>Remove</Button>
                  )}
                </Box>
                <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1.9fr 0.9fr 1fr" }} gap={1.5}>
                  <TextField label="Item Code" value={item.itemCode || ""} onChange={(e) => updateArrayItem("items", index, "itemCode", e.target.value)} />
                  <TextField label="Description" value={item.description || ""} onChange={(e) => updateArrayItem("items", index, "description", e.target.value)} />
                  <TextField type="number" label="Quantity" value={item.quantity || ""} onChange={(e) => updateArrayItem("items", index, "quantity", Number(e.target.value))} />
                  <TextField label="Colour" value={item.colour || ""} onChange={(e) => updateArrayItem("items", index, "colour", e.target.value)} />
                </Box>
              </Paper>
            ))}
            <Button startIcon={<AddOutlinedIcon />} onClick={() => addArrayItem("items", emptyLossItem())}>Add Item</Button>
          </Stack>
        </FormSection>

        <FormSection title="Supervisor Verification" subtitle="The report needs accountable sign-off before it moves to inventory.">
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
            <TextField label="Supervisor Name" value={formState.supervisorName || ""} onChange={(e) => updateField("supervisorName", e.target.value)} />
            <TextField label="Supervisor Employee Number" value={formState.supervisorEmployeeNumber || ""} onChange={(e) => updateField("supervisorEmployeeNumber", e.target.value)} />
            <TextField label="Supervisor Signature" value={formState.supervisorSignature || ""} onChange={(e) => updateField("supervisorSignature", e.target.value)} />
            <TextField type="date" label="Supervisor Date" value={formState.supervisorDate || ""} onChange={(e) => updateField("supervisorDate", e.target.value)} InputLabelProps={{ shrink: true }} />
          </Box>
        </FormSection>

        <FormSection title="Items Returned By" subtitle="Capture the handback details from the operational team.">
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
            <TextField label="Name" value={formState.itemsReturnedByName || ""} onChange={(e) => updateField("itemsReturnedByName", e.target.value)} />
            <TextField label="Employee Number" value={formState.itemsReturnedByEmployeeNumber || ""} onChange={(e) => updateField("itemsReturnedByEmployeeNumber", e.target.value)} />
            <TextField label="Signature" value={formState.itemsReturnedBySignature || ""} onChange={(e) => updateField("itemsReturnedBySignature", e.target.value)} />
            <TextField type="date" label="Date" value={formState.itemsReturnedByDate || ""} onChange={(e) => updateField("itemsReturnedByDate", e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField multiline minRows={2} label="Variance Report Notes" value={formState.varianceReportNotes || ""} onChange={(e) => updateField("varianceReportNotes", e.target.value)} sx={{ gridColumn: "1 / -1" }} />
            <TextField multiline minRows={3} label="Notes" value={formState.notes || ""} onChange={(e) => updateField("notes", e.target.value)} sx={{ gridColumn: "1 / -1" }} />
          </Box>
        </FormSection>
      </Stack>
    );
  };

  const isMaterialDialog = dialogMode === "CREATE_MATERIAL" || dialogMode === "EDIT_MATERIAL";
  const isPreparationDialog = dialogMode === "CREATE_PREPARATION" || dialogMode === "EDIT_PREPARATION";
  const isReportDialog = dialogMode === "CREATE_REPORT" || dialogMode === "EDIT_REPORT";
  const isInventoryDialog = dialogMode === "CREATE_INVENTORY" || dialogMode === "EDIT_INVENTORY";
  const isStockTakeDialog = dialogMode === "CREATE_STOCK_TAKE" || dialogMode === "EDIT_STOCK_TAKE";
  const isCleaningDialog = dialogMode === "CREATE_CLEANING_ROSTER";
  const isEditDialog =
    dialogMode === "EDIT_PREPARATION" ||
    dialogMode === "EDIT_MATERIAL" ||
    dialogMode === "EDIT_INVENTORY" ||
    dialogMode === "EDIT_STOCK_TAKE" ||
    dialogMode === "EDIT_REPORT";
  const dialogTitle = checklistActionIntent === "REQUEST_APPROVAL"
    ? "Request Dispatch Approval"
    : checklistActionIntent === "DISPATCH"
    ? "Confirm Material Dispatch"
    : checklistActionIntent === "RETURN"
      ? "Confirm Material Return"
      : dialogMode
        ? DIALOG_TITLES[dialogMode]
        : "";
  const dialogPrimaryLabel = checklistActionIntent === "REQUEST_APPROVAL"
    ? "Save & Request Approval"
    : checklistActionIntent === "DISPATCH"
    ? "Save & Confirm Dispatch"
    : checklistActionIntent === "RETURN"
      ? "Save & Confirm Return"
      : isEditDialog
        ? "Update"
        : isMaterialDialog
          ? "Save Checklist"
          : "Save";
  const canSubmitDialog =
    (isPreparationDialog && canWritePreparation) ||
    (isMaterialDialog && (
      checklistActionIntent === "REQUEST_APPROVAL"
        ? canWriteChecklist
        : checklistActionIntent
          ? canApproveChecklist
          : canWriteChecklist
    )) ||
    (isInventoryDialog && canManageInventory) ||
    (isReportDialog && canWriteReport) ||
    (isStockTakeDialog && (dialogMode === "EDIT_STOCK_TAKE" ? canManageInventory : canWritePreparation)) ||
    (isCleaningDialog && canWritePreparation);

  const currentMetrics =
    section === "PREPARATIONS"
      ? preparationMetrics
      : section === "MATERIAL"
        ? materialMetrics
        : section === "INVENTORY"
          ? inventoryMetrics
          : section === "REPORTS"
            ? reportMetrics
            : section === "STOCK_TAKE"
              ? stockTakeMetrics
              : cleaningMetrics;

  const statusOptions = section === "PREPARATIONS" ? PREPARATION_STATUS_OPTIONS : section === "MATERIAL" ? MATERIAL_STATUS_OPTIONS : section === "INVENTORY" ? ["", "ACTIVE", "INACTIVE"] : section === "REPORTS" ? REPORT_STATUS_OPTIONS : [""];

  if (!canViewWarehouse) {
    return (
      <OperationsPageShell
        eyebrow="Warehouse"
        title="Warehouse Control Centre"
        description="This workspace is restricted by your IFAS role."
      >
        <OperationsEmptyState
          title="No warehouse access"
          description="Ask an IFAS administrator or warehouse supervisor if you should work in this module."
        />
      </OperationsPageShell>
    );
  }

  const pageActions = (() => {
    if (isPreparationDetails || isMaterialDetails || isReportDetails) {
      return null;
    }

    const refreshCurrentSection = () => {
      if (section === "PREPARATIONS") loadPreparations();
      else if (section === "MATERIAL") loadMaterialChecklists();
      else if (section === "INVENTORY") loadInventory();
      else if (section === "REPORTS") loadReports();
      else if (section === "STOCK_TAKE") loadStockTakes();
      else loadCleaningRosters();
    };

    return (
      <Box sx={operationsActionBarSx}>
        <Button variant="outlined" onClick={refreshCurrentSection} sx={standardGhostBtnSx}>
          Refresh
        </Button>
        {section === "PREPARATIONS" && !isPreparationDetails && (
          <Chip
            label="Preparations are created automatically after Funeral Confirmation"
            sx={{ bgcolor: ifasColors.softOrange, color: ifasColors.orange, fontWeight: 850 }}
          />
        )}
        {section === "INVENTORY" && canManageInventory && (
          <>
            <Button
              variant="outlined"
              onClick={() => setInventoryImportOpen(true)}
              sx={standardGhostBtnSx}
            >
              Import Excel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setSelectedInventoryMaterial(null);
                setFormState(emptyInventoryForm(userName || "", branchFilter || ""));
                setDialogMode("CREATE_INVENTORY");
              }}
              sx={standardPrimaryBtnSx}
            >
              New Material
            </Button>
          </>
        )}
        {section === "STOCK_TAKE" && canWritePreparation && (
          <Button
            variant="contained"
            onClick={() => {
              setFormState({
                warehouseBranch: branchFilter || "",
                frequency: "WEEKLY",
                periodStart: dayjs().startOf("week").format("YYYY-MM-DD"),
                periodEnd: dayjs().endOf("week").format("YYYY-MM-DD"),
                countDueDate: dayjs().startOf("week").add(2, "day").format("YYYY-MM-DD"),
                stockItemsJson: "",
                varianceNotes: "",
              });
              setDialogMode("CREATE_STOCK_TAKE");
            }}
            sx={standardPrimaryBtnSx}
          >
            New Stock Take
          </Button>
        )}
        {section === "CLEANING" && canWritePreparation && (
          <Button
            variant="contained"
            onClick={() => {
              setFormState({
                warehouseBranch: branchFilter || "",
                equipmentArea: "",
                cleaningFrequency: "WEEKLY",
                scheduledDate: dayjs().format("YYYY-MM-DD"),
                assignedTo: userName || "",
                storageLocation: "",
                chemicalName: "",
                chemicalDilution: "",
                cleaningChecklist: "",
                notes: "",
              });
              setDialogMode("CREATE_CLEANING_ROSTER");
            }}
            sx={standardPrimaryBtnSx}
          >
            New Cleaning Roster
          </Button>
        )}
      </Box>
    );
  })();

  const detailActionStrip = (() => {
    if (isPreparationDetails) {
      return (
        <Paper elevation={0} sx={detailActionStripSx}>
          <Box display="flex" alignItems={{ xs: "stretch", lg: "center" }} justifyContent="space-between" gap={1.5} flexWrap="wrap">
            <Box minWidth={0}>
              <Typography fontSize={16} fontWeight={900} color={ifasColors.text} textAlign="left">
                Preparation Actions
              </Typography>
              <Typography fontSize={12.5} color={ifasColors.muted} mt={0.3} textAlign="left">
                Review the preparation, move it into warehouse processing, and create dispatch work when ready.
              </Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end" ml={{ xs: 0, lg: "auto" }}>
              {canWritePreparation && (
              <IconButton onClick={() => openPreparationDialog(true)} sx={headerEditButtonSx}>
                <EditOutlinedIcon />
              </IconButton>
              )}
              {preparationPrimaryAction && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={preparationPrimaryAction.onClick}
                  disabled={saving}
                  sx={headerActionButtonSx}
                >
                  {preparationPrimaryAction.label}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      );
    }

    if (isMaterialDetails) {
      const effectiveChecklistStatus = resolveChecklistUiStatus(selectedChecklist);
      const rawChecklistStatus = String(selectedChecklist?.status || "").toUpperCase();
      const pendingOutstandingCount = countPendingOutstandingItems(selectedChecklist?.items);
      const canEditChecklist =
        effectiveChecklistStatus !== "RETURNED" &&
        !(rawChecklistStatus === "LOSS_REPORTED" && effectiveChecklistStatus !== "OUTSTANDING_MATERIALS");
      const reportAlreadyCreated = Boolean(selectedChecklist?.brokenLossReportCreated);
      const dispatchApprovalStatus = String(
        selectedChecklist?.dispatchApprovalStatus || "",
      ).toUpperCase();
      const materialActionHelper =
        effectiveChecklistStatus === "DRAFT"
          ? dispatchApprovalStatus === "APPROVED"
            ? "Dispatch approval is complete. Mark only what is leaving now, then confirm dispatch."
            : dispatchApprovalStatus === "PENDING"
              ? "Dispatch approval is pending. A supervisor decision is required before material dispatch can be confirmed."
              : dispatchApprovalStatus === "REJECTED"
                ? "Dispatch approval was rejected. Review the checklist, update it if needed, then request approval again."
                : "Dispatch stage: request approval first, then confirm dispatch after approval."
          : effectiveChecklistStatus === "DISPATCHED"
            ? "Return stage: before Confirm Return, allocate each item into returned, outstanding, and loss / broken quantities."
            : effectiveChecklistStatus === "OUTSTANDING_MATERIALS"
              ? pendingOutstandingCount === 0
                ? reportAlreadyCreated
                  ? "All outstanding items are marked collected. You can now reconcile outstanding materials. The loss report has already been logged separately."
                  : "All outstanding items are marked collected. You can now reconcile outstanding materials, and if needed you can still raise the loss report separately."
                : reportAlreadyCreated
                  ? `${pendingOutstandingCount} outstanding item${pendingOutstandingCount === 1 ? "" : "s"} still need to come back from the family. The loss report has already been logged separately.`
                  : `${pendingOutstandingCount} outstanding item${pendingOutstandingCount === 1 ? "" : "s"} still need to come back from the family. If some other items are broken or missing, you can also raise the loss report now.`
              : effectiveChecklistStatus === "PARTIALLY_RETURNED"
                ? "Discrepancies remain on this checklist. Review flagged lines and continue into loss reporting."
                : "This checklist is already closed.";

      return (
        <Paper elevation={0} sx={detailActionStripSx}>
          <Box display="flex" alignItems={{ xs: "stretch", lg: "center" }} justifyContent="space-between" gap={1.5} flexWrap="wrap">
            <Box minWidth={0}>
              <Typography fontSize={16} fontWeight={900} color={ifasColors.text} textAlign="left">
                Checklist Actions
              </Typography>
              <Typography fontSize={12.5} color={ifasColors.muted} mt={0.3} textAlign="left">
                {materialActionHelper}
              </Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end" ml={{ xs: 0, lg: "auto" }}>
              {canEditChecklist && canWriteChecklist && (
                <IconButton onClick={() => openMaterialDialog(true)} sx={headerEditButtonSx}>
                  <EditOutlinedIcon />
                </IconButton>
              )}
              {checklistPrimaryAction && (
                <Button
                  variant={checklistPrimaryAction.variant}
                  color="secondary"
                  onClick={checklistPrimaryAction.onClick}
                  disabled={saving || checklistPrimaryAction.disabled}
                  sx={headerActionButtonSx}
                >
                  {checklistPrimaryAction.label}
                </Button>
              )}
              {checklistSecondaryAction && (
                <Button
                  variant={checklistSecondaryAction.variant}
                  color="secondary"
                  onClick={checklistSecondaryAction.onClick}
                  disabled={saving}
                  sx={headerActionButtonSx}
                >
                  {checklistSecondaryAction.label}
                </Button>
              )}
            </Box>
          </Box>
        </Paper>
      );
    }

    if (isReportDetails) {
      return (
        <Paper elevation={0} sx={detailActionStripSx}>
          <Box display="flex" alignItems={{ xs: "stretch", lg: "center" }} justifyContent="space-between" gap={1.5} flexWrap="wrap">
            <Box minWidth={0}>
              <Typography fontSize={16} fontWeight={900} color={ifasColors.text} textAlign="left">
                Report Actions
              </Typography>
              <Typography fontSize={12.5} color={ifasColors.muted} mt={0.3} textAlign="left">
                Finalize the discrepancy record and push it onward for inventory follow-up once details are complete.
              </Typography>
            </Box>
            <Box display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end" ml={{ xs: 0, lg: "auto" }}>
              {canWriteReport && (
              <IconButton onClick={() => openReportDialog(true)} sx={headerEditButtonSx}>
                <EditOutlinedIcon />
              </IconButton>
              )}
              {canSubmitReport && (
              <Button
                variant="contained"
                color="secondary"
                onClick={runSubmitReport}
                disabled={saving || selectedReport?.status === "SUBMITTED_TO_INVENTORY"}
                sx={headerActionButtonSx}
              >
                Submit To Inventory
              </Button>
              )}
            </Box>
          </Box>
        </Paper>
      );
    }

    return null;
  })();

  return (
    <OperationsPageShell
      eyebrow="Warehouse"
      title="Warehouse Control Centre"
      description="Run preparation, dispatch, inventory, loss reporting, stock takes and cleaning as one operation — Warehouse is the single source of truth for stock levels and availability."
      actions={pageActions}
    >
      <Box display="flex" flexDirection="column" gap={2}>
        {detailActionStrip}

        <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "repeat(auto-fit, minmax(220px, 1fr))" }} gap={2}>
          {currentMetrics.map((metric: any) => (
            <StandardMetricCard key={metric.label} label={metric.label} value={metric.value} icon={metric.icon} tone={metric.tone} caption={metric.caption} />
          ))}
        </Box>
        <Paper elevation={0} sx={{ ...commandPanelSx, p: 1 }}>
        <Tabs
          value={section}
          onChange={handleSectionChange}
          sx={{
            minHeight: 0,
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTab-root": {
              minHeight: 0,
              minWidth: 0,
              px: 1.4,
              py: 0.85,
              mr: 1,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 800,
              color: ifasColors.muted,
            },
            "& .Mui-selected": {
              color: ifasColors.orange,
              bgcolor: ifasColors.softOrange,
              border: `1px solid ${alpha(ifasColors.orange, 0.22)}`,
            },
          }}
        >
          <Tab value="PREPARATIONS" label="Preparation" />
          <Tab value="MATERIAL" label="Material Dispatch" />
          <Tab value="INVENTORY" label="Inventory" />
          <Tab value="REPORTS" label="Broken / Loss" />
          <Tab value="STOCK_TAKE" label="Stock Take" />
          <Tab value="CLEANING" label="Cleaning Roster" />
        </Tabs>
        </Paper>


        {!isPreparationDetails && !isMaterialDetails && !isReportDetails && (
          <Paper
            elevation={0}
            sx={{
              p: 2.25,
              border: `1px solid ${ifasColors.border}`,
              borderRadius: "16px",
              background: `linear-gradient(135deg, ${alpha(ifasColors.orange, 0.09)}, ${ifasColors.surface} 46%)`,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ md: "center" }}
              justifyContent="space-between"
              mb={2}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    bgcolor: ifasColors.softOrange,
                    color: ifasColors.orange,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                  }}
                >
                  {activeSectionMeta.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 19, fontWeight: 900, color: ifasColors.text }}>
                    {activeSectionMeta.label}
                  </Typography>
                  <Typography
                    sx={{ mt: 0.4, color: ifasColors.muted, fontSize: 12.5, lineHeight: 1.45, maxWidth: 520 }}
                  >
                    {activeSectionMeta.desc}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                {section === "INVENTORY" && (searchText || branchFilter || statusFilter || inventoryCategoryFilter || inventoryStockFilter) && (
                  <Button
                    size="small"
                    onClick={() => {
                      setSearchText("");
                      setBranchFilter("");
                      setStatusFilter("");
                      setInventoryCategoryFilter("");
                      setInventoryStockFilter("");
                    }}
                    sx={{ textTransform: "none", fontWeight: 800 }}
                  >
                    Clear filters
                  </Button>
                )}
                <StandardCountBadge
                  count={section === "PREPARATIONS"
                    ? preparationsTotal
                    : section === "MATERIAL"
                      ? checklistsTotal
                      : section === "INVENTORY"
                        ? `${filteredInventoryMaterials.length} / ${inventoryMaterials.length}`
                        : section === "REPORTS"
                          ? reportsTotal
                          : section === "STOCK_TAKE"
                            ? stockTakes.length
                            : cleaningRosters.length}
                />
              </Stack>
            </Stack>

            {section === "INVENTORY" ? (
              <Box
                display="grid"
                gridTemplateColumns={{
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "minmax(280px, 2fr) repeat(4, minmax(145px, 1fr))",
                }}
                gap={{ xs: 1.25, md: 1.5 }}
                alignItems="start"
              >
                <TextField
                  size="small"
                  placeholder="Item code, material, branch or location"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  inputProps={{ "aria-label": "Search inventory" }}
                  InputProps={{ startAdornment: <SearchOutlinedIcon sx={{ mr: 1, color: ifasColors.muted }} /> }}
                  sx={{
                    ...inventoryFilterFieldSx,
                    gridColumn: { xs: "auto", sm: "1 / -1", lg: "auto" },
                  }}
                />
                <TextField
                  size="small"
                  select
                  label="Branch"
                  value={branchFilter}
                  onChange={(e) => setBranchFilter(e.target.value)}
                  sx={inventoryFilterFieldSx}
                >
                  <MenuItem value="">All Branches</MenuItem>
                  {inventoryBranchOptions.map((branch) => <MenuItem key={branch} value={branch}>{branch}</MenuItem>)}
                </TextField>
                <TextField
                  size="small"
                  select
                  label="Category"
                  value={inventoryCategoryFilter}
                  onChange={(e) => setInventoryCategoryFilter(e.target.value)}
                  sx={inventoryFilterFieldSx}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {inventoryCategoryOptions.map((category) => <MenuItem key={category} value={category}>{formatOperationsStatus(category)}</MenuItem>)}
                </TextField>
                <TextField
                  size="small"
                  select
                  label="Stock Position"
                  value={inventoryStockFilter}
                  onChange={(e) => setInventoryStockFilter(e.target.value)}
                  sx={inventoryFilterFieldSx}
                >
                  <MenuItem value="">All Stock</MenuItem>
                  <MenuItem value="IN_STOCK">In Stock</MenuItem>
                  <MenuItem value="ZERO_STOCK">Zero Stock</MenuItem>
                  <MenuItem value="LOW_STOCK">Low Stock</MenuItem>
                  <MenuItem value="DAMAGED">Damaged Stock</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  SelectProps={{ displayEmpty: true }}
                  InputLabelProps={{ shrink: true }}
                  sx={inventoryFilterFieldSx}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option || "ALL"} value={option}>{option ? formatOperationsStatus(option) : "All Statuses"}</MenuItem>
                  ))}
                </TextField>
              </Box>
            ) : (
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap">
                <TextField
                  size="small"
                  placeholder="Search reference, deceased, branch"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{ startAdornment: <SearchOutlinedIcon sx={{ mr: 1, color: ifasColors.muted }} /> }}
                  sx={{ minWidth: 280, bgcolor: ifasColors.surface, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />
                {section === "PREPARATIONS" && (
                  <TextField
                    size="small"
                    placeholder="All branches"
                    value={branchFilter}
                    onChange={(e) => setBranchFilter(e.target.value)}
                    sx={{ minWidth: 190, bgcolor: ifasColors.surface, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  />
                )}
                {!["STOCK_TAKE", "CLEANING"].includes(section) && (
                  <TextField
                    size="small"
                    select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    SelectProps={{ displayEmpty: true }}
                    sx={{ minWidth: 190, bgcolor: ifasColors.surface, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option || "ALL"} value={option}>
                        {option ? formatOperationsStatus(option) : "All statuses"}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
            )}
          </Paper>
        )}

        {isPreparationDetails
          ? renderPreparationDetails()
          : isMaterialDetails
            ? renderChecklistDetails()
            : isReportDetails
              ? renderReportDetails()
              : renderListTable()}
      </Box>

      <Dialog open={Boolean(cleaningCompletionRecord)} onClose={() => !saving && setCleaningCompletionRecord(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography fontSize={20} fontWeight={900}>Complete & Verify Cleaning</Typography>
          <Typography variant="body2" color="text.secondary">
            {cleaningCompletionRecord?.rosterNumber} · {cleaningCompletionRecord?.equipmentArea} · {cleaningCompletionRecord?.chemicalName}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography fontWeight={800} mb={1}>Mandatory completion checks</Typography>
              {[
                ["ppeConfirmed", "Correct PPE was used"],
                ["equipmentCleaned", "Equipment was cleaned with the assigned chemical"],
                ["areaSanitised", "Work area was sanitised"],
                ["equipmentInspected", "Equipment condition was inspected"],
                ["storageConfirmed", "Equipment was returned to the assigned storage location"],
              ].map(([field, label]) => (
                <FormControlLabel key={field} control={<Checkbox checked={Boolean(cleaningCompletionForm[field])} onChange={(e) => setCleaningCompletionForm((prev: any) => ({ ...prev, [field]: e.target.checked }))} />} label={label} />
              ))}
            </Paper>
            <TextField multiline minRows={2} label="Checklist / Procedure Notes" value={cleaningCompletionForm.cleaningChecklist || ""} onChange={(e) => setCleaningCompletionForm((prev: any) => ({ ...prev, cleaningChecklist: e.target.value }))} />
            <TextField multiline minRows={2} label="Issues / Damage Found" value={cleaningCompletionForm.issuesFound || ""} onChange={(e) => setCleaningCompletionForm((prev: any) => ({ ...prev, issuesFound: e.target.value }))} />
            <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2}>
              <TextField required label="Supervisor Verified By" value={cleaningCompletionForm.supervisorVerifiedBy || ""} onChange={(e) => setCleaningCompletionForm((prev: any) => ({ ...prev, supervisorVerifiedBy: e.target.value }))} />
              <TextField required label="Supervisor Signature" value={cleaningCompletionForm.supervisorSignature || ""} onChange={(e) => setCleaningCompletionForm((prev: any) => ({ ...prev, supervisorSignature: e.target.value }))} helperText="Type full-name confirmation." />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCleaningCompletionRecord(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" disabled={saving || !["ppeConfirmed", "equipmentCleaned", "areaSanitised", "equipmentInspected", "storageConfirmed"].every((field) => cleaningCompletionForm[field]) || !cleaningCompletionForm.supervisorVerifiedBy?.trim() || !cleaningCompletionForm.supervisorSignature?.trim()} onClick={finishCleaningRoster}>Complete & Verify</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(stockTakeReviewRecord)} onClose={() => !saving && setStockTakeReviewRecord(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography fontSize={20} fontWeight={900}>Review Stock Take</Typography>
          <Typography variant="body2" color="text.secondary">
            {stockTakeReviewRecord?.stockTakeNumber} · {stockTakeReviewRecord?.warehouseBranch}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ overflowX: "auto", borderRadius: 2 }}>
              <Table size="small">
                <TableHead sx={operationsTableHeadSx}><TableRow><TableCell>Item</TableCell><TableCell>Expected</TableCell><TableCell>Physical</TableCell><TableCell>Variance</TableCell><TableCell>Notes</TableCell></TableRow></TableHead>
                <TableBody>
                  {(stockTakeReviewRecord?.items || []).map((item: any) => (
                    <TableRow key={item.itemCode}>
                      <TableCell><b>{item.itemCode}</b><br/><Typography variant="caption">{item.description}</Typography></TableCell>
                      <TableCell>{item.expectedOnHandQty}</TableCell><TableCell>{item.physicalOnHandQty}</TableCell>
                      <TableCell><Chip size="small" label={Number(item.varianceQty || 0) > 0 ? `+${item.varianceQty}` : item.varianceQty || 0} color={item.varianceQty ? "warning" : "success"}/></TableCell>
                      <TableCell>{item.notes || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
            <TextField multiline minRows={3} label="Review notes" value={stockTakeReviewNotes} onChange={(e) => setStockTakeReviewNotes(e.target.value)} helperText="A rejection must include the recount or correction reason." />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          <Button onClick={() => setStockTakeReviewRecord(null)} disabled={saving}>Cancel</Button>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" color="error" disabled={saving || !stockTakeReviewNotes.trim()} onClick={() => decideStockTakeReview(false)}>Reject for Recount</Button>
            <Button variant="contained" color="success" disabled={saving} onClick={() => decideStockTakeReview(true)}>Approve & Apply Variance</Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog
        open={inventoryHistoryOpen}
        onClose={() => closeInventoryHistory()}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden", height: { xs: "94vh", md: "90vh" } } }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${ifasColors.border}`, background: `linear-gradient(135deg, ${ifasColors.surface} 0%, ${ifasColors.softOrange} 100%)` }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
            <Box>
              <Chip label="Immutable inventory audit" size="small" sx={{ bgcolor: ifasColors.softOrange, color: ifasColors.orange, fontWeight: 900, mb: 0.8 }} />
              <Typography fontSize={22} fontWeight={900}>Mortuary-wise Inventory History</Typography>
              <Typography fontSize={12.5} color={ifasColors.muted} mt={0.35}>
                Track opening balances, reservations, dispatches, returns, damages and stock adjustments by mortuary.
              </Typography>
            </Box>
            <IconButton onClick={() => closeInventoryHistory()} disabled={inventoryHistoryLoading} sx={{ border: `1px solid ${ifasColors.border}` }}>
              <CloseOutlinedIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: ifasColors.page, p: { xs: 1.5, md: 2 } }}>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", lg: "2fr 1fr 1fr 1fr auto" }} gap={1.25}>
                <TextField
                  size="small"
                  label="Search history"
                  placeholder="Material, item code, case, batch or movement number"
                  value={inventoryHistorySearch}
                  onChange={(event) => setInventoryHistorySearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      setInventoryHistoryPage(0);
                      setInventoryHistoryAppliedSearch(inventoryHistorySearch.trim());
                    }
                  }}
                />
                <TextField size="small" select label="Movement Type" value={inventoryHistoryMovementType} onChange={(event) => { setInventoryHistoryMovementType(event.target.value); setInventoryHistoryPage(0); }}>
                  <MenuItem value="">All Movements</MenuItem>
                  {["OPENING_BALANCE_IMPORT", "CREATE", "RESERVE", "RELEASE_RESERVATION", "DISPATCH", "RETURN", "OUTSTANDING_RETURN", "DAMAGE_LOSS", "ADJUSTMENT", "UPDATE"].map((type) => (
                    <MenuItem key={type} value={type}>{formatOperationsStatus(type)}</MenuItem>
                  ))}
                </TextField>
                <TextField size="small" type="date" label="Start Date" value={inventoryHistoryStartDate} onChange={(event) => { setInventoryHistoryStartDate(event.target.value); setInventoryHistoryPage(0); }} InputLabelProps={{ shrink: true }} />
                <TextField size="small" type="date" label="End Date" value={inventoryHistoryEndDate} onChange={(event) => { setInventoryHistoryEndDate(event.target.value); setInventoryHistoryPage(0); }} InputLabelProps={{ shrink: true }} />
                <Box display="flex" gap={1}>
                  <Button variant="contained" color="secondary" onClick={() => { setInventoryHistoryPage(0); setInventoryHistoryAppliedSearch(inventoryHistorySearch.trim()); }} sx={{ textTransform: "none", fontWeight: 900 }}>Apply</Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setInventoryHistorySearch("");
                      setInventoryHistoryAppliedSearch("");
                      setInventoryHistoryMovementType("");
                      setInventoryHistoryStartDate("");
                      setInventoryHistoryEndDate("");
                      setInventoryHistoryMortuary("");
                      setInventoryHistoryPage(0);
                    }}
                    sx={{ textTransform: "none", fontWeight: 800 }}
                  >
                    Reset
                  </Button>
                </Box>
              </Box>
            </Paper>

            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={1}>
                <Typography fontWeight={900}>Mortuary overview</Typography>
                <Typography fontSize={11.5} color={ifasColors.muted}>Select a mortuary to open its complete movement trail</Typography>
              </Box>
              <Box display="flex" gap={1.25} overflow="auto" pb={1} sx={{ scrollSnapType: "x proximity" }}>
                <Paper
                  variant="outlined"
                  onClick={() => { setInventoryHistoryMortuary(""); setInventoryHistoryPage(0); }}
                  sx={{ minWidth: 205, p: 1.5, borderRadius: 2, cursor: "pointer", scrollSnapAlign: "start", borderColor: !inventoryHistoryMortuary ? ifasColors.orange : ifasColors.border, bgcolor: !inventoryHistoryMortuary ? ifasColors.softOrange : ifasColors.surface }}
                >
                  <Typography fontSize={11} color={ifasColors.muted} fontWeight={800}>ALL MORTUARIES</Typography>
                  <Typography fontSize={24} fontWeight={900} color={ifasColors.text}>{(inventoryHistory?.mortuarySummaries || []).reduce((total: number, summary: any) => total + Number(summary.totalMovements || 0), 0)}</Typography>
                  <Typography fontSize={10.5} color={ifasColors.muted}>{inventoryHistory?.mortuarySummaries?.length || 0} locations with activity</Typography>
                </Paper>
                {(inventoryHistory?.mortuarySummaries || []).map((summary: any) => {
                  const selected = inventoryHistoryMortuary === summary.mortuaryName;
                  return (
                    <Paper
                      key={summary.mortuaryName}
                      variant="outlined"
                      onClick={() => { setInventoryHistoryMortuary(summary.mortuaryName); setInventoryHistoryPage(0); }}
                      sx={{ minWidth: 235, p: 1.5, borderRadius: 2, cursor: "pointer", scrollSnapAlign: "start", borderColor: selected ? ifasColors.orange : ifasColors.border, bgcolor: selected ? ifasColors.softOrange : ifasColors.surface, transition: "all 160ms ease", "&:hover": { borderColor: ifasColors.orange, transform: "translateY(-1px)" } }}
                    >
                      <Typography fontSize={12} fontWeight={900} color={ifasColors.text} noWrap>{summary.mortuaryName}</Typography>
                      <Box display="flex" alignItems="baseline" gap={0.75} mt={0.6}>
                        <Typography fontSize={22} fontWeight={900}>{summary.totalMovements || 0}</Typography>
                        <Typography fontSize={10.5} color={ifasColors.muted}>movements</Typography>
                      </Box>
                      <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={0.6} mt={0.75}>
                        <Typography fontSize={9.5} color={ifasColors.muted}>Dispatch <b>{summary.dispatchMovements || 0}</b></Typography>
                        <Typography fontSize={9.5} color={ifasColors.muted}>Return <b>{summary.returnMovements || 0}</b></Typography>
                        <Typography fontSize={9.5} color={summary.damageLossMovements ? STANDARD_TONES.red.color : ifasColors.muted}>Damage <b>{summary.damageLossMovements || 0}</b></Typography>
                      </Box>
                      <Typography fontSize={9.5} color={ifasColors.muted} mt={0.8}>Last activity: {formatDateTime(summary.lastActivityAt)}</Typography>
                    </Paper>
                  );
                })}
              </Box>
            </Box>

            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
              <Box px={2} py={1.25} display="flex" alignItems="center" justifyContent="space-between" gap={2} borderBottom={`1px solid ${ifasColors.border}`}>
                <Box>
                  <Typography fontWeight={900}>{inventoryHistoryMortuary || "All Mortuaries"}</Typography>
                  <Typography fontSize={11.5} color={ifasColors.muted}>{inventoryHistory?.totalElements || 0} movement records match the current filters</Typography>
                </Box>
                {inventoryHistoryAppliedSearch && <Chip size="small" label={`Search: ${inventoryHistoryAppliedSearch}`} onDelete={() => { setInventoryHistorySearch(""); setInventoryHistoryAppliedSearch(""); setInventoryHistoryPage(0); }} />}
              </Box>
              {inventoryHistoryLoading ? (
                <Box minHeight={260} display="grid" sx={{ placeItems: "center" }}><Loader /></Box>
              ) : (
                <Box sx={{ overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 1120 }}>
                    <TableHead sx={operationsTableHeadSx}>
                      <TableRow>
                        <TableCell>Date & Movement</TableCell><TableCell>Mortuary</TableCell><TableCell>Material</TableCell><TableCell>Action</TableCell><TableCell align="center">Qty</TableCell><TableCell align="center">Available After</TableCell><TableCell>Case / Reference</TableCell><TableCell>Performed By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(inventoryHistory?.movements || []).length ? (inventoryHistory.movements || []).map((movement: any) => (
                        <TableRow key={movement.id || movement.movementNumber} sx={{ ...operationsRowSx, "&:nth-of-type(even)": { bgcolor: ifasColors.page } }}>
                          <TableCell sx={{ minWidth: 180 }}><Typography fontSize={11.5} fontWeight={800}>{formatDateTime(movement.createdAt)}</Typography><Typography fontSize={10} color={ifasColors.muted} fontFamily="monospace">{movement.movementNumber}</Typography></TableCell>
                          <TableCell sx={{ minWidth: 180 }}><Typography fontSize={11.5} fontWeight={800}>{movement.warehouseBranch || "Unassigned"}</Typography></TableCell>
                          <TableCell sx={{ minWidth: 210 }}><Typography fontSize={11.5} fontWeight={800}>{movement.description || "Not captured"}</Typography><Typography fontSize={10} color={ifasColors.orange} fontFamily="monospace">{movement.itemCode}</Typography></TableCell>
                          <TableCell><Chip size="small" label={formatOperationsStatus(movement.movementType)} sx={getOperationsStatusSx(movement.movementType)} /></TableCell>
                          <TableCell align="center"><Typography fontWeight={900}>{movement.quantity ?? 0}</Typography></TableCell>
                          <TableCell align="center"><Chip size="small" label={movement.balanceAfter ?? 0} sx={{ bgcolor: STANDARD_TONES.green.bg, color: STANDARD_TONES.green.color, fontWeight: 900 }} /></TableCell>
                          <TableCell sx={{ minWidth: 190 }}>
                            <Typography fontSize={11.5} fontWeight={800}>{movement.caseNumber || movement.referenceNumber || "Manual"}</Typography>
                            <Typography fontSize={10} color={ifasColors.muted}>{movement.referenceType ? formatOperationsStatus(movement.referenceType) : "Inventory movement"}</Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 145 }}><Typography fontSize={11.5}>{movement.performedBy || "System"}</Typography></TableCell>
                        </TableRow>
                      )) : (
                        <TableRow><TableCell colSpan={8}><OperationsEmptyState title="No movement history found" description="Try another mortuary, date range, movement type, or search term." /></TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              )}
              <TablePagination
                component="div"
                count={Number(inventoryHistory?.totalElements || 0)}
                page={inventoryHistoryPage}
                onPageChange={(_, nextPage) => setInventoryHistoryPage(nextPage)}
                rowsPerPage={inventoryHistoryRowsPerPage}
                onRowsPerPageChange={(event) => { setInventoryHistoryRowsPerPage(Number(event.target.value)); setInventoryHistoryPage(0); }}
                rowsPerPageOptions={[20, 50, 100]}
                labelRowsPerPage="History rows per page"
                sx={{ borderTop: `1px solid ${ifasColors.border}` }}
              />
            </Paper>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 1.5, borderTop: `1px solid ${ifasColors.border}` }}>
          <Button variant="contained" onClick={() => closeInventoryHistory()} disabled={inventoryHistoryLoading} sx={{ textTransform: "none", fontWeight: 900 }}>Close History</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={inventoryImportOpen}
        onClose={() => closeInventoryImport()}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${ifasColors.border}`, background: `linear-gradient(135deg, ${ifasColors.surface} 0%, ${ifasColors.softOrange} 100%)` }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2}>
            <Box>
              <Chip label="Inventory opening balance" size="small" sx={{ bgcolor: ifasColors.softOrange, color: ifasColors.orange, fontWeight: 900, mb: 0.8 }} />
              <Typography fontSize={22} fontWeight={900}>Import Consolidated Inventory</Typography>
              <Typography fontSize={12.5} color={ifasColors.muted} mt={0.4}>
                Upload the wide Excel sheet, validate every material/branch quantity, then confirm the import.
              </Typography>
            </Box>
            <IconButton onClick={() => closeInventoryImport()} disabled={inventoryImportLoading} sx={{ border: `1px solid ${ifasColors.border}` }}>
              <CloseOutlinedIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: ifasColors.page }}>
          <Stack spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
                <Button component="label" variant="outlined" color="secondary" sx={{ fontWeight: 800 }} disabled={inventoryImportLoading}>
                  Choose Excel File
                  <input
                    hidden
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      setInventoryImportFile(file);
                      setInventoryImportPreview(null);
                      event.target.value = "";
                    }}
                  />
                </Button>
                <Box flex={1} minWidth={0}>
                  <Typography fontWeight={800}>{inventoryImportFile?.name || "No Excel file selected"}</Typography>
                  <Typography fontSize={12} color={ifasColors.muted}>Accepted formats: .xlsx and .xls. Existing inventory records will never be overwritten.</Typography>
                </Box>
                <Button variant="contained" color="secondary" disabled={!inventoryImportFile || inventoryImportLoading} onClick={validateInventoryImport} sx={{ fontWeight: 900 }}>
                  {inventoryImportLoading && !inventoryImportPreview ? "Validating..." : "Validate & Preview"}
                </Button>
              </Stack>
            </Paper>

            {inventoryImportPreview && (
              <>
                <Box display="grid" gridTemplateColumns={{ xs: "1fr 1fr", md: "repeat(4, 1fr)" }} gap={1.5}>
                  {[
                    ["Generated Records", inventoryImportPreview.totalRecords || 0, ifasColors.text],
                    ["Ready to Import", inventoryImportPreview.readyRecords || 0, STANDARD_TONES.green.color],
                    ["Existing / Skipped", inventoryImportPreview.existingRecords || 0, STANDARD_TONES.grey.color],
                    ["Needs Attention", inventoryImportPreview.errorRecords || 0, STANDARD_TONES.red.color],
                  ].map(([label, value, colour]) => (
                    <Paper key={String(label)} variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderTop: `3px solid ${colour}` }}>
                      <Typography fontSize={11} color={ifasColors.muted} fontWeight={800}>{label}</Typography>
                      <Typography fontSize={24} fontWeight={900} color={String(colour)}>{value}</Typography>
                    </Paper>
                  ))}
                </Box>

                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
                  <Box px={2} py={1.3} display="flex" justifyContent="space-between" gap={2} alignItems="center">
                    <Box>
                      <Typography fontWeight={900}>Validation preview</Typography>
                      <Typography fontSize={11.5} color={ifasColors.muted}>Attention rows are shown first; preview is limited to 150 rows.</Typography>
                    </Box>
                    <Chip label={`${inventoryImportPreview.materialRows || 0} materials · ${inventoryImportPreview.branchColumns || 0} branches`} size="small" />
                  </Box>
                  <Box sx={{ maxHeight: 420, overflow: "auto", borderTop: `1px solid ${ifasColors.border}` }}>
                    <Table stickyHeader size="small">
                      <TableHead sx={operationsTableHeadSx}>
                        <TableRow>
                          <TableCell>Row</TableCell><TableCell>Material / Code</TableCell><TableCell>Branch</TableCell><TableCell>Category / UOM</TableCell><TableCell>Total</TableCell><TableCell>Damaged</TableCell><TableCell>Available</TableCell><TableCell>Result</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...(inventoryImportPreview.rows || []).filter((row: any) => row.result !== "READY"), ...(inventoryImportPreview.rows || []).filter((row: any) => row.result === "READY")].slice(0, 150).map((row: any, index: number) => (
                          <TableRow key={`${row.sheetRow}-${row.itemCode}-${row.warehouseBranch}-${index}`}>
                            <TableCell>{row.sheetRow}</TableCell>
                            <TableCell><Typography fontSize={12} fontWeight={800}>{row.material}</Typography><Typography fontSize={10.5} color={ifasColors.muted}>{row.itemCode}</Typography></TableCell>
                            <TableCell>{row.warehouseBranch}</TableCell>
                            <TableCell>{row.category}<Typography fontSize={10.5} color={ifasColors.muted}>{row.uom}</Typography></TableCell>
                            <TableCell>{row.totalQty ?? (row.rawQuantity || "—")}</TableCell>
                            <TableCell>{row.damagedQty ?? "—"}</TableCell>
                            <TableCell>{row.availableQty ?? "—"}</TableCell>
                            <TableCell>
                              <Chip size="small" label={formatOperationsStatus(row.result)} sx={getOperationsStatusSx(row.result)} />
                              <Typography fontSize={10.5} color={ifasColors.muted} mt={0.5} maxWidth={260}>{row.message}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </Paper>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between", borderTop: `1px solid ${ifasColors.border}` }}>
          <Typography fontSize={12.5} color={ifasColors.muted}>
            Zero quantities are imported as zero. “1 broken” becomes Total 1, Damaged 1, Available 0.
          </Typography>
          <Box display="flex" gap={1}>
            <Button variant="outlined" onClick={() => closeInventoryImport()} disabled={inventoryImportLoading}>Cancel</Button>
            <Button variant="contained" color="secondary" onClick={confirmInventoryImport} disabled={inventoryImportLoading || !inventoryImportPreview?.readyRecords} sx={{ fontWeight: 900 }}>
              {inventoryImportLoading && inventoryImportPreview ? "Importing..." : `Import ${inventoryImportPreview?.readyRecords || 0} Valid Records`}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(dialogMode)}
        onClose={resetDialog}
        maxWidth={isMaterialDialog || isStockTakeDialog ? "xl" : "lg"}
        fullWidth
        BackdropProps={{
          sx: {
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            backgroundColor: "rgba(24, 28, 39, 0.26)",
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
            border: `1px solid ${ifasColors.border}`,
            boxShadow: "0 28px 52px rgba(20, 24, 31, 0.18)",
            background: `linear-gradient(180deg, ${ifasColors.surface} 0%, ${ifasColors.page} 100%)`,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.1,
            borderBottom: `1px solid ${ifasColors.border}`,
            background: isMaterialDialog || isPreparationDialog || isInventoryDialog || isReportDialog || isStockTakeDialog || isCleaningDialog
              ? `linear-gradient(135deg, ${ifasColors.surface} 0%, ${alpha(ifasColors.orange, 0.065)} 100%)`
              : ifasColors.surface,
          }}
        >
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
            <Box minWidth={0}>
              <Chip
                label={
                  isMaterialDialog
                    ? "Warehouse Dispatch"
                    : isPreparationDialog
                      ? "Warehouse Preparation"
                      : isInventoryDialog
                        ? "Inventory Master"
                      : isReportDialog
                        ? "Warehouse Loss Report"
                        : isStockTakeDialog
                          ? "Warehouse Stock Take"
                          : isCleaningDialog
                            ? "Equipment Roster"
                            : "Warehouse Record"
                }
                size="small"
                sx={{
                  bgcolor: ifasColors.softOrange,
                  color: ifasColors.orange,
                  fontWeight: 900,
                  mb: 0.8,
                }}
              />
              <Typography fontSize={22} fontWeight={900} color={ifasColors.text} textAlign="left">
                {dialogTitle}
              </Typography>
              <Typography fontSize={12.5} color={ifasColors.muted} mt={0.35} textAlign="left">
                {isMaterialDialog
                  ? "Capture dispatch, return, and outstanding-material accountability in one clean operational checklist."
                  : isPreparationDialog
                    ? "Update the funeral-preparation record cleanly without adding extra review clutter."
                    : isInventoryDialog
                      ? "Maintain material master stock by warehouse branch with live availability controls."
                    : isReportDialog
                      ? "Capture discrepancy details clearly so inventory follow-up is quick and accountable."
                      : isStockTakeDialog
                        ? dialogMode === "EDIT_STOCK_TAKE"
                          ? "Capture each physical quantity and let the system calculate stock variances."
                          : "Open a weekly or monthly count from the branch's live Inventory Master."
                        : isCleaningDialog
                          ? "Schedule equipment cleaning and storage checks with accountable sign-off."
                          : "Keep the operational record tight and clear so dispatch, return, and follow-up stay accountable."}
              </Typography>
            </Box>
            <IconButton
              onClick={resetDialog}
              sx={{
                flex: "0 0 auto",
                border: `1px solid ${ifasColors.border}`,
                bgcolor: ifasColors.surface,
                "&:hover": {
                  bgcolor: ifasColors.softOrange,
                  color: ifasColors.orange,
                },
              }}
            >
              <CloseOutlinedIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            bgcolor: ifasColors.page,
            "& .MuiPaper-root": {
              boxShadow: "0 8px 20px rgba(20, 24, 31, 0.045)",
            },
          }}
        >
          {renderDialogContent()}
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: `1px solid ${ifasColors.border}`,
            px: 2,
            py: 1.5,
            bgcolor: ifasColors.surface,
            justifyContent: "space-between",
          }}
        >
          <Typography fontSize={12.5} color={ifasColors.muted}>
            {isMaterialDialog
              ? "Dispatch and return accountability will be saved on this checklist."
            : isInventoryDialog
              ? "Inventory totals update the live available, reserved, allocated, and damaged position."
            : isReportDialog
                ? "Changes will be saved to this broken / loss report."
                : isStockTakeDialog
                  ? dialogMode === "EDIT_STOCK_TAKE"
                    ? "Physical counts and calculated variances will be saved for review."
                    : "This stock take will snapshot active materials for warehouse counting."
                  : isCleaningDialog
                    ? "This roster will be scheduled for cleaning/storage completion."
                    : "Changes will be saved to the warehouse record."}
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              onClick={resetDialog}
              variant="outlined"
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
            >
              Cancel
            </Button>
            {canSubmitDialog && (
            <Button
              variant="contained"
              onClick={submitDialog}
              disabled={saving || dialogFormLoading}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 900, minWidth: 140 }}
            >
              {saving
                ? checklistActionIntent
                  ? "Saving & confirming..."
                  : isEditDialog
                    ? "Updating..."
                    : "Saving..."
                : dialogPrimaryLabel}
            </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>
    </OperationsPageShell>
  );
}
